import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR, CHIP_TONES, RADIUS } from "../lib/tokens";
import { ServingsStepper } from "./ServingsStepper";
import { generateRecipe, generateRecipeStreaming, classifyDishPrompt, requestClarifications } from "../lib/generateRecipe";
import GenerationProgress from "./GenerationProgress";
import AppetiteFunnel from "./AppetiteFunnel";
import { allergenExclusionStrings, findAllergyConflicts, recipeAllergyConflicts, suggestSubstitutes } from "../data/allergens";
import { useCommunityDedupe } from "../lib/useCommunityRecipes";
import { loadDraft, saveDraft, clearDraft, draftAgeLabel } from "../lib/aiRecipeDraft";
import {
  RECIPE_TYPES,
  pickConceptFrame,
  pickConceptFrameWithType,
  readRecentFrameIds,
  pushRecentFrameId,
  pushRecentHeroId,
  recentHeroFamilyExclusions,
  topCuisinesFromCookLogs,
  lovedCuisinesFromCookLogs,
} from "../data/conceptFrames";
import AIRecipeSetup from "../experiments/mcm-cooking/AIRecipeSetup";
import { ThemeProvider as MCMThemeProvider } from "../lib/theme";
import { suggestCookInstructions } from "../lib/suggestCookInstructions";
import { buildAIContext } from "../lib/aiContext";
import { captureTrainingExample } from "../lib/captureTrainingExample";
import { totalTimeMin, difficultyLabel } from "../data/recipes";
import { resolveCookingIntent } from "../data/cookingIntents";
import { runRecipeTechniqueChecks } from "../lib/recipeValidator";
import {
  findIngredient,
  getIngredientInfo,
  coerceRecipeCanonicalIds,
  cutLabel,
  CANONICAL_ALIASES,
} from "../data/ingredients";
import { loadPinned as loadAiPinned, clearPinned as clearAiPinned, subscribePinned } from "../lib/aiRecipePinned";
import {
  extractDietaryClaims,
  namesMatch,
  normalizeForMatch,
  resolveNameToCanonicalId,
  sameCanonicalFamily,
  resolvesToSameCanonical,
  deriveRowHeader,
  deriveRowCut,
  pairRecipeIngredients,
  describePairing,
} from "../lib/recipePairing";
import { recipeNutrition, formatMacros } from "../lib/nutrition";
import { useBrandNutrition } from "../lib/useBrandNutrition";
import UnitPicker from "./UnitPicker";
import { applyPreferredUnit, prefKeyForIngredient, useUnitPrefsVersion, DISPLAY_CONTEXT } from "../lib/unitPrefs";
import { buildUnitConversionTargets, tokenizeSwappedInstruction } from "../lib/effectiveRecipe";
import { tasteBalanceNote } from "../lib/tasteBalanceCritic";
import { targetInternalTempF, minSafeInternalTempF } from "../lib/validators/meatDoneness";
import { recordGeneration, fetchRecentGenerationTitles } from "../lib/generationLedger";
import { applyHonestTime } from "../lib/honestTime";
import { playSfx } from "../lib/sfx";
import { scaleRecipe } from "../lib/recipeScaling";
import RecipeNutritionLine from "./RecipeNutritionLine";
import TechniqueCheckBadge from "./TechniqueCheckBadge";
import { supabase } from "../lib/supabase";

// Kick off a Claude-drafted recipe from the user's pantry. Three phases:
//   setup   — meal prompt + star ingredients + timing/course + nuance chips,
//             tap DRAFT to call the edge fn
//   loading — skeleton while the edge function is running
//   preview — show the generated recipe; four actions below
//
// Preview action bar (four buttons):
//   ↻ REGEN    — back to setup, same prefs
//   SAVE       — onSave(recipe) → persist privately, close
//   📅 SCHED   — onSchedule(recipe) → parent persists (shared=true)
//                and opens SchedulePicker
//   COOK IT    — onSaveAndCook(recipe) → persist + enter CookMode
//
// The parent (CreateMenu) owns the shared/private semantics. This
// component just emits events.

const CUISINE_CHIPS = [
  { id: "any",      label: "Any cuisine"   },
  { id: "italian",  label: "Italian"       },
  { id: "french",   label: "French"        },
  { id: "mexican",  label: "Mexican"       },
  { id: "american", label: "American"      },
  { id: "japanese", label: "Japanese"      },
  { id: "thai",     label: "Thai"          },
  { id: "indian",   label: "Indian"        },
];

const TIME_CHIPS = [
  { id: "quick",  label: "≤30 min" },
  { id: "medium", label: "≤60 min" },
  { id: "long",   label: "Long cook" },
];

const DIFFICULTY_CHIPS = [
  { id: "easy",     label: "Easy"    },
  { id: "medium",   label: "Medium"  },
  { id: "advanced", label: "Advanced"},
];

// Funnel seen-set persistence — sessionStorage-scoped so reroll depth
// survives component remounts but dies with the tab (right lifetime
// for "ideas this funnel session already showed me"). Keyed per
// user + prompt: a new prompt is a new funnel session.
const funnelSeenKey = (uid, prompt) =>
  `ai.funnel.seen.v1:${uid || "anon"}:${String(prompt || "").toLowerCase().trim().slice(0, 80)}`;
function readFunnelSeen(uid, prompt) {
  try {
    const raw = window.sessionStorage?.getItem(funnelSeenKey(uid, prompt));
    const v = raw ? JSON.parse(raw) : null;
    return v && Array.isArray(v.moods) && Array.isArray(v.lanes)
      ? v : { moods: [], lanes: [] };
  } catch { return { moods: [], lanes: [] }; }
}
function writeFunnelSeen(uid, prompt, seen) {
  try {
    window.sessionStorage?.setItem(funnelSeenKey(uid, prompt), JSON.stringify(seen));
  } catch { /* quota / private mode — non-fatal */ }
}

// When is the user eating this? A breakfast dish and a dinner entrée
// draft very differently; telling Claude the intended meal time keeps
// it from suggesting pancakes for dinner unless asked.
const MEAL_TIMING_CHIPS = [
  { id: "any",       label: "Any time" },
  { id: "breakfast", label: "Breakfast" },
  { id: "lunch",     label: "Lunch" },
  { id: "dinner",    label: "Dinner" },
];

// Course type. Mains carry the meal, sides support it, desserts sit
// on their own track. Same dish title can read very differently
// depending on which of these Claude is aiming for.
const COURSE_CHIPS = [
  { id: "any",       label: "Any course" },
  { id: "main",      label: "Main" },
  { id: "side",      label: "Side" },
  { id: "dessert",   label: "Dessert" },
  { id: "appetizer", label: "Appetizer" },
  // "Baked Goods" and "Prep" are component recipes (bread, stock,
  // sauce, pickles, pastry) that live in the library without a meal
  // slot. Selecting them hides MEAL TIMING since these aren't tied to
  // breakfast/lunch/dinner — they're pantry/bakery-case items.
  //
  // Label says "Baked Goods" rather than just "Bake" because users
  // kept getting meals-on-bread (pesto focaccia topped with burrata,
  // pizza, savory galettes) when they picked "Bake" — the plain verb
  // read as "put something in the oven" and Claude happily obliged
  // with sheet-pan dinners disguised as flatbreads. "Baked Goods"
  // primes the bakery-case mental model. Edge-fn prompt reinforces
  // it with an explicit exclusion list. Oven-roasted mains stay under
  // "Main" where they belong.
  { id: "bake",      label: "Baked Goods" },
  { id: "prep",      label: "Prep" },
];

// PRIORITY chips — three modes that define how Claude balances the
// user's meal prompt against the pantry contents:
//
//   classic       — User gets what they asked for. Meal prompt is
//                   king; the pantry is a soft reference. Shopping
//                   rows for missing ingredients are fine.
//   chefs_choice  — Most freedom. Chef leans on the pantry (especially
//                   expiring rows) but is free to reach for whatever
//                   elevates the dish. Shopping rows are fine.
//   pantry        — STRICT pantry-only. Every ingredientId MUST be in
//                   the pantry block. Expiry priority is highest. If
//                   the meal prompt names something the pantry can't
//                   support, the edge fn returns a structured reason
//                   instead of inventing a shopping row.
//
// Legacy `category` / `pantry` priority values from old drafts get
// migrated at draft-load time (category → classic, pantry → chefs_choice
// since the old pantry mode was soft, not strict).
//
// The mode PICKER lives in AIRecipeSetup.jsx (its own PRIORITY_CHIPS)
// — that's where "Kitchen-only" is hidden when the pantry is empty
// (kitchen-tracking off passes pantry=[]).

// Canonical ids that count as "protein" for the STAR INGREDIENTS
// picker when the pantry row's category isn't in the meat/poultry/
// seafood set. Keeps eggs / tofu / beans from being filtered out.
const PLANT_PROTEIN_SLUGS = new Set([
  "eggs", "egg_whites", "tofu", "tempeh", "beans", "lentils",
  "chickpeas", "black_beans", "kidney_beans", "pinto_beans",
  "white_beans", "edamame", "peanut_butter",
]);
const PROTEIN_CATEGORIES = new Set(["meat", "poultry", "seafood"]);

// Is a pantry row "proteiny enough" to show up in the STAR picker?
// Shows meat/poultry/seafood from the canonical registry + a hand-
// picked set of plant / egg / dairy proteins.
// Best-effort partial JSON parse for streaming visual feedback.
// Closes unbalanced braces/brackets/quotes in a token-aware way so
// mid-stream text can be parsed enough to extract whatever fields
// have already finished. Only used for live render — the validated
// recipe always comes from the SSE "complete" event.
function tryPartialJson(text) {
  if (!text) return null;
  try { return JSON.parse(text); } catch { /* try repaired */ }
  let inString = false, escape = false;
  const stack = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (escape) { escape = false; continue; }
    if (inString) {
      if (c === "\\") escape = true;
      else if (c === '"') inString = false;
      continue;
    }
    if (c === '"') inString = true;
    else if (c === "{") stack.push("}");
    else if (c === "[") stack.push("]");
    else if (c === "}" || c === "]") stack.pop();
  }
  let closed = text;
  if (inString) closed += '"';
  closed = closed.replace(/[,:]\s*$/, "");      // trim dangling comma / colon
  closed += stack.reverse().join("");
  try { return JSON.parse(closed); } catch { return null; }
}

function isProteinRow(row) {
  const canon = row?.ingredientId ? findIngredient(row.ingredientId) : null;
  if (canon && PROTEIN_CATEGORIES.has(canon.category)) return true;
  const slug = row?.ingredientId || row?.canonicalId;
  if (slug && PLANT_PROTEIN_SLUGS.has(slug)) return true;
  return false;
}

// Split an ideal name on alternative separators into candidate sub-
// names. Claude frequently emits "ciabatta or crusty bread" / "salt &
// black pepper" / "penne, rigatoni" — those are alternatives, not
// compound identities, and matching the literal compound against the
// pantry fails on both sides: "ciabatta or crusty bread" isn't a
// substring of "Ciabatta Roll" (false negative), and a broad category
// fallback on a loose resolver sweep pulls in unrelated rows (false
// positive). Splitting lets each alternative resolve to its own
// canonical and match cleanly.
const NAME_SEPARATOR_RE = /\s+or\s+|\s+and\s+|\s*[&/,]\s*/i;

// Network-shape error detection. iOS Safari/WebKit's fetch failures
// surface as Error("Load failed"); Chromium uses "Failed to fetch";
// supabase-js sometimes wraps with FunctionsFetchError. Catching any
// of these gives us a chance to retry once before showing the user
// an opaque "Draft hiccup" — most of these are transient connection
// blips that resolve on a second attempt.
function isNetworkError(e) {
  if (!e) return false;
  const msg = String(e?.message || e || "").toLowerCase();
  if (/load failed|failed to fetch|network ?error|networkerror/.test(msg)) return true;
  // Supabase-js wrapped errors expose .name = "FunctionsFetchError"
  // for the network-stack failures specifically.
  const name = String(e?.name || "");
  if (name === "FunctionsFetchError" || (name === "TypeError" && /fetch/i.test(msg))) return true;
  return false;
}

// withRetryOnNetwork — call fn(), and if it throws a network-shape
// error, wait `delayMs` and call once more. Any other error or a
// second network failure bubbles out. The retry is a single shot —
// further retries belong at the user's TRY AGAIN button so they
// stay in control after the first auto-fallback.
async function withRetryOnNetwork(fn, { delayMs = 1500 } = {}) {
  try {
    return await fn();
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    await new Promise((r) => setTimeout(r, delayMs));
    return await fn();
  }
}
function splitIdealName(name) {
  if (!name) return [];
  const parts = String(name)
    .split(NAME_SEPARATOR_RE)
    .map(s => s.trim())
    .filter(Boolean);
  return parts.length > 0 ? parts : [String(name)];
}

// Scan the user's actual pantry for rows that can cover a sketch.pantry
// row Claude marked as shopping (pantryItemId == null). Returns a
// map { sketchPantryIdx → pantryRowId } of recommended auto-pairs.
//
// Why this exists: the AI frequently emits a sketch.pantry row with
// pantryItemId: null ("you need to buy tortillas") even when the
// user's real pantry has a matching item. The ingredientEntries
// pairing code then renders that row as "matched" — it's not
// `missing`, so findRawPantryCandidates never runs — and the user
// sees "NOT IN PANTRY — SHOPPING" on an item they already own. Plus
// the top-line coverage count reads "you have everything" because
// the sketch.pantry length equals the ideal length, regardless of
// whether any of those rows trace back to a real pantryItemId.
//
// Auto-pair fixes both: we promote the real pantry row into the
// pantryEdits.swaps diff before first render, so `showRow` resolves
// cleanly and the green "FROM your pantry" tag lights up. The swap
// is rendered as "⚡ AUTO-PAIRED" (not ✓ FROM) so the substitution
// is visible and the user can still tap SWAP to override.
//
// Rules:
//   - Never steal a pantry row another sketch.pantry row already
//     claims via pantryItemId.
//   - Tier 1: canonical-id match (sketch row's ingredientId or
//     resolved-from-name canonical ↔ pantry row's ingredientId).
//   - Tier 2: name-fuzzy match via namesMatch — catches free-text
//     user pantry rows without canonical ids.
// Auto-pair sketch shopping rows against pantry rows.
//
// `strict` controls how aggressively we match across canonicals:
//   - true  (CLASSIC + DREAM modes): canonical-id equality only
//     (after alias redirect). Yellow onion ≠ green onion, potato ≠
//     carrot, even when they share a hub. Recipe identity is
//     authoritative; the user typed an authentic dish and we don't
//     pantry-shoehorn substitutions.
//   - false (PANTRY mode): full hub-family matching. Generic
//     "chicken" pairs with "chicken_breast"; "yellow_onion" pairs
//     with any onion sibling. The user explicitly asked for "use
//     what I have" so cross-canonical hub matches are the point.
//
// Also: name-fallback (namesMatch) is REMOVED in strict mode. Name-
// based matching can pull cross-canonical ("Carrot" matching against
// "Carrot Cake"), and we want strict mode to stay strict.
function autoPairShoppingRows(sketch, pantry, { strict = true } = {}) {
  const swaps = {};
  if (!sketch || !Array.isArray(sketch.pantry) || !Array.isArray(pantry)) return swaps;
  const sameAs = strict ? resolvesToSameCanonical : sameCanonicalFamily;
  const used = new Set();
  for (const row of sketch.pantry) {
    if (row && row.pantryItemId) used.add(row.pantryItemId);
  }
  sketch.pantry.forEach((row, j) => {
    if (!row || row.pantryItemId) return;
    const canonId = row.ingredientId || resolveNameToCanonicalId(row.name) || null;
    let found = null;
    if (canonId) {
      found = pantry.find(p => {
        if (!p || used.has(p.id)) return false;
        const pSlug = p.ingredientId || p.canonicalId || null;
        if (!pSlug) return false;
        return sameAs(pSlug, canonId);
      });
    }
    // Name-fallback only in non-strict (pantry) mode. Strict mode
    // requires a real canonical id on both sides.
    if (!found && !strict && row.name) {
      found = pantry.find(p =>
        p && !used.has(p.id) && namesMatch(p.name, row.name),
      );
    }
    if (found) {
      used.add(found.id);
      swaps[j] = found.id;
    }
  });

  // Spurious-sub auto-correct. Only fires when the AI emitted a
  // subbedFrom marker (it explicitly substituted). Same strictness
  // gate — in classic/dream modes we don't conjure direct matches
  // across hubs even for sub-correction.
  sketch.pantry.forEach((row, j) => {
    if (!row || !row.subbedFrom) return;
    const origSlug = resolveNameToCanonicalId(row.subbedFrom);
    if (!origSlug) return;
    const directMatch = pantry.find(p => {
      if (!p || used.has(p.id)) return false;
      const pSlug = p.ingredientId || p.canonicalId || null;
      if (!pSlug) return false;
      return sameAs(pSlug, origSlug);
    });
    if (!directMatch) return;
    if (row.pantryItemId === directMatch.id) return;
    used.add(directMatch.id);
    swaps[j] = directMatch.id;
  });
  return swaps;
}

// Walk a sketch and stamp each ideal slot with the dietary claims
// extracted from its name. Non-destructive copy. The claims travel
// with the slot from ingestion through render so the swap/pair UI
// can diff them against the paired pantry row's attributes.claims
// and flag "⚠ NO LONGER VEGAN" when the user takes a non-compliant
// substitution.
// MUST-INCLUDE picker renderer — pulled out of the main component
// body so the JSX + filter logic doesn't bloat the setup phase. Not
// a React component (no hooks of its own); just a render helper that
// takes the shared state bag and returns the <Section> block.
function renderStarPicker({
  starOptionGroups,
  starIngredientIds,
  setStarIngredientIds,
  starSearch,
  setStarSearch,
}) {
  const q = starSearch.trim().toLowerCase();
  // Default state (empty search) shows NOTHING — a 100-row pantry
  // dumped as chips is unusable. The user either searches for
  // something specific ("crisco", "ricotta") or taps one of their
  // already-selected chips. Matching groups only render while a
  // search is active.
  const filteredGroups = q
    ? starOptionGroups
        .map(g => ({
          ...g,
          // Match against the augmented search-token set (label,
          // pantry-cut labels, species synonyms). Falls back to the
          // raw label include for items that didn't get tokens
          // computed for any reason.
          items: g.items.filter(o => {
            if (Array.isArray(o.searchTokens)) {
              return o.searchTokens.some(t => t.includes(q));
            }
            return (o.label || "").toLowerCase().includes(q);
          }),
        }))
        .filter(g => g.items.length > 0)
    : [];
  const allOptions = starOptionGroups.flatMap(g => g.items);
  // Currently-selected chips always pin at the top — they're the
  // user's own picks, not noise, and they need to be tappable to
  // remove without re-searching.
  const selectedOptions = starIngredientIds
    .map(id => allOptions.find(o => o.id === id))
    .filter(Boolean);
  // Drop the search id set from result groups so a selected chip
  // doesn't render twice (once in SELECTED, once in search results).
  const selectedIdSet = new Set(starIngredientIds);
  const visibleGroups = filteredGroups.map(g => ({
    ...g,
    items: g.items.filter(o => !selectedIdSet.has(o.id)),
  })).filter(g => g.items.length > 0);

  return (
    <Section label="MUST INCLUDE — star any pantry item">
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <input
          type="text"
          value={starSearch}
          onChange={(e) => setStarSearch(e.target.value)}
          placeholder="Search pantry (e.g. crisco, ricotta, gochujang)"
          style={{
            width: "100%", padding: "10px 12px",
            background: COLOR.deep, color: COLOR.ink,
            border: `1px solid ${COLOR.border}`, borderRadius: 8,
            fontFamily: FONT.sans, fontSize: 13,
            outline: "none",
          }}
        />
        {/* SELECTED chips always pin at the top once they've been
            added — independent of what's in the search box. Tapping
            removes. */}
        {selectedOptions.length > 0 && (
          <div>
            <div style={{
              fontFamily: FONT.mono, fontSize: 9,
              color: COLOR.gold, letterSpacing: "0.1em",
              marginBottom: 6,
            }}>
              SELECTED ({selectedOptions.length})
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {selectedOptions.map(o => (
                <button
                  key={o.id}
                  onClick={() => setStarIngredientIds(prev => prev.filter(id => id !== o.id))}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    padding: "7px 12px",
                    background: COLOR.goldDeep,
                    border: `1px solid ${COLOR.gold}`,
                    color: COLOR.gold,
                    borderRadius: 20,
                    fontFamily: FONT.sans, fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  <span style={{ fontSize: 14 }}>{o.emoji}</span>
                  {o.label}
                  <span style={{ opacity: 0.6, marginLeft: 4 }}>×</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {/* Empty state: nothing searched AND nothing selected → a
            one-liner hint instead of a wall of chips. Users asked
            explicitly NOT to dump the whole pantry by default. */}
        {!q && selectedOptions.length === 0 && (
          <div style={{
            fontFamily: FONT.sans, fontSize: 12,
            color: COLOR.muted, fontStyle: "italic",
          }}>
            Type to search your pantry — e.g. &quot;crisco&quot;, &quot;ricotta&quot;,
            &quot;gochujang&quot;. Pick anything the dish MUST include.
          </div>
        )}
        {q && visibleGroups.length === 0 && (
          <div style={{
            fontFamily: FONT.sans, fontSize: 12,
            color: COLOR.muted, fontStyle: "italic",
          }}>
            No pantry items match &quot;{starSearch}&quot;.
          </div>
        )}
        {visibleGroups.map(group => (
          <div key={group.key}>
            <div style={{
              fontFamily: FONT.mono, fontSize: 9,
              color: COLOR.muted, letterSpacing: "0.1em",
              marginBottom: 6,
            }}>
              {group.label.toUpperCase()}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {group.items.map(o => {
                const active = starIngredientIds.includes(o.id);
                return (
                  <button
                    key={o.id}
                    onClick={() => setStarIngredientIds(prev => (
                      active ? prev.filter(id => id !== o.id) : [...prev, o.id]
                    ))}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "7px 12px",
                      background: active ? COLOR.goldDeep : COLOR.soil,
                      border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                      color: active ? COLOR.gold : COLOR.dim,
                      borderRadius: 20,
                      fontFamily: FONT.sans, fontSize: 12,
                      cursor: "pointer", transition: "all 0.2s",
                    }}
                  >
                    <span style={{ fontSize: 14 }}>{o.emoji}</span>
                    {o.label}
                    {Array.isArray(o.cuts) && o.cuts.length > 0 && (
                      <span style={{ opacity: 0.7, marginLeft: 2 }}>
                        · {o.cuts.join(", ")}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

function stampDietaryClaims(sketch) {
  if (!sketch || !Array.isArray(sketch.ideal)) return sketch;
  return {
    ...sketch,
    ideal: sketch.ideal.map(row => {
      if (!row || typeof row !== "object") return row;
      const { claims } = extractDietaryClaims(row.name);
      if (claims.length === 0) return row;
      return { ...row, dietaryClaims: claims };
    }),
  };
}

// Dedup two banned-ingredient identity lists. An entry matches on
// ingredientId when it has one, otherwise on lowercased name — so the
// same ingredient arriving from two passes (one canonical-bound, one
// name-only) doesn't double up. Order-preserving, first occurrence wins.
function mergeBannedIdentities(a, b) {
  const out = [];
  const seenId = new Set();
  const seenName = new Set();
  for (const item of [...(a || []), ...(b || [])]) {
    if (!item) continue;
    const id = item.ingredientId || null;
    const nm = item.name ? String(item.name).toLowerCase().trim() : null;
    if (id && seenId.has(id)) continue;
    if (!id && nm && seenName.has(nm)) continue;
    if (id) seenId.add(id);
    if (nm) seenName.add(nm);
    out.push({ name: item.name || null, ingredientId: id });
  }
  return out;
}

// The ingredients the user EXPLICITLY removed from the current sketch.
// pantryEdits.removes holds indices into the LIVE sketch.pantry; this
// resolves them to stable {name, ingredientId} identities so the removal
// survives a regen that throws those indices away. (Soft "didn't promote
// an ideal row to shopping" signals are intentionally NOT included — only
// the explicit remove gesture carries across regens.)
function removedSketchIdentities(sketch, removes) {
  if (!sketch) return [];
  const out = [];
  removes.forEach((i) => {
    const row = sketch.pantry?.[i];
    if (!row) return;
    out.push({ name: row.name || null, ingredientId: row.ingredientId || null });
  });
  return out;
}

// True when a pantry/context row matches one of the banned identities —
// used to strip banned ingredients out of the soft pantry context sent on
// a regen, so Claude isn't staring at ricotta in the "what you have" block
// while being told not to use it.
function rowIsBanned(row, banned) {
  if (!row || !banned || banned.length === 0) return false;
  const id = row.ingredientId || row.canonicalId || null;
  const nm = row.name ? String(row.name).toLowerCase().trim() : null;
  return banned.some((b) =>
    (b.ingredientId && id && b.ingredientId === id) ||
    (b.name && nm && String(b.name).toLowerCase().trim() === nm)
  );
}


export default function AIRecipe({
  pantry = [],
  profile,          // viewer's profile row (dietary, level, skill_levels, …)
  cookLogs = [],    // viewer's recent cook_log rows for the history summary
  ingredientInfo,   // the useIngredientInfo() context — optional
  onCancel,
  onSave,           // (recipe) => Promise — persist privately, then close
  onSilentSave,     // (recipe) => Promise<persistedRecipe> — persist
                    // WITHOUT toasting or closing. Used by the compose-
                    // a-meal flow to lock the anchor in user_recipes
                    // before re-entering setup for a side/dessert.
                    // Falls back to onSave when parent didn't provide.
  onSchedule,       // (recipe) => Promise — parent persists + opens SchedulePicker
  onSaveAndCook,    // (recipe) => Promise — existing save + cook path
  onBackgroundGenerate, // ({ payload, finalizeInputs, titleGuess, subtitleGuess,
                    //    emojiGuess }) => Promise — DEFAULT path for the final
                    // pass. Parent mints a pending Recipe-Book stub, navigates
                    // to it, and runs generation detached so the user is never
                    // stuck on a spinner. When absent, cookFinal falls back to
                    // the inline streaming preview.
  onMealSave,       // ({ name, emoji, cuisine, mealTiming, anchorSlug, pieces })
                    // => Promise<meal> — parent persists a MEAL row
                    // (meals + meal_recipes). Called from the sticky
                    // header's SAVE MEAL CTA once the user has two+
                    // pieces composed. Optional — when absent, the
                    // CTA just closes the overlay and leaves pieces
                    // as loose recipes in the library.
  // Community dedupe: when a community-published recipe already covers
  // the user's prompt, this opens that recipe in CookMode directly
  // instead of paying for AI generation. Receives the full recipe
  // object with `_communityRecipeId` attached so the eventual cook_logs
  // insert can attribute the cook as a vote on the leaderboard. Optional
  // — without it the dedupe surface still renders but the "cook this
  // version" button no-ops.
  onCookExistingRecipe,
  onShoppingAdd,    // (items[]) => void — receives the locked ingredients
                    // promoted to shopping in the tweak phase so the
                    // parent can merge them into the shoppingList state.
                    // Optional — when absent the promotions still show
                    // in the recipe with a "user will pick up" note
                    // but don't land on the actual shopping list.
  onAddToPantry,    // ({name, ingredientId, amount}) => void — opens
                    // the manual add-item drawer pre-seeded with the
                    // ingredient identity. Lets the user add a missing
                    // ingredient directly to pantry instead of routing
                    // through shopping (e.g. they bought nutmeg today
                    // and want to log it as in-pantry now).
  // Library sources used by the "Pick existing" path of the compose
  // flow — filtered to course=target (side/dessert/appetizer) and
  // soft-sorted by cuisine/timing match to the anchor.
  userRecipes = [], // [{ id, recipe, source }, ...] from useUserRecipes
  bundledRecipes = [],
  // Optional pre-fill for the meal-prompt field. The "finished dish"
  // photo flow uses this to seed Claude with the dishName + technique
  // + finishing signals it reverse-engineered from the photo so the
  // generated recipe matches what the user shot. Applied once on mount;
  // the user can still edit the prompt before generating.
  initialMealPrompt = null,
  // Photo-seed pinning. When the parent opens AIRecipe via the photo
  // flow, it can hand us the identified items + sketch so the setup
  // screen can render visible chip strips ("Working from your photo —
  // 4 ingredients identified"). Without these the user sees only a
  // count line and has no confirmation the seed survived. Optional;
  // null when AIRecipe was opened from a normal pantry flow.
  photoSeedItems = null,
  photoSeedSketch = null,
}) {
  // Brand-nutrition lookup for the per-serving rollup. Wrapped in a
  // Map-like shape so the resolver's signature stays source-agnostic.
  const { get: getBrandNutrition } = useBrandNutrition();
  const brandNutrition = useMemo(
    () => ({ get: (k) => getBrandNutrition?.(k) || null }),
    [getBrandNutrition],
  );

  // Subscribe to unit-pref changes at the parent level so step prose
  // re-tokenizes when the picker (inside IngredientsWithPairing's
  // child scope) writes a new preference. Without this, only the
  // child's ingredient list updates — the steps further down stay
  // stale until the next full re-render.
  useUnitPrefsVersion();

  const [phase,  setPhase]  = useState("setup");     // setup | frame_reveal | community_match | clarify_loading | clarify | funnel | allergy_conflict | sketch_loading | tweak | final_loading | preview | error
  const [recipe, setRecipe] = useState(null);
  const [errMsg, setErrMsg] = useState("");
  // "network" when the error matched isNetworkError() — drives a
  // clearer message + a smarter retry that re-fires the SAME call
  // instead of dumping the user back to setup. "other" for
  // everything else.
  const [errKind, setErrKind] = useState("other");
  // Snapshot of which path threw so the retry button knows whether
  // to re-run the sketch or the final-cook step.
  const [errSource, setErrSource] = useState(null); // "sketch" | "final" | null
  // Community pre-gen dedupe (migration 0236). Before paying for the
  // Haiku clarify call + Sonnet generation, check whether the typed
  // prompt matches a community-published recipe that's already been
  // cooked + rated. If yes, surface those candidates so the user can
  // skip generation entirely. communityDedupedFor tracks which prompt
  // we've already deduped on — prevents a re-check loop when the user
  // hits "generate fresh anyway" and re-fires runClarifyThenStart.
  const { match: matchCommunity } = useCommunityDedupe();
  const [communityMatches, setCommunityMatches] = useState([]);
  const [communityDedupedFor, setCommunityDedupedFor] = useState(null);
  // Streaming buffer for the final-pass call. Accumulates Claude's
  // raw JSON text as it arrives. The final_loading render parses it
  // best-effort and shows whatever fields are already there (title
  // first, then ingredients filling in, then steps building) so the
  // user watches the recipe write itself instead of staring at a
  // 30-60s spinner. Cleared at the start of each cookFinal() and
  // discarded once the validated recipe lands.
  const [streamedText, setStreamedText] = useState("");
  // Sticky partial-recipe state. Updated only when the partial JSON
  // parser succeeds at extracting at least a title — never reset to
  // null mid-stream. Without this, the parser oscillates as new
  // tokens arrive (well-formed → succeeds, then a half-emitted
  // string fails, then well-formed again) and the title visibly
  // flashes between "Fudgy Protein Dark Chocolate" and "Cooking it
  // up…" every few hundred ms. Once we have a title, we keep it.
  const [streamedPartial, setStreamedPartial] = useState(null);
  useEffect(() => {
    if (!streamedText) {
      setStreamedPartial(null);
      return;
    }
    const parsed = tryPartialJson(streamedText);
    if (parsed && (parsed.title || Array.isArray(parsed.ingredients) || Array.isArray(parsed.steps))) {
      setStreamedPartial(parsed);
    }
  }, [streamedText]);
  // Pre-cook servings adjuster — the recipe ships at recipe.serves
  // (Claude's typical default) but the user gets a +/- stepper in
  // the preview header to scale up/down before cooking. null = use
  // the recipe's authored serves; any other integer scales via
  // scaleRecipe(). Resets whenever a fresh recipe lands so a user
  // who set 6-serving last draft doesn't carry that into a new
  // dish.
  const [servingsOverride, setServingsOverride] = useState(null);
  useEffect(() => { setServingsOverride(null); }, [recipe?.slug]);
  // Scaled recipe for the preview render + commit handlers. When the
  // user hits SAVE / SCHED / COOK IT, the persisted/cooked recipe is
  // the scaled one — they're cooking what they see, not the original
  // 4-person draft.
  const previewRecipe = useMemo(() => {
    if (!recipe) return null;
    const baseServes = Math.max(1, Number(recipe.serves) || 1);
    if (servingsOverride == null || servingsOverride === baseServes) return recipe;
    return scaleRecipe(recipe, servingsOverride) || recipe;
  }, [recipe, servingsOverride]);
  // Concept frame — pre-picked corner of recipe space (recipe type +
  // optional cuisine/technique/hero/vibe). Drawn from
  // src/data/conceptFrames.js. Two paths populate this:
  //   1. SURPRISE ME button → pickConceptFrame() with full frame visible
  //      to the user on the frame_reveal screen. User can REROLL freely
  //      (no AI cost) before tapping DRAFT IT.
  //   2. Typed prompt + GENERATE → silent sub-frame seed inside start()
  //      based on classifier tier. User sees the frame as a small chip
  //      on the sketch / preview screen for transparency.
  // Either way, the frame becomes a HARD CONSTRAINT in the edge fn
  // prompt — pushing Claude off its modal answer (the Cacio-e-Pepe rut)
  // into the frame's specific corner.
  const [conceptFrame, setConceptFrame] = useState(null);
  const [changeTypeOpen, setChangeTypeOpen] = useState(false);
  // Frame-detail sheet — opens when the user taps the "FRAMED BY:" chip
  // on the sketch / preview screens. Shows the full axis breakdown and
  // a REROLL button that re-seeds + re-fires the sketch.
  const [frameDetailOpen, setFrameDetailOpen] = useState(false);
  // Clarifications — Haiku planning-mode questions returned BEFORE the
  // sketch. The user answers (or skips) each on the clarify phase, and
  // the answers ride into the sketch + final prompts as explicit
  // user-stated preferences. Empty array means Haiku judged nothing was
  // ambiguous (e.g. "lasagna with bechamel") — clarify phase is
  // skipped entirely in that case.
  const [clarifications, setClarifications] = useState([]);
  // Appetite funnel — the mood-first drill-down shown when the clarify
  // step detects an OPEN axis ("chicken with sauce"). { axis, moodQuestion,
  // moods:[{id,label,lanes:[{id,label,blurb}]}] }. Mutually exclusive with
  // flat clarifications; the chosen lane is synthesized into a single
  // clarification answer that anchors the draft (see pickFunnelLane).
  const [funnel, setFunnel] = useState(null);
  // Reroll plumbing. funnelSeenRef accumulates every mood + lane label
  // shown for the CURRENT prompt's funnel session so "fresh 4" never
  // repeats an idea the user already saw (both views). Reset when a
  // fresh clarify run starts. funnelRerolling drives the button spinner.
  const funnelSeenRef = useRef({ moods: [], lanes: [] });
  const [funnelRerolling, setFunnelRerolling] = useState(false);
  // The prompt that most recently went THROUGH clarify. The regen
  // fast-path must only skip clarify when re-drafting the SAME
  // prompt — previousTitles persists across drafts, and gating on it
  // alone meant the funnel fired once per session and then never
  // again (the "it stopped triggering" bug).
  const clarifiedPromptRef = useRef(null);
  // Allergy guard. ON by default — the user's allergens are hard-excluded
  // from generation and the funnel. The user can turn it OFF for a session
  // ("cooking for someone without my allergies") via the setup toggle, or
  // via the conflict interstitial when they explicitly request a dish that
  // contains one of their allergens. allergyConflicts holds the matched
  // allergen labels driving that interstitial.
  const [allergyGuardOff, setAllergyGuardOff] = useState(false);
  const [allergyConflicts, setAllergyConflicts] = useState([]);
  // When the user accepts an allergy-safe SUBSTITUTE for a dish that
  // classically uses their allergen, this holds { allergens, subsHint } so
  // the preview can surface a "this is an alternative — want the original?"
  // notice with a one-tap regenerate-original + a pointer to Settings.
  const [allergyAltInfo, setAllergyAltInfo] = useState(null);
  // POST-GENERATION ALLERGEN BACKSTOP (safety-critical). The prompt's
  // ABSOLUTE block is the primary rail, but context modes can drop it
  // (the lean-regen bug shipped a "dairy-free" recipe carrying a cup
  // of parmesan) and models can slip. Every finished recipe is scanned
  // against the viewer's allergies at landing; hits render a red
  // banner on the preview. Component state, NOT stamped on the recipe
  // — conflicts are viewer-specific and must never fossilize into the
  // shared row (the book computes its own chips per viewer).
  const [finalAllergenConflicts, setFinalAllergenConflicts] = useState([]);
  // CHEF'S BALANCE NOTE (roadmap A4) — deterministic taste-balance
  // tally over the finished recipe's bound canonicals. One gold
  // remark suggesting ONE finishing improvement; never mutates,
  // never blocks (tasteBalanceCritic.js).
  const [balanceNote, setBalanceNote] = useState(null);
  const scanFinalForAllergens = (rec) => {
    try {
      setBalanceNote(tasteBalanceNote(rec, ingredientInfo?.getInfo || (() => null)));
    } catch {
      setBalanceNote(null);
    }
    if (!rec || allergyGuardOff || !Array.isArray(userAllergies) || userAllergies.length === 0) {
      setFinalAllergenConflicts([]);
      return;
    }
    try {
      setFinalAllergenConflicts(recipeAllergyConflicts(rec, userAllergies));
    } catch {
      setFinalAllergenConflicts([]);
    }
  };
  // Carries the in-flight conceptFrame across the allergy-conflict
  // interstitial so "generate anyway" resumes with the same frame.
  const pendingFrameRef = useRef(null);
  // Deferred resume after the user turns the guard off in the conflict
  // interstitial. We can't call runClarifyThenStart synchronously — it
  // (and everything it calls: buildAIContext, the clarify request) would
  // read the stale allergyGuardOff=false from this render's closure. So we
  // flip the guard + set this flag, and an effect re-fires the draft on the
  // next render, where the guard is committed off everywhere.
  const [resumeDraftAfterGuardOff, setResumeDraftAfterGuardOff] = useState(false);
  const userAllergies = useMemo(
    () => (Array.isArray(profile?.allergies) ? profile.allergies : []),
    [profile?.allergies],
  );
  // Map of question id → chosen option id (or "chef-choice" when the
  // user skipped). Initialized to all-chef-choice when clarifications
  // arrive, so SKIP ALL is always a one-tap exit.
  const [clarificationAnswers, setClarificationAnswers] = useState({});
  // Titles we've already shown the user this session — handed to the
  // edge function on every draft so REGEN produces a genuinely
  // different dish instead of converging on the same one every time.
  const [previousTitles, setPreviousTitles] = useState([]);
  // Per-action busy state so exactly one button shows SAVING… and
  // the other two stay tappable / disabled appropriately.
  const [busy,   setBusy]   = useState(null);         // null | "save" | "schedule" | "cook"

  // Sketch + tweak state. The sketch holds the rough draft (title +
  // dual IDEAL/PANTRY ingredient lists, no steps). pantryEdits is
  // the diff the user applied during the tweak phase: per-row
  // swaps to a different pantry item, removes, extra adds (e.g.
  // "throw in egg whites"), and ideal items promoted to shopping
  // ("yes I'll buy the mozzarella"). Combined into the locked
  // ingredient list on COOK THIS.
  const [sketch, setSketch] = useState(null);
  // proposedCanonicals — list of synthetics the AI minted via the
  // proposedCanonical path. Each: { slug, name, fallbackId }. The
  // sketch tweak phase shows an Accept/Decline banner per entry so
  // the user can grow the registry deliberately or fall back to a
  // generic umbrella. Cleared when the user decides on each one.
  const [proposedCanonicals, setProposedCanonicals] = useState([]);
  const [pantryEdits, setPantryEdits] = useState({
    swaps:    {},          // sketch.pantry index → pantry row id
    removes:  new Set(),   // sketch.pantry indices the user dropped
    adds:     [],          // [{ name, amount, ingredientId, pantryItemId, emoji }]
    shopping: new Set(),   // sketch.ideal indices the user wants on the shopping list
  });
  // Ingredients the user removed and then REGENERATED past. pantryEdits.removes
  // is index-based and gets wiped on every fresh sketch (the setPantryEdits reset
  // in the sketch handler), so a removal made right before a reroll was silently
  // lost: the new sketch re-proposed the same ingredient — the "I took out ricotta
  // and it came back" bug. This identity-based list (name + ingredientId) survives
  // regens. It's folded into the sketch payload (so the reroll never re-proposes
  // the dropped ingredient and the pantry soft-context drops it too) and into the
  // final ban list. Reset only on a genuinely fresh session, never per-sketch.
  const [bannedAcrossRegens, setBannedAcrossRegens] = useState([]);
  // Inline picker open state. swapOpenIdx = which pantry-row's swap
  // popover is open (null = none). addOpen = "+ ADD" picker visible.
  // swapSearch is the live filter query inside the open swap popover
  // — resets every time a different row's picker opens.
  const [swapOpenIdx, setSwapOpenIdx] = useState(null);
  const [swapSearch,  setSwapSearch]  = useState("");
  // Override toggle for the swap picker. When false (default), the
  // picker hides incompatible candidates (score 0 — different food
  // category and no token overlap). When true, the user has
  // explicitly asked to see the full pantry including the rough
  // matches. Resets whenever the picker closes so each open starts
  // with the safe default.
  const [showIncompatSwaps, setShowIncompatSwaps] = useState(false);
  const openSwapPicker = (idx) => {
    setSwapOpenIdx(idx);
    setSwapSearch("");
    setShowIncompatSwaps(false);   // each open starts with the safe gate on
  };
  const [addOpen, setAddOpen]         = useState(false);
  // Revision instruction for the FINAL cook — the user's "make it
  // spicier, skip the garlic in the steps" note. Sent as
  // prefs.recipeFeedback on the second call.
  const [recipeFeedback, setRecipeFeedback] = useState("");

  // Prefs. mealPrompt is the hero input — renamed from "notes" to
  // signal that the user is DIRECTING an AI, not scribbling a
  // secondary note. Lives at the top of the setup screen.
  const [mealPrompt, setMealPrompt] = useState(initialMealPrompt || "");
  // One-shot prefill when initialMealPrompt arrives after mount (the
  // CreateMenu→AIRecipe handoff path fires this prop a tick after
  // mount). Guard with a ref so user-typed edits don't get clobbered
  // on subsequent renders.
  const initialMealPromptAppliedRef = useRef(false);
  // Photo-flow bypass — when the parent hands us a photo seed AND
  // a pre-composed meal prompt, the user has already reviewed the
  // ingredients on PhotoReviewStage. Forcing them through the AIRecipe
  // setup screen one more time is friction without payoff. Auto-fire
  // the draft as soon as mealPrompt lands. The user can still cancel
  // or tweak on the next phase. Guard with a ref so the auto-fire
  // happens exactly once per mount.
  const autoStartFiredRef = useRef(false);
  useEffect(() => {
    if (initialMealPromptAppliedRef.current) return;
    if (initialMealPrompt && initialMealPrompt.trim()) {
      setMealPrompt(initialMealPrompt);
      initialMealPromptAppliedRef.current = true;
    }
  }, [initialMealPrompt]);
  // Dish contract (classified from mealPrompt). Session-cached so
  // regens don't re-classify. Three tiers:
  //   SPECIFIC  → aliases + rules injected into the prompt; deterministic
  //               post-check enforces that the output title matches.
  //   FAMILY    → family + examples injected; output must fit the family.
  //   OPEN      → (empty mealPrompt) drop pantry entirely, dream mode.
  //   FREEFORM  → (unrecognizable text) fall back to verbatim quoting.
  // classifiedPromptRef tracks which mealPrompt produced the current
  // contract so re-typing the SAME text doesn't refire the Haiku call.
  const [dishContract, setDishContract] = useState(null);
  const [classifiedFrom, setClassifiedFrom] = useState("");
  const [mealTiming, setMealTiming] = useState("any");
  const [course,     setCourse]     = useState("any");
  // Priority mode — which side of the "I want X / I have Y" tension
  // wins when the two conflict. "category" (default) makes the course
  // constraint authoritative and filters the pantry palette to
  // compatible items (bake = flour/sugar/butter/eggs/etc, not hot
  // dogs). "pantry" keeps the old behavior where Claude drafts around
  // whatever's stocked and bends the course to fit. Hidden from the
  // UI when course === "any" — no tension to resolve.
  const [priority,   setPriority]   = useState("classic");
  const [starIngredientIds, setStarIngredientIds] = useState([]);
  // Search query for the MUST-INCLUDE picker. Filters starOptionGroups
  // by substring (case-insensitive) against item labels so a user
  // searching "crisco" in a 104-row pantry doesn't have to scroll.
  const [starSearch, setStarSearch] = useState("");
  const [cuisine,    setCuisine]    = useState("any");
  const [time,       setTime]       = useState("medium");
  const [difficulty, setDifficulty] = useState("medium");
  // Explicit portion count (null = Auto: occasion-derived by the
  // classifier). When set, overrides the derived intent at fire time
  // so quantities are authored for the right table from step one —
  // the previous flow never told the model who it was cooking for.
  const [serves,     setServes]     = useState(null);

  // Compose-a-meal state. pairWith carries the anchor dish's identity
  // into Claude's prompt so a side/dessert/appetizer is drafted to
  // COMPLEMENT the main (contrast, balance, no duplicated protein)
  // rather than in isolation. mealInProgress tracks every piece the
  // user has committed to this meal so the sticky header can render
  // the composition. Each piece's `recipe` is the row we actually
  // persisted to user_recipes — pieces are reusable standalone
  // recipes, not meal-owned rows.
  const [pairWith,        setPairWith]        = useState(null);
  //   { title, course, cuisine, ingredients: [{name, amount}] } | null
  const [mealInProgress,  setMealInProgress]  = useState(null);
  //   { anchor: recipe, pieces: [{ course, recipe }] } | null

  // "+ Add X" sheet state. When non-null, the two-option sheet
  // ("Draft new" / "Pick existing") is open for this course.
  const [addSheetCourse,  setAddSheetCourse]  = useState(null);
  //   null | "side" | "dessert" | "appetizer"
  // When non-null, the library picker is open, filtered to this course.
  const [pickExistingFor, setPickExistingFor] = useState(null);
  //   null | "side" | "dessert" | "appetizer"

  // Draft persistence. When the user is mid-tweak and leaves the page,
  // we stash the sketch + all editable state in localStorage so they
  // can pick up where they were without burning another Claude call.
  // userId keys the entry per-account so multi-user devices don't
  // cross-contaminate drafts. Hydration happens once via a ref-guarded
  // effect; save runs debounced on any tweak-phase state change; clear
  // fires on successful final cook or explicit "start fresh."
  const userId = profile?.id || profile?.user_id || null;
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  // Protein picker source — collapse the pantry to one chip per
  // canonical (5 cans of tuna = one TUNA chip, not five) and group
  // by category so the user can scan MEAT / POULTRY / SEAFOOD /
  // PLANT at a glance instead of reading a wall of chips. Uses the
  // canonical's full `name` (not `shortName`) so "Ground Beef" and
  // "Ground Pork" don't both read as just "Ground."
  // MUST-INCLUDE picker — the full pantry (not just proteins).
  // Previously this was gated to meat/poultry/seafood + a hand-picked
  // plant-protein set, but users (rightly) wanted to tag things like
  // Crisco, tortillas, brown butter, or a specific cheese when that
  // ingredient was the POINT of the dish. Now every pantry row with
  // kind="ingredient" (i.e. not a compound dish or leftover) is
  // pickable. A search input sits above the groups so finding one
  // item in a 100-row pantry doesn't require scrolling.
  //
  // Grouping: prefer meat/poultry/seafood/dairy/produce/grains/fats/
  // pantry/frozen in that visual order; anything with an unknown
  // category falls under "Other" at the end.
  const starOptionGroups = useMemo(() => {
    // Per-canonical search-token bucket. A typed query like "steak"
    // should match the user's beef rows even though no row's display
    // label contains "steak"; "poultry" should match chicken; "fish"
    // should match salmon/tuna. The token set covers three sources:
    //   1. The canonical's display name (literal match path).
    //   2. Cuts the user's pantry rows actually carry (so "ribeye"
    //      matches the beef row tagged cut=ribeye).
    //   3. A small species-level synonym map for the categories users
    //      type by colloquial name (steak → beef, poultry → chicken).
    // Synonyms are intentionally narrow — only the words a cook
    // colloquially uses in place of a canonical's name. Adding too
    // many becomes search noise.
    const SYNONYMS = {
      beef:    ["steak", "beef"],
      chicken: ["poultry"],
      pork:    [],
      turkey:  ["poultry"],
      salmon:  ["fish"],
      tuna:    ["fish"],
      cod:     ["fish"],
      shrimp:  ["seafood"],
    };
    // Resolve a slug's "search anchor" — for legacy compound slugs
    // (ribeye / ny_strip / chicken_breast) walk through CANONICAL_ALIASES
    // to the species hub so a row written before migration 0122 still
    // picks up the hub's synonyms ("steak", "poultry"). Returns
    // { base, cut } where base is the species hub (or the slug itself
    // when no alias) and cut is the alias's cut tag (or null).
    const resolveAnchor = (slug) => {
      const alias = CANONICAL_ALIASES[slug];
      if (alias?.base) return { base: alias.base, cut: alias.cut || null };
      return { base: slug, cut: null };
    };
    const byCanonical = new Map();
    for (const row of pantry) {
      // Compound dishes (frozen pizzas) and leftovers (last night's
      // rice) carry ingredients but aren't pickable as "must
      // include" tags — you don't build a dish around the fact that
      // you have a leftover.
      if (row?.kind && row.kind !== "ingredient") continue;
      const slug = row.ingredientId || row.canonicalId;
      if (!slug) continue;
      if (!byCanonical.has(slug)) {
        const canon = findIngredient(slug);
        const tokens = new Set();
        if (canon?.name)      tokens.add(canon.name.toLowerCase());
        if (canon?.shortName) tokens.add(canon.shortName.toLowerCase());
        if (row?.name)        tokens.add(String(row.name).toLowerCase());
        tokens.add(slug.toLowerCase());
        // Walk to base canonical so legacy compound slugs ("ny_strip"
        // → "beef") inherit the hub's synonyms. Without this, a beef
        // pantry row written before the cut-axis split would match
        // "ny_strip" but not "steak".
        const anchor = resolveAnchor(slug);
        if (anchor.base && anchor.base !== slug) {
          const baseCanon = findIngredient(anchor.base);
          if (baseCanon?.name) tokens.add(baseCanon.name.toLowerCase());
          tokens.add(anchor.base.toLowerCase());
        }
        if (anchor.cut) {
          const lbl = cutLabel(anchor.cut) || anchor.cut;
          tokens.add(lbl.toLowerCase());
          tokens.add(String(anchor.cut).toLowerCase().replace(/_/g, " "));
        }
        for (const syn of (SYNONYMS[slug] || [])) tokens.add(syn);
        if (anchor.base !== slug) {
          for (const syn of (SYNONYMS[anchor.base] || [])) tokens.add(syn);
        }
        byCanonical.set(slug, {
          id: slug,
          label: canon?.name || row.name || slug,
          emoji: row.emoji || canon?.emoji || "🍽️",
          category: canon?.category || row.category || null,
          slug,
          // The cuts the user actually has in pantry for this
          // canonical, in display-label form. Fed into the chip
          // sub-line so "Beef" reads as "Beef · NY strip, Ribeye".
          cuts: new Set(),
          // Search tokens the picker filter checks against — see
          // SYNONYMS above.
          searchTokens: tokens,
        });
      }
      // Add the row's cut to the canonical's cut set + token set.
      const cutSlug = row?.cut;
      if (cutSlug) {
        const opt = byCanonical.get(slug);
        const lbl = cutLabel(cutSlug) || cutSlug;
        opt.cuts.add(lbl);
        opt.searchTokens.add(lbl.toLowerCase());
        opt.searchTokens.add(String(cutSlug).toLowerCase().replace(/_/g, " "));
      }
    }
    // Materialize the per-opt cut set into a sorted comma label and
    // freeze searchTokens to a plain array so consumers don't pay the
    // Set#has cost per filter-render.
    for (const opt of byCanonical.values()) {
      opt.cuts = [...opt.cuts].sort();
      opt.searchTokens = [...opt.searchTokens];
    }
    const categoryOrder = [
      { key: "meat",     label: "Meat" },
      { key: "poultry",  label: "Poultry" },
      { key: "seafood",  label: "Seafood" },
      { key: "dairy",    label: "Dairy & eggs" },
      { key: "produce",  label: "Produce" },
      { key: "grains",   label: "Grains & starches" },
      { key: "fats",     label: "Fats & oils" },
      { key: "pantry",   label: "Pantry" },
      { key: "frozen",   label: "Frozen" },
      { key: "beverage", label: "Beverages" },
      { key: "other",    label: "Other" },
    ];
    const groups = new Map(categoryOrder.map(c => [c.key, { ...c, items: [] }]));
    // Map a handful of equivalent category strings to the canonical
    // bucket keys above. Anything unmapped lands under "other" so the
    // picker never silently loses a pantry row.
    const CATEGORY_BUCKET = {
      meat: "meat", poultry: "poultry", seafood: "seafood",
      dairy: "dairy", egg: "dairy", eggs: "dairy",
      produce: "produce", fruit: "produce", vegetable: "produce",
      grain: "grains", grains: "grains", starch: "grains", bread: "grains", pasta: "grains", rice: "grains",
      fat: "fats", fats: "fats", oil: "fats", oils: "fats",
      pantry: "pantry", condiment: "pantry", spice: "pantry", sauce: "pantry", baking: "pantry",
      frozen: "frozen",
      beverage: "beverage", drink: "beverage",
    };
    for (const opt of byCanonical.values()) {
      const bucket = CATEGORY_BUCKET[opt.category] || "other";
      groups.get(bucket).items.push(opt);
    }
    const out = [];
    for (const c of categoryOrder) {
      const g = groups.get(c.key);
      if (g.items.length === 0) continue;
      g.items.sort((a, b) => a.label.localeCompare(b.label));
      out.push(g);
    }
    return out;
  }, [pantry]);

  const pantryCount = pantry.length;

  // Shared prefs payload — both the sketch and final calls assemble
  // the same set; only mode + lockedIngredients differ.
  const buildPrefs = (extra = {}) => {
    // bake/prep courses don't belong to a meal slot — drop any stale
    // mealTiming so the backend's HARD CONSTRAINTS block doesn't get a
    // "breakfast" tag on a chicken-stock prep.
    const isComponentCourse = course === "bake" || course === "prep";
    return {
      cuisine, time, difficulty,
      mealPrompt: mealPrompt.trim() || undefined,
      mealTiming: (isComponentCourse || mealTiming === "any") ? undefined : mealTiming,
      course: course === "any" ? undefined : course,
      // Always send priority on BOTH sketch and final — the edge fn
      // previously had an inconsistency where the final pass dropped
      // priority when course="any" and the edge fn silently defaulted
      // it back to "category", flipping precedence mid-session. The
      // user's "category-first" vs "pantry-first" intent is authoritative
      // on every call in the draft.
      priority,
      starIngredientIds: starIngredientIds.length ? starIngredientIds : undefined,
      // Compose-a-meal anchor. Only present when the user clicked
      // "+ Add side/dessert/appetizer" from a main's preview; Claude
      // uses it to build something that complements rather than
      // duplicates the anchor. The meta split keeps the identity
      // payload (title/cuisine/ingredients) separate from the
      // logistics the edge fn renders as side-dish constraints
      // (portions must match; oven conflicts steer to stovetop).
      pairWith: pairWith || undefined,
      pairWithMeta: pairWith
        ? {
            serves: pairWith.serves ?? null,
            totalTimeMinutes: pairWith.totalTimeMinutes ?? null,
            oven: !!pairWith.oven,
          }
        : undefined,
      // Clarifications — pre-sketch planning answers. Each entry is
      // { id, question, answer } where answer is either the human-
      // readable option label the user picked (mapped from the
      // option id via clarifications[*].options) or the literal
      // "chef-choice" when they skipped that question. Edge fn
      // renders these in the USER PREFERENCES block so Claude treats
      // them as hard preferences. Empty array (or undefined) means
      // no clarify step ran for this draft — preserve undefined so
      // the prompt block doesn't render an empty "(none)" line.
      clarifications: clarifications.length > 0
        ? clarifications.map(q => {
            // New shape: clarificationAnswers[q.id] = { ids: [], custom: "" }.
            // Empty ids + empty custom = chef-choice. Otherwise we
            // join the picked option labels + custom text into one
            // comma-separated answer Claude reads as a multi-pref
            // ("easy weeknight, vegetarian" etc).
            const a = clarificationAnswers[q.id] || { ids: [], custom: "" };
            const ids = Array.isArray(a.ids) ? a.ids : [];
            const customText = typeof a.custom === "string" ? a.custom.trim() : "";
            const labels = ids
              .map(id => (q.options || []).find(o => o.id === id)?.label)
              .filter(Boolean);
            const parts = [...labels];
            if (customText) parts.push(customText);
            return {
              id: q.id,
              question: q.question,
              answer: parts.length === 0 ? "chef-choice" : parts.join(", "),
            };
          })
        : undefined,
      ...extra,
    };
  };

  // Resolve the dish contract for the current mealPrompt. Cached by
  // the exact string so re-typing the same prompt doesn't refire the
  // Haiku call; regens within a session reuse the contract.
  // Hydrate a saved draft on first mount. Runs once per userId — we
  // gate on draftHydrated so a remount (hot reload, parent re-render)
  // doesn't re-restore over in-flight user edits. Drops the user
  // straight into the tweak phase when a draft is present so the
  // resume experience is "click AI Recipe, your sketch is back."
  useEffect(() => {
    if (draftHydrated) return;
    if (!userId) { setDraftHydrated(true); return; }
    const d = loadDraft(userId);
    setDraftHydrated(true);
    if (!d) return;
    // Only hydrate when the setup screen is still fresh — if the
    // user has already typed into mealPrompt or picked a course, they
    // started a new draft and we shouldn't clobber it.
    if (mealPrompt || starIngredientIds.length > 0 || course !== "any") return;
    if (d.recipe) setRecipe(d.recipe);
    if (d.sketch) setSketch(d.sketch);
    setPantryEdits(d.pantryEdits);
    setBannedAcrossRegens(Array.isArray(d.bannedAcrossRegens) ? d.bannedAcrossRegens : []);
    setRecipeFeedback(d.recipeFeedback);
    setPreviousTitles(d.previousTitles);
    setMealPrompt(d.mealPrompt);
    setMealTiming(d.mealTiming);
    setCourse(d.course);
    setPriority(d.priority);
    setStarIngredientIds(d.starIngredientIds);
    setCuisine(d.cuisine);
    setTime(d.time);
    setDifficulty(d.difficulty);
    setServes(d.serves ?? null);
    setDishContract(d.dishContract);
    setClassifiedFrom(d.classifiedFrom);
    setConceptFrame(d.conceptFrame || null);
    setDraftSavedAt(d.savedAt);
    // Restore to the phase they last reached. Preview wins over tweak
    // when a full recipe is on the draft — the user spent money on
    // Sonnet to get it, losing it to a tab close was the bug.
    setPhase(d.recipe && d.phase === "preview" ? "preview" : "tweak");
    // Deps intentionally include the setup-state we read in the
    // freshness guard — when they change, the effect re-runs and
    // the draftHydrated flag short-circuits it on line 1. Cheap
    // no-op after the first hydration.
  }, [userId, draftHydrated, mealPrompt, starIngredientIds, course]);

  // Merge AI-pinned canonicals (the "+" button on KitchenCard) into
  // starIngredientIds. Runs AFTER draft hydration so a hydrated draft's
  // stars are preserved; pins layer on top, deduped. Also subscribes
  // to the pinned store so a user pinning more items in another tab —
  // or via a deep link — flows through without remount. Pins stay in
  // localStorage until the user actually generates a sketch (see the
  // generate handler below for clearPinned), so the user can keep
  // adding pins and see them all surface here.
  useEffect(() => {
    if (!draftHydrated) return;
    const apply = () => {
      const pinned = loadAiPinned(userId);
      if (pinned.length === 0) return;
      setStarIngredientIds(prev => {
        const seen = new Set(prev);
        const merged = [...prev];
        for (const id of pinned) {
          if (!seen.has(id)) {
            seen.add(id);
            merged.push(id);
          }
        }
        return merged.length === prev.length ? prev : merged;
      });
    };
    apply();
    return subscribePinned(apply);
  }, [draftHydrated, userId]);

  // Auto-save on any tweak-OR-preview state change, debounced so
  // rapid edits don't thrash localStorage. 500ms matches the human
  // "paused thinking" threshold. Preview phase is included because
  // the generated recipe is the most expensive artifact in the
  // session (Sonnet call, real dollars) and losing it to a tab
  // close was a billing-visible bug. Save clears only on explicit
  // save/schedule/cook actions below.
  const draftTimerRef = useRef(null);
  useEffect(() => {
    if (!draftHydrated || !userId) return;
    if (phase !== "tweak" && phase !== "preview") return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      saveDraft(userId, {
        phase, recipe,
        sketch, pantryEdits, bannedAcrossRegens, recipeFeedback, previousTitles,
        mealPrompt, mealTiming, course, priority, starIngredientIds,
        cuisine, time, difficulty, serves, dishContract, classifiedFrom,
        conceptFrame,
      });
      setDraftSavedAt(new Date().toISOString());
    }, 500);
    return () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    };
  }, [
    userId, draftHydrated, phase, recipe, sketch, pantryEdits, bannedAcrossRegens, recipeFeedback,
    previousTitles, mealPrompt, mealTiming, course, priority,
    starIngredientIds, cuisine, time, difficulty, serves, dishContract,
    classifiedFrom, conceptFrame,
  ]);

  // Explicit abandon — drops the saved draft and resets tweak state
  // back to a blank setup screen. Wired into the "Start fresh" banner
  // that shows when the user has an un-consumed resume offer.
  const abandonDraft = () => {
    if (userId) clearDraft(userId);
    setDraftSavedAt(null);
    setRecipe(null);
    setSketch(null);
    setPantryEdits({ swaps: {}, removes: new Set(), adds: [], shopping: new Set() });
    setBannedAcrossRegens([]);
    setRecipeFeedback("");
    setPreviousTitles([]);
    setMealPrompt("");
    setMealTiming("any");
    setCourse("any");
    setPriority("classic");
    setStarIngredientIds([]);
    setCuisine("any");
    setTime("medium");
    setDifficulty("medium");
    setServes(null);
    setDishContract(null);
    setClassifiedFrom("");
    setConceptFrame(null);
    setClarifications([]);
    setClarificationAnswers({});
    setFunnel(null);
    // Full reset → allergy guard returns to its safe default (ON).
    setAllergyGuardOff(false);
    setAllergyConflicts([]);
    setAllergyAltInfo(null);
    setPhase("setup");
  };

  const ensureContract = async ({ forceFresh = false } = {}) => {
    const target = (mealPrompt || "").trim();
    if (!forceFresh && dishContract && classifiedFrom === target) return dishContract;

    // Photo-flow short-circuit. When AIRecipe is auto-firing from a
    // photo seed, the meal prompt is a composed multi-segment string
    // ("Triple-layer chocolate cake · Techniques: bake · Finishing:
    // ganache") that classifyDishPrompt routinely mis-tiers as OPEN —
    // which drops the pantry entirely and Claude defaults to the
    // modal answer (Cacio e Pepe rut).
    //
    // Bypass the classifier when:
    //   - the parent supplied a photoSeedSketch with a dishName, OR
    //   - photoSeedItems is set (raw-ingredients mode)
    //
    // Synthesize a SPECIFIC contract directly:
    //   tier: "SPECIFIC", dishName, rules: "use the named ingredients"
    //
    // This forces isDreamMode=false downstream so the pantry stays
    // attached and Claude works against the actual constraint.
    if (photoSeedSketch?.dishName) {
      const synthetic = {
        tier: "SPECIFIC",
        dishName: photoSeedSketch.dishName,
        aliases: [],
        rules: `User photographed this dish. Build a recipe that matches what they shot — use the identified ingredients, respect the techniques (${(photoSeedSketch.techniques || []).join(", ") || "—"}) and finishing (${photoSeedSketch.finishing?.type || "—"}). Do NOT pivot to a default pasta or unrelated dish. The user wants to recreate THIS specific plate.`,
        rawPrompt: target,
      };
      const synIntent = resolveCookingIntent({ mealPrompt: target });
      if (synIntent) synthetic.intent = synIntent;
      setDishContract(synthetic);
      setClassifiedFrom(target);
      return synthetic;
    }
    if (photoSeedItems && photoSeedItems.length > 0) {
      // Raw-ingredients mode — no sketch / dishName, but we still have
      // a constraining ingredient list. Synthesize a FAMILY contract
      // (looser than SPECIFIC, tighter than OPEN) anchored to the
      // ingredients themselves.
      const names = photoSeedItems
        .map((i) => i?.name)
        .filter((n) => typeof n === "string" && n.trim())
        .slice(0, 10);
      const synthetic = {
        tier: "FAMILY",
        familyName: "Recipe from photographed ingredients",
        familyExamples: names,
        rules: `User photographed these ingredients: ${names.join(", ")}. Build a recipe that uses them directly. Do NOT pivot to a default pasta dish or substitute with pantry staples Claude prefers. The user's pantry IS what's in the photo.`,
        rawPrompt: target,
      };
      const synIntent = resolveCookingIntent({ mealPrompt: target });
      if (synIntent) synthetic.intent = synIntent;
      setDishContract(synthetic);
      setClassifiedFrom(target);
      return synthetic;
    }

    const c = await classifyDishPrompt(target);
    // Degraded fallback (classifier flaked — API error, parse failure,
    // token denial): never let it poison the cache. If we already hold
    // a REAL contract for this same prompt (forceFresh regen path),
    // keep it — dropping a known-good SPECIFIC contract to a flaked
    // FREEFORM silently kills the copycat trigger + dish pinning for
    // the sketch AND the later final (which cache-hits). With no prior
    // contract, use the fallback for THIS draft but don't cache it, so
    // the next ensureContract retries the classifier.
    if (c?.degraded) {
      if (dishContract && classifiedFrom === target) return dishContract;
      const intent0 = resolveCookingIntent({ mealPrompt: target, derivedIntent: null });
      return intent0 ? { ...c, intent: intent0 } : c;
    }
    // Cooking-intent cascade (curated + classifier-derived). The vibe
    // BRIDGE tier is folded in later, at fire-time, against the active
    // concept frame (which can change via SURPRISE-ME reroll while this
    // contract stays cached by mealPrompt). See withFireTimeIntent.
    const intent = resolveCookingIntent({ mealPrompt: target, derivedIntent: c?.intent || null });
    const withIntent = intent ? { ...c, intent } : c;
    setDishContract(withIntent);
    setClassifiedFrom(target);
    return withIntent;
  };

  // Fold the concept-frame vibe (bridge tier) into a contract's cooking
  // intent at fire-time. Curated > classifier-derived > vibe, per axis.
  // Returns the contract (with intent attached) or undefined when null.
  const withFireTimeIntent = (contract, frame) => {
    // An explicit Serves pick beats every derived source — the user
    // told us who's at the table. It must survive even when no
    // contract classified (blank prompt + surprise-me paths).
    const userServes = Number.isFinite(Number(serves)) && Number(serves) >= 1
      ? Math.round(Number(serves)) : null;
    if (!contract) {
      // Tier MUST be FREEFORM: verifyContract short-circuits pass on
      // it and the prompt builder adds no dish-ask block. A tier-less
      // contract falls into the FAMILY branch with no familyName and
      // fails EVERY draft (burning the paid contract retry); OPEN
      // would re-fire the DREAM MODE banner. FREEFORM = "no dish
      // pinned, intent only" — exactly what a bare Serves pick is.
      return userServes ? { tier: "FREEFORM", intent: { serves: userServes } } : undefined;
    }
    const intent = resolveCookingIntent({
      mealPrompt: (mealPrompt || "").trim(),
      derivedIntent: contract.intent || null,
      conceptFrame: frame,
    });
    const merged = userServes
      ? { ...(intent || contract.intent || {}), serves: userServes }
      : intent;
    return merged ? { ...contract, intent: merged } : contract;
  };

  // SURPRISE ME entry — pick a fresh full concept frame and route to
  // the reveal phase. The user can REROLL freely (no AI cost) before
  // tapping DRAFT IT, which enters the existing sketch flow with the
  // frame attached. Triggered by the SURPRISE ME button on the setup
  // screen.
  const surpriseMe = () => {
    const recent = readRecentFrameIds(userId);
    const cookHistory = (cookLogs || []).length > 0
      ? {
          topCuisines: topCuisinesFromCookLogs(cookLogs),
          lovedCuisines: lovedCuisinesFromCookLogs(cookLogs),
        }
      : null;
    const frame = pickConceptFrame({
      recentFrameIds: recent,
      pantry,
      mode: priority === "pantry" ? "pantry" : "open",
      cookHistory,
      excludeHeroIds: recentHeroFamilyExclusions(userId),
    });
    setConceptFrame(frame);
    setPhase("frame_reveal");
  };

  // CLARIFY → DRAFT — planning step before the sketch. Asks Haiku for
  // 0-3 quick clarifying questions ("chunky or smooth?", "steak
  // doneness?") that would change the recipe materially, lets the
  // user answer them (or skip), then fires start() with the answers
  // riding in prefs.clarifications.
  //
  // Skipped entirely when:
  //   1. Haiku returns 0 questions (nothing genuinely ambiguous —
  //      "lasagna with bechamel" pins it all already).
  //   2. The clarify call fails (non-fatal — sketch still drafts).
  //   3. We're on regen (previousTitles non-empty) — re-use the
  //      already-answered clarifications from state, don't re-ask.
  //   4. Roll the dice (zero-input mode by design — see rollAndDraft).
  //
  // Costs ~$0.005 per call (Haiku, ~700ms). Worth it because it
  // eliminates "I have to tweak this 3 times" friction on the cook
  // flow and gives Claude explicit user-stated preferences to honor.
  const runClarifyThenStart = async (overrideFrame = null, opts = {}) => {
    // Allergy conflict gate — the user explicitly asked for something that
    // contains one of their own allergens (e.g. allergic to shellfish, typed
    // "shrimp scampi"). With the guard on, generating would hand Claude a
    // contradiction ("make shrimp scampi" + "never include shellfish"). Stop
    // and let the user decide: cooking for someone else (turn the guard off)
    // or pick something else. Skipped on regen and when already bypassed.
    if (!opts.bypassAllergyConflict && !allergyGuardOff && previousTitles.length === 0) {
      const conflicts = findAllergyConflicts(mealPrompt || "", userAllergies);
      if (conflicts.length > 0) {
        setAllergyConflicts(conflicts);
        pendingFrameRef.current = overrideFrame || null;
        setPhase("allergy_conflict");
        return;
      }
    }
    // Regen path: reuse existing clarifications/answers from state —
    // but ONLY when re-drafting the same prompt that already went
    // through clarify. previousTitles persists across drafts in a
    // session, so gating on it alone made the funnel fire once and
    // then never again for any later prompt.
    const promptKey = (mealPrompt || "").trim();
    if (previousTitles.length > 0 && clarifiedPromptRef.current === promptKey) {
      start(overrideFrame);
      return;
    }
    // Mark as fired so the photo-flow auto-start effect below doesn't
    // re-trigger when mealPrompt changes from within the flow.
    autoStartFiredRef.current = true;
    // Fresh draft → clear any clarifications/funnel from a previous attempt
    // so the sketch payload only carries the answers from THIS run.
    setClarifications([]);
    setClarificationAnswers({});
    setFunnel(null);
    // Re-hydrate the seen-set for THIS prompt instead of zeroing it —
    // a remount (tab nav, resume) used to reset reroll depth, so
    // "fresh ideas" served the same four moods the user already
    // rejected. sessionStorage-scoped: dies with the tab, which is
    // the right lifetime for "what this funnel session showed me."
    funnelSeenRef.current = readFunnelSeen(userId, mealPrompt);
    if (!opts.bypassAllergyConflict) setAllergyAltInfo(null);

    // ── Community dedupe — pre-gen intercept (migration 0236) ──────
    // Goal: avoid spending Haiku clarify + Sonnet sketch + final on a
    // dish the community has already nailed. Only fires once per
    // distinct prompt; "generate fresh anyway" sets communityDedupedFor
    // to this prompt so re-entry bypasses the check.
    const dedupeTarget = (mealPrompt || "").trim();
    if (dedupeTarget && communityDedupedFor !== dedupeTarget) {
      try {
        const contract = await ensureContract({ forceFresh: false });
        const aliases = Array.isArray(contract?.aliases) ? contract.aliases : [];
        const { matches } = matchCommunity(mealPrompt, aliases);
        if (matches.length > 0) {
          setCommunityMatches(matches);
          setPhase("community_match");
          return;
        }
      } catch (e) {
        // Non-fatal — dedupe failure should never block generation.
        // eslint-disable-next-line no-console
        console.warn("[AIRecipe] community dedupe failed (non-fatal):", e?.message || e);
      }
    }

    setPhase("clarify_loading");
    setErrMsg("");
    try {
      const { clarifications: qs, funnel: fn } = await requestClarifications({
        mealPrompt,
        conceptFrame: overrideFrame || conceptFrame,
        prefs: { course: course === "any" ? null : course },
        context: { profile: {
          dietary: profile?.dietary || null,
          allergenExclusions: allergyGuardOff ? [] : allergenExclusionStrings(userAllergies),
        } },
      });
      clarifiedPromptRef.current = promptKey;
      // Open-axis appetite funnel wins — walk the user mood → lane instead
      // of asking flat questions. The override frame is stashed so the
      // lane pick can carry it into start().
      if (fn && Array.isArray(fn.moods) && fn.moods.length > 0) {
        setClarifications([]);
        setClarificationAnswers({});
        rememberFunnelSeen(fn);
        setFunnel({ ...fn, _overrideFrame: overrideFrame || null });
        setPhase("funnel");
        return;
      }
      if (!Array.isArray(qs) || qs.length === 0) {
        // No questions — straight to sketch.
        start(overrideFrame);
        return;
      }
      // Seed with empty multi-select + empty custom — equivalent to
      // chef-choice. SKIP ALL fires from this default state with no
      // extra writes. Each chip click toggles option ids in/out.
      const seeded = {};
      qs.forEach((q) => { seeded[q.id] = { ids: [], custom: "" }; });
      setClarifications(qs);
      setClarificationAnswers(seeded);
      setPhase("clarify");
    } catch (e) {
      // Non-fatal. Sketch draft still proceeds without the planning step.
      // eslint-disable-next-line no-console
      console.warn("[AIRecipe] clarify failed, proceeding to sketch:", e?.message || e);
      start(overrideFrame);
    }
  };

  // CLARIFY phase confirm — fires when user taps DRAFT IT on the
  // clarify screen. Reads current clarificationAnswers from state and
  // calls start(); start() pulls them into the sketch prefs payload.
  const confirmClarifyAndDraft = () => {
    start(conceptFrame);
  };

  // ALLERGY CONFLICT — user explicitly requested a dish containing one of
  // their allergens. "Generate anyway" turns the guard off (they're cooking
  // for someone else / accept the risk) and resumes the draft; "keep me
  // safe" returns to setup so they can ask for something else.
  const allergyConflictGenerate = () => {
    setAllergyConflicts([]);
    setAllergyGuardOff(true);
    setResumeDraftAfterGuardOff(true);
  };
  // PRIMARY path — keep the guard ON and make an allergy-safe version: the
  // generator substitutes the allergen (the prompt block now says swap +
  // name, not omit). We stash allergyAltInfo so the preview can say "this
  // is an alternative — want the original?" The substitute directive rides
  // into the draft via the same clarifications plumbing the funnel uses.
  const allergyConflictSubstitute = () => {
    const conflicts = allergyConflicts;
    const subsHint = suggestSubstitutes(conflicts);
    const frame = pendingFrameRef.current;
    pendingFrameRef.current = null;
    setAllergyAltInfo({ allergens: conflicts, subsHint });
    setAllergyConflicts([]);
    const id = "allergy-substitute";
    const question = "Allergy-safe version";
    const answer = `Make an allergy-safe version of this dish — the cook is allergic to ${conflicts.join(", ")}, which it classically uses. SUBSTITUTE the allergen with a suitable allergen-free alternative${subsHint ? ` (${subsHint})` : ""}, keep the dish's spirit, and clearly NAME the swap.`;
    setClarifications([{ id, question, options: [], chefChoiceLabel: "Chef's choice" }]);
    setClarificationAnswers({ [id]: { ids: [], custom: answer } });
    // Guard STAYS ON (don't set allergyGuardOff) so the allergen block
    // applies; go straight to the draft with the directive inline.
    start(frame, { appetiteClarifications: [{ id, question, answer }] });
  };
  const allergyConflictCancel = () => {
    setAllergyConflicts([]);
    pendingFrameRef.current = null;
    setPhase("setup");
  };
  // From the preview's "want the original?" notice — turn the guard off and
  // regenerate the SAME dish with the allergen included.
  const generateOriginalDespiteAllergy = () => {
    setAllergyAltInfo(null);
    setClarifications([]);
    setClarificationAnswers({});
    setAllergyGuardOff(true);
    setResumeDraftAfterGuardOff(true);
  };
  useEffect(() => {
    if (!resumeDraftAfterGuardOff || !allergyGuardOff) return;
    setResumeDraftAfterGuardOff(false);
    const frame = pendingFrameRef.current;
    pendingFrameRef.current = null;
    runClarifyThenStart(frame, { bypassAllergyConflict: true });
    // runClarifyThenStart is a stable in-render closure; re-firing it here
    // (after the guard commits) is the whole point — don't add it as a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resumeDraftAfterGuardOff, allergyGuardOff]);

  // APPETITE FUNNEL handlers. The chosen lane (or mood-level surprise)
  // becomes a single synthesized clarification answer that anchors the
  // draft via the SAME prefs.clarifications plumbing flat questions use.
  // We set it into state (so the later final pass reads it) AND pass it
  // inline to start() (so the immediate sketch doesn't race the setState).
  const draftFromFunnel = (clarif, answer, overrideFrame) => {
    if (clarif) {
      setClarifications([clarif]);
      setClarificationAnswers({ [clarif.id]: answer });
    } else {
      setClarifications([]);
      setClarificationAnswers({});
    }
    setFunnel(null);
    const inline = clarif
      ? [{ id: clarif.id, question: clarif.question, answer: answer._draftAnswer }]
      : undefined;
    start(overrideFrame || null, inline ? { appetiteClarifications: inline } : {});
  };

  const pickFunnelLane = (lane, mood) => {
    if (!lane) return;
    const axis = (funnel?.axis || "direction").toString();
    const overrideFrame = funnel?._overrideFrame || null;
    const id = `appetite-${axis}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const question = `${axis.charAt(0).toUpperCase()}${axis.slice(1)} direction`;
    // The mood is the craving REGISTER the user walked through to
    // reach this lane ("comforting & rich" → Cacio e Pepe) — it's
    // load-bearing taste direction, not navigation chrome. Ride it
    // into the answer so the sketch aims at the feeling, not just
    // the dish name.
    const laneText = lane.blurb ? `${lane.label} (${lane.blurb})` : lane.label;
    const draftAnswer = mood?.label ? `${laneText} — craving: ${mood.label}` : laneText;
    draftFromFunnel(
      { id, question, options: [{ id: lane.id, label: lane.label }], chefChoiceLabel: "Chef's choice" },
      { ids: [lane.id], custom: lane.blurb || "", _draftAnswer: draftAnswer },
      overrideFrame,
    );
  };

  // Accumulate every shown mood/lane label so rerolls never repeat an
  // idea from THIS prompt's funnel session (either view). Written
  // through to sessionStorage so a remount doesn't reset reroll depth.
  const rememberFunnelSeen = (fn) => {
    const seen = funnelSeenRef.current;
    for (const m of fn?.moods || []) {
      if (m?.label && !seen.moods.includes(m.label)) seen.moods.push(m.label);
      for (const l of m?.lanes || []) {
        if (l?.label && !seen.lanes.includes(l.label)) seen.lanes.push(l.label);
      }
    }
    writeFunnelSeen(userId, mealPrompt, seen);
  };

  // FRESH IDEAS reroll — both views. General view (keepMoods=null):
  // 4 brand-new moods, nothing previously shown may repeat. Drilled
  // view (keepMoods=current labels): same 4 moods, all-new lanes.
  // The exclusion list is every label seen this session, so repeated
  // rerolls keep pushing outward into new cuisines and techniques.
  const rerollFunnel = async (keepMoods = null) => {
    if (funnelRerolling || !funnel) return;
    setFunnelRerolling(true);
    try {
      const seen = funnelSeenRef.current;
      const { funnel: fn } = await requestClarifications({
        mealPrompt,
        conceptFrame: funnel?._overrideFrame || conceptFrame,
        prefs: { course: course === "any" ? null : course },
        context: { profile: {
          dietary: profile?.dietary || null,
          allergenExclusions: allergyGuardOff ? [] : allergenExclusionStrings(userAllergies),
        } },
        funnelExclude: {
          keepMoods: Array.isArray(keepMoods) && keepMoods.length > 0 ? keepMoods : null,
          moods: keepMoods ? [] : seen.moods.slice(),
          lanes: seen.lanes.slice(),
        },
      });
      if (fn && Array.isArray(fn.moods) && fn.moods.length > 0) {
        rememberFunnelSeen(fn);
        setFunnel({ ...fn, _overrideFrame: funnel?._overrideFrame || null });
      }
    } catch (e) {
      // Non-fatal — the current funnel stays on screen.
      // eslint-disable-next-line no-console
      console.warn("[AIRecipe] funnel reroll failed:", e?.message || e);
    } finally {
      setFunnelRerolling(false);
    }
  };

  // KEEP IT BASIC — the skip the funnel promised. Not a surprise, not
  // a mood: an explicit instruction to cook the prompt straight.
  const basicPickFunnel = () => {
    const overrideFrame = funnel?._overrideFrame || null;
    const id = "appetite-basic";
    const question = "Direction";
    const answer = "Keep it basic and classic — a straightforward, traditional preparation of exactly what was asked. No creative twists, no added flavor systems.";
    draftFromFunnel(
      { id, question, options: [], chefChoiceLabel: "Chef's choice" },
      { ids: [], custom: answer, _draftAnswer: answer },
      overrideFrame,
    );
  };

  const chefPickFunnel = (mood) => {
    const axis = (funnel?.axis || "direction").toString();
    const overrideFrame = funnel?._overrideFrame || null;
    if (mood) {
      // Soft anchor — the mood, let the chef pick the specific sauce.
      const id = `appetite-${axis}`.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const question = `${axis.charAt(0).toUpperCase()}${axis.slice(1)} direction`;
      const draftAnswer = `${mood.label} ${axis} (chef's pick)`;
      draftFromFunnel(
        { id, question, options: [], chefChoiceLabel: "Chef's choice" },
        { ids: [], custom: draftAnswer, _draftAnswer: draftAnswer },
        overrideFrame,
      );
    } else {
      // Wide-open surprise — no anchor at all.
      draftFromFunnel(null, null, overrideFrame);
    }
  };

  // ROLL THE DICE — one-tap path. Picks a frame and immediately fires
  // the sketch, skipping the reveal phase entirely. Frame is visible
  // post-draft via the FRAMED BY chip on the sketch screen, so the
  // user still sees what the dice rolled — just after the fact, not
  // before. The frame gets passed inline as overrideFrame to dodge
  // React's async setState batching (setConceptFrame won't have
  // landed by the time start() reads conceptFrame from state).
  //
  // Note: Roll the dice INTENTIONALLY bypasses clarify. The whole
  // appeal of this entry point is "zero input, surprise me with a
  // draft" — adding a 3-question gate would defeat that.
  const rollAndDraft = () => {
    const recent = readRecentFrameIds(userId);
    const cookHistory = (cookLogs || []).length > 0
      ? {
          topCuisines: topCuisinesFromCookLogs(cookLogs),
          lovedCuisines: lovedCuisinesFromCookLogs(cookLogs),
        }
      : null;
    const frame = pickConceptFrame({
      recentFrameIds: recent,
      pantry,
      mode: priority === "pantry" ? "pantry" : "open",
      cookHistory,
      excludeHeroIds: recentHeroFamilyExclusions(userId),
    });
    setConceptFrame(frame);
    // Clear any stale clarifications — Roll the dice intentionally
    // skips the clarify step, but stale answers from a previous run
    // would still ride through buildPrefs into the sketch payload.
    setClarifications([]);
    setClarificationAnswers({});
    start(frame);
  };

  // Reroll from the frame_reveal screen — re-sample without burning
  // an AI call. We push the previous frame's id into recent before
  // sampling so the same frame doesn't immediately resurface.
  const rerollFrame = () => {
    if (conceptFrame?.id && userId) pushRecentFrameId(userId, conceptFrame.id);
    const recent = readRecentFrameIds(userId);
    const frame = pickConceptFrame({
      recentFrameIds: recent,
      pantry,
      mode: priority === "pantry" ? "pantry" : "open",
      excludeHeroIds: recentHeroFamilyExclusions(userId),
    });
    setConceptFrame(frame);
  };

  // REROLL & REDRAFT — fired from the FrameDetailSheet on the sketch
  // or preview screen. Re-seeds the frame (random new draw) AND
  // re-fires the sketch flow so the user sees a draft from the new
  // corner of recipe space. Costs an AI call — explicit user action.
  const rerollAndRedraft = () => {
    if (conceptFrame?.id && userId) pushRecentFrameId(userId, conceptFrame.id);
    // Clear the frame so start()'s sub-frame seeder picks a fresh one.
    setConceptFrame(null);
    setFrameDetailOpen(false);
    // Fire on next tick so the cleared state lands before start() reads it.
    setTimeout(() => { start(); }, 0);
  };

  // CHANGE TYPE — user pinned a recipe type from the type-picker
  // sheet. Reroll the rest with that type fixed.
  const changeFrameType = (typeId) => {
    if (conceptFrame?.id && userId) pushRecentFrameId(userId, conceptFrame.id);
    const recent = readRecentFrameIds(userId);
    const frame = pickConceptFrameWithType({
      typeId,
      recentFrameIds: recent,
      pantry,
      mode: priority === "pantry" ? "pantry" : "open",
      excludeHeroIds: recentHeroFamilyExclusions(userId),
    });
    setConceptFrame(frame);
    setChangeTypeOpen(false);
  };

  // Phase 2 entry — kicks off the cheap sketch pass. User lands on
  // the tweak screen with the rough draft + dual IDEAL/PANTRY lists,
  // can swap / remove / add / promote-to-shopping / type feedback,
  // then taps COOK THIS to fire the final full-recipe call.
  //
  // Photo-flow bypass — auto-fire the draft when the parent opens
  // AIRecipe via the photo flow. The user already reviewed ingredients
  // + answered follow-up questions on PhotoReviewStage; making them
  // tap "Draft recipe" on the setup screen is a redundant gate.
  //
  // Two modes both auto-fire:
  //   - finished_dish: initialMealPrompt supplied → wait for mealPrompt
  //                    state to receive it (one tick of lag), then fire
  //   - raw_ingredients: no prompt — fire immediately with empty
  //                      mealPrompt, which is AIRecipe's "dream mode
  //                      with pantry constraint" (Claude picks the
  //                      dish from the available ingredients)
  useEffect(() => {
    if (autoStartFiredRef.current) return;
    if (!photoSeedItems || photoSeedItems.length === 0) return;
    // When a prompt was supplied, wait for it to land in state before
    // firing. Prevents a race where the auto-fire runs with empty
    // mealPrompt and the prompt populates a tick later (lost).
    if (initialMealPrompt && initialMealPrompt.trim() &&
        (!mealPrompt || !mealPrompt.trim())) {
      return;
    }
    autoStartFiredRef.current = true;
    runClarifyThenStart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoSeedItems, mealPrompt, initialMealPrompt]);

  // `overrideFrame` lets one-tap entry points (Roll the dice) pass a
  // freshly-picked frame inline without round-tripping through state.
  // Without this, setConceptFrame() + start() in the same handler
  // races React's async setState batch — start() reads stale state
  // (conceptFrame === null) and re-seeds a different frame than the
  // one Roll the dice just rolled.
  const start = async (overrideFrame = null, opts = {}) => {
    setPhase("sketch_loading");
    setErrMsg("");
    setErrKind("other");
    setErrSource(null);
    try {
      // applyNotes = "re-sketch THIS dish with my tweak note" (vs a
      // fresh reroll to a different dish). Keeps the current dish: not
      // treated as a pivot-regen, current title dropped from
      // avoidTitles, recipeFeedback + sketchTitle ride in prefs so the
      // edge fn's REFINEMENT block keeps the dish and applies the note.
      const applyNotes = !!opts.applyNotes && recipeFeedback.trim().length > 0;
      const isRegen = !applyNotes && previousTitles.length > 0;
      // On regen, ALWAYS reclassify the dish prompt — never reuse the
      // cached contract. The cached contract pins Claude to the same
      // dish family ("Italian pasta" → keeps drafting Aglio e Olio),
      // and avoidTitles can't override a hard contract anchor. A fresh
      // classify lets the prompt's avoidTitles list actually steer
      // Claude to a different dish in the same family. SPECIFIC-tier
      // contracts (user typed "lasagna") still resolve to the same
      // dish — that's correct, the user wants that dish — but
      // FAMILY/FREEFORM tiers get a chance to pivot.
      const contract = await ensureContract({ forceFresh: isRegen });
      // For regen, also drop FAMILY/FREEFORM tier contracts entirely so
      // avoidTitles is the only steering signal. Keep SPECIFIC contracts
      // (user explicitly named the dish — they want that dish).
      const rawContract =
        isRegen && contract?.tier !== "SPECIFIC" ? null : contract;
      // PANTRY-ONLY priority is an explicit "use what's in my kitchen"
      // override — it ALWAYS beats an OPEN-tier contract from an empty
      // meal prompt. Without this drop, the edge fn fired its DREAM
      // MODE banner ("IGNORE the pantry entirely") AND the client
      // sent an empty pantry array, while priority=pantry's PRECEDENCE
      // block kept saying "every ingredientId MUST appear in the
      // pantry above." Claude resolved the conflict by drafting the
      // simplest staples-only pasta it knows — Aglio e Olio, every
      // single regen.
      const effectiveContract =
        priority === "pantry" && rawContract?.tier === "OPEN" ? null : rawContract;
      // Sub-frame seeding for typed prompts. If the user came in via
      // SURPRISE ME, conceptFrame is already set (and the user has seen
      // it on the reveal screen). If they came in via Roll the dice,
      // overrideFrame carries the freshly-rolled frame inline. Otherwise
      // seed silently here so vague typed asks ("pasta" / "lasagna")
      // get pushed off Claude's modal answer.
      //
      // SKIP frame seeding for CLASSIC + named-specific dishes
      // ("Boerenkool stamppot", "Carbonara", "Beef Wellington"). The
      // user explicitly asked for THE CLASSIC version — applying a
      // technique/hero anchor produces "fermented twist" / "smoky
      // hero" decorations on dishes that have a canonical traditional
      // identity. Vague-specific ("pasta", "salad") still gets a
      // frame because the modal answer there is too generic to be
      // useful. Pantry mode and dream mode are unaffected.
      let activeFrame = overrideFrame || conceptFrame;
      if (!activeFrame) {
        // Classic mode = "follow the recipe, don't reinvent." Any
        // form of classic priority skips frame seeding entirely —
        // no technique anchors, no hero injection, no cuisine
        // re-skinning. That belongs to chef-choice / dream / pantry
        // modes where creative direction is the whole point.
        // Catches both named-specific (Boerenkool stamppot) AND
        // vague-specific (pasta, lasagna) classic asks — both want
        // the canonical dish, and frame-seeded heroes (sumac on
        // Dutch comfort food) corrupt that.
        const skipFrame = priority === "classic";
        if (!skipFrame) {
          const recent = readRecentFrameIds(userId);
          const fixedDishName =
            effectiveContract?.tier === "SPECIFIC" ? effectiveContract.dishName : null;
          const subFrameMode =
            effectiveContract?.tier === "SPECIFIC" ? "specific"
            : priority === "pantry"                 ? "pantry"
            : "open";
          activeFrame = pickConceptFrame({
            recentFrameIds: recent,
            pantry,
            mode: subFrameMode,
            fixedDishName,
            excludeHeroIds: recentHeroFamilyExclusions(userId),
          });
          setConceptFrame(activeFrame);
        }
      }
      // Hero-level rotation memory — record at DRAFT time (an AI call
      // is firing on this frame), so the next surprise-me/roll draw
      // excludes this hero AND its family (miso_white blocks miso_red).
      // Frame-id rotation alone let the same hero repeat 3+ drafts in
      // a row whenever any other axis differed.
      if (activeFrame?.hero?.id && userId) pushRecentHeroId(userId, activeFrame.hero.id);
      // Cross-session repeat guard — titles this user's generator
      // drafted in PAST sessions (ledger, migration 0284). SPECIFIC
      // asks skip it: a user typing "carbonara" twice in one lifetime
      // wants carbonara both times. ~1KB read, swallowed on failure.
      const ledgerTitles = effectiveContract?.tier === "SPECIFIC"
        ? []
        : await fetchRecentGenerationTitles(userId);
      // Library exclusions — bundled + user-saved titles. Forbidden in
      // the prompt when a frame is attached (which is now always),
      // with a carve-out for typed-by-name SPECIFIC asks (handled
      // server-side via the EXCEPTION line in the prompt).
      const libraryTitles = [
        ...(bundledRecipes || []).map(r => r?.title).filter(Boolean),
        ...(userRecipes || [])
          .filter(ur => ur?.userId === userId)
          .map(ur => ur?.recipe?.title)
          .filter(Boolean),
      ];
      const isDreamMode = effectiveContract?.tier === "OPEN";
      const built = buildAIContext({
        pantry,
        // Allergy guard OFF for this draft → strip allergies so no exclusion
        // block is generated; otherwise the real list drives the hard block.
        profile: allergyGuardOff ? { ...profile, allergies: [] } : profile,
        ingredientInfo, cookLogs,
        mode: isRegen ? "lean" : "rich",
        starIngredientIds,
        // filterPantryByCourse is a no-op when course is "any" (no
        // compatibility set) or priority is "chefs_choice" / "pantry"
        // (chef wants free hand / strict pantry-only) — pass both
        // through unconditionally; the filter does the gating.
        course:   course === "any" ? undefined : course,
        priority,
      });
      // Carry forward every ingredient the user has explicitly removed
      // across the session — both prior drafts (bannedAcrossRegens) and
      // anything dropped on the sketch we're rerolling away from. Without
      // this, a reroll re-proposes the dropped ingredient (the "removed
      // ricotta, it came back" bug), because pantryEdits.removes is wiped
      // the moment the new sketch lands.
      const carriedBans = mergeBannedIdentities(
        bannedAcrossRegens,
        removedSketchIdentities(sketch, pantryEdits.removes),
      );
      // Persist so the NEXT regen still knows everything dropped so far,
      // even though the per-sketch pantryEdits reset is about to run.
      if (carriedBans.length !== bannedAcrossRegens.length) {
        setBannedAcrossRegens(carriedBans);
      }
      const payload = {
        mode: "sketch",
        // OPEN/dream mode: drop the pantry entirely — Claude dreams
        // without any "use what's here" pressure. User's dietary
        // constraints + cook history still ride through `context`.
        // Otherwise send the pantry MINUS anything the user banned — a
        // banned row sitting in the "what you have" block is a standing
        // invitation for Claude to re-propose it.
        pantry: isDreamMode
          ? []
          : built.pantry.filter((p) => !rowIsBanned(p, carriedBans)),
        prefs: buildPrefs({
          pantryFiltered: isDreamMode ? false : built.pantryFiltered,
          dishContract: withFireTimeIntent(effectiveContract, activeFrame) || undefined,
          // Re-sketch-with-notes: carry the tweak + pin the dish so the
          // edge fn refines instead of rerolling.
          ...(applyNotes ? {
            recipeFeedback: recipeFeedback.trim(),
            sketchTitle: sketch?.title || undefined,
          } : {}),
          // Appetite-funnel pick passed inline so the lane anchors THIS
          // sketch without waiting on the setClarifications state commit
          // (which still happens, for the later final pass to read).
          ...(Array.isArray(opts.appetiteClarifications)
            ? { clarifications: opts.appetiteClarifications }
            : {}),
        }),
        // On a notes re-sketch, DON'T avoid the current dish (we want
        // to keep it). Every other reroll avoids prior titles to pivot.
        // Cross-session repeats ride in from the generation ledger —
        // previousTitles alone dies with the tab, so "surprise me"
        // tomorrow used to re-draft tonight's dish.
        avoidTitles: applyNotes
          ? previousTitles.filter((t) => t !== (sketch?.title || ""))
          : [...new Set([...previousTitles, ...ledgerTitles])],
        context: built.context,
        bannedIngredients: carriedBans,
        conceptFrame: activeFrame,
        libraryTitles,
      };
      // Telemetry for the user-reported "thinks my pantry is empty"
      // bug. Surfaces in the browser console so the user can confirm
      // the pantry block actually contains rows on the wire.
      // eslint-disable-next-line no-console
      console.info("[AIRecipe] sketch payload",
        "pantryRows=", payload.pantry.length,
        "avoidTitles=", payload.avoidTitles.length,
        "contract=", effectiveContract?.tier || "(none)",
        "priority=", priority,
        "frame=", activeFrame?.label || "(none)",
        "libraryTitles=", libraryTitles.length,
      );
      // Per-row canonical telemetry — surfaces exactly what's hitting
      // generate-recipe so the user can confirm photo items survived
      // the pipeline. If pantry rows show canonicalId:null when they
      // should have ids, the drop is somewhere upstream of here.
      // eslint-disable-next-line no-console
      console.info("[AIRecipe] pantry canonicals →",
        payload.pantry.map((p) => ({
          canonicalId: p.canonicalId,
          name: p.name,
          state: p.state,
        })),
      );
      // eslint-disable-next-line no-console
      console.info("[AIRecipe] mealPrompt →", mealPrompt);
      // Once we've drafted with this frame, stamp it into recent so
      // the next SURPRISE ME / sub-frame draw doesn't immediately
      // resurface it.
      if (activeFrame?.id && userId) pushRecentFrameId(userId, activeFrame.id);
      const result = await withRetryOnNetwork(() => generateRecipe(payload));
      // Graceful fallback — if the edge function hasn't been
      // redeployed with Phase 2, the client sees { recipe }
      // instead of { sketch } and we skip tweak entirely, landing
      // in preview with the single-pass result. Logs so the user
      // knows they need to deploy to unlock the tweak flow.
      if (result.fellBackToFinal) {
        // eslint-disable-next-line no-console
        console.warn("[AIRecipe] generate-recipe edge fn predates Phase 2 (sketch mode) — deploy `supabase functions deploy generate-recipe` to enable the tweak flow.");
        // Pipe every ingredientId through coerceRecipeCanonicalIds so
        // the recipe we persist + render is strictly bound to the
        // canonical registry. Claude occasionally drifts ("fresh
        // tortillas" vs "tortillas"); the coercer normalizes those
        // back and sets unknown ids to null.
        const coerced = coerceRecipeCanonicalIds(result.recipe);
        const { recipe: workingFb, diagnostics: fallbackMerged } =
          runRecipeTechniqueChecks(applyHonestTime(coerced), { findIngredient });
        const coercedWithDiagnostics = fallbackMerged
          ? { ...workingFb, bakingDiagnostics: fallbackMerged }
          : workingFb;
        scanFinalForAllergens(coercedWithDiagnostics);
        setRecipe(coercedWithDiagnostics);
        recordGeneration({
          userId,
          recipe: coercedWithDiagnostics,
          frame: activeFrame,
          getInfo: ingredientInfo?.getInfo,
        });
        if (coerced?.title) {
          setPreviousTitles(prev => (prev.includes(coerced.title) ? prev : [...prev, coerced.title]));
        }
        setPhase("preview");
        return;
      }
      // Same normalization on the sketch so pantry-coverage + swap
      // matching downstream work against real canonicals, not the
      // raw strings Claude handed back. Stamp dietaryClaims onto
      // each ideal slot so diet-loss detection has the signal when
      // a paired pantry row doesn't carry the same claim.
      let drafted = stampDietaryClaims(coerceRecipeCanonicalIds(result.sketch));
      // Pantry-only mode HARD CONSTRAINT enforcement. Claude's prompt
      // says "every ingredientId MUST appear in the pantry block"
      // but it occasionally hallucinates — adds spaghetti to a draft
      // when the user has no spaghetti. Client-side scrub is the
      // safety net: any sketch.pantry slot whose canonicalId isn't in
      // the actual pantry's canonical set gets MOVED to sketch.ideal
      // (i.e., demoted to "you'd need to buy this"). User can then
      // promote-to-shopping or swap from the tweak screen.
      if (priority === "pantry" && drafted) {
        const pantryCanonSet = new Set(
          (pantry || [])
            .flatMap(p => [p.canonicalId, p.ingredientId, ...(Array.isArray(p.ingredients) ? p.ingredients : [])])
            .filter(Boolean)
        );
        const inPantry = (slot) => slot?.ingredientId && pantryCanonSet.has(slot.ingredientId);
        const survivors = (drafted.pantry || []).filter(inPantry);
        const evicted = (drafted.pantry || []).filter(s => !inPantry(s));
        if (evicted.length > 0) {
          // eslint-disable-next-line no-console
          console.warn("[AIRecipe] pantry-only scrub — Claude hallucinated", evicted.length, "ingredient(s):",
            evicted.map(e => e.ingredientId || e.name).filter(Boolean).join(", ")
          );
          drafted = {
            ...drafted,
            pantry: survivors,
            ideal: [...(drafted.ideal || []), ...evicted.map(e => ({ ...e, pantryItemId: null }))],
          };
        }
      }
      setSketch(drafted);
      // Notes re-sketch: the tweak is now baked into this sketch, so
      // clear the note. Leaving it would re-apply a now-stale change on
      // the next Cook/Re-sketch ("cream cheese instead of mayo" when
      // there's already no mayo). User types a fresh tweak for the next
      // pass.
      if (applyNotes) setRecipeFeedback("");
      // Capture proposedCanonicals from the response so the tweak
      // phase can surface the Accept/Decline banner. Empty array
      // when the AI didn't propose anything new (the common case
      // once the registry covers what users typically cook).
      setProposedCanonicals(Array.isArray(result?.proposedCanonicals) ? result.proposedCanonicals : []);
      // Sketch landed — pins served their purpose, clear them. They
      // already live as starIngredientIds (merged in via the load
      // effect above) which the sketch was generated from, so the
      // localStorage pin set is redundant noise from here on. A
      // fresh empty pin set means the next AI session starts clean.
      if (userId) clearAiPinned(userId);
      // Reset the tweak diff every fresh sketch — old swaps from a
      // previous draft don't apply to the new ingredient list. The
      // initial swaps map is pre-populated by autoPairShoppingRows:
      // any sketch.pantry row Claude marked as shopping (pantryItemId
      // null) that we can cover from the user's real pantry gets
      // swap-linked up front, so the UI's "in-kitchen" tag and
      // coverage count reflect what the user actually owns. The
      // matched render branch shows "⚡ AUTO-PAIRED" for these so
      // the substitution is visible and reversible via SWAP.
      //
      // STRICT in classic + dream modes (canonical-id equality only,
      // no hub-family widening). LOOSE in pantry mode where the user
      // explicitly asked for "use what I have" and hub-sibling matches
      // (yellow_onion ↔ green_onion, chicken ↔ chicken_breast) are
      // the whole point.
      //
      // Without strict mode the recipe's "Yellow Onion" was pairing
      // with the pantry's "Green Onion" via vegetable_hub family —
      // recipe-corrupting in any non-eat-through context. Strict
      // mode pairs ONLY canonical-equality matches: butter ↔ butter
      // ✓, yellow_onion ↔ green_onion ✗ (different canonicals,
      // shopping label).
      const autoSwaps = autoPairShoppingRows(drafted, pantry, {
        strict: priority !== "pantry",
      });
      setPantryEdits({ swaps: autoSwaps, removes: new Set(), adds: [], shopping: new Set() });
      setRecipeFeedback("");
      setSwapOpenIdx(null);
      setSwapSearch("");
      setAddOpen(false);
      if (drafted?.title) {
        setPreviousTitles(prev => (prev.includes(drafted.title) ? prev : [...prev, drafted.title]));
      }
      setPhase("tweak");
    } catch (e) {
      console.error("AI recipe sketch failed:", e);
      const net = isNetworkError(e);
      setErrKind(net ? "network" : "other");
      setErrSource("sketch");
      setErrMsg(net
        ? "Couldn't reach the server. Check your connection and tap TRY AGAIN."
        : (e?.message || "Sketch failed"));
      setPhase("error");
    }
  };

  // Build the locked-ingredients payload from the sketch + the
  // user's tweak diff. Order: kept pantry rows (with swaps applied)
  // → user adds → ideal-promoted-to-shopping. Removes are dropped.
  // Brand NEVER enters generation (it belongs to the pairing layer,
  // which re-attaches the user's brand at render time). Pantry rows
  // carry names like "Truff Hotter Sauce" / "SIZZLE EVOO"; the final
  // pass is caged to use locked names VERBATIM, so a branded locked
  // name lands verbatim in the shipped ingredient list. Resolve to
  // the canonical display name when linked; otherwise strip the brand
  // prefix off the raw text.
  const genericName = (name, ingredientId, brand) => {
    if (ingredientId) {
      const ing = findIngredient(ingredientId);
      if (ing?.name) return ing.name;
    }
    let n = String(name || "").trim();
    const b = String(brand || "").trim();
    if (b && n.toLowerCase().startsWith(b.toLowerCase())) {
      n = n.slice(b.length).trim().replace(/^[-–—:,]+\s*/, "");
    }
    return n || String(name || "");
  };

  const buildLockedIngredients = () => {
    if (!sketch) return [];
    const out = [];
    sketch.pantry.forEach((row, i) => {
      if (pantryEdits.removes.has(i)) return;
      const swappedId = pantryEdits.swaps[i];
      const swappedRow = swappedId ? pantry.find(p => p.id === swappedId) : null;
      if (swappedRow) {
        const swapCanon = swappedRow.ingredientId || swappedRow.canonicalId || null;
        out.push({
          name: genericName(swappedRow.name, swapCanon, swappedRow.brand),
          amount: row.amount,
          ingredientId: swapCanon,
          pantryItemId: swappedRow.id,
          source: "pantry",
          note: row.subbedFrom ? `subbed from ${row.subbedFrom} (was ${row.name})` : `swapped in for ${row.name}`,
        });
      } else {
        out.push({
          name: genericName(row.name, row.ingredientId ?? null, row.brand),
          amount: row.amount,
          ingredientId: row.ingredientId ?? null,
          pantryItemId: row.pantryItemId ?? null,
          source: row.pantryItemId ? "pantry" : "shopping",
          note: row.subbedFrom ? `subbed from ${row.subbedFrom}` : null,
        });
      }
    });
    pantryEdits.adds.forEach(add => {
      out.push({
        name: genericName(add.name, add.ingredientId || null, add.brand),
        amount: add.amount,
        ingredientId: add.ingredientId || null,
        pantryItemId: add.pantryItemId || null,
        source: "added",
        note: null,
      });
    });
    pantryEdits.shopping.forEach(idx => {
      const ideal = sketch.ideal[idx];
      if (!ideal) return;
      out.push({
        name: ideal.name,
        amount: ideal.amount,
        ingredientId: null,
        pantryItemId: null,
        source: "shopping",
        note: "user will pick up",
      });
    });
    // Grounding stamp (B4): the final prompt's GROUNDED NUMBERS block
    // renders these so step prose carries measured times/temps
    // (caramelize = 35-40 min; chicken = 165°F) instead of stylistic
    // ones. technique = curated per-verb timing map flattened to one
    // line; internalTempF = safe pull target from the doneness table.
    return out.map((row) => {
      const id = row.ingredientId;
      if (!id) return row;
      let technique = null;
      try {
        // Merged read (bundled + DB) — the curated TECHNIQUES maps
        // live in bundled INGREDIENT_INFO; ingredientInfo.getInfo is
        // the DB layer only and would miss every top-50 staple.
        const map = getIngredientInfo(findIngredient(id), ingredientInfo?.getInfo?.(id))?.technique;
        if (map && typeof map === "object") {
          technique = Object.entries(map)
            .slice(0, 4)
            .map(([verb, hint]) => `${verb}: ${String(hint)}`)
            .join(" | ")
            .slice(0, 420) || null;
        }
      } catch { /* enrichment is optional signal */ }
      // Two temps, two claims: internalTempF is the PULL TARGET
      // (brisket 205°F = texture goal), minSafeTempF is the floor the
      // prompt's "never write lower" safety clause hangs off.
      const internalTempF = targetInternalTempF(id);
      const minSafeTempF = minSafeInternalTempF(id);
      return {
        ...row,
        ...(technique ? { technique } : {}),
        ...(internalTempF != null ? { internalTempF } : {}),
        ...(minSafeTempF != null ? { minSafeTempF } : {}),
      };
    });
  };

  // Negative companion to buildLockedIngredients: ingredients the
  // user EXPLICITLY removed from the sketch are banned from the
  // final draft. Without this, the locked list is purely positive
  // (use these) — Claude has no signal that miso paste was tossed
  // mid-sketch and freely re-introduces it on the final pass via
  // training-data bias. Removing an ingredient is a load-bearing
  // gesture; treat it as a hard constraint.
  const buildBannedIngredients = () => {
    if (!sketch) return [];
    const out = [];
    pantryEdits.removes.forEach(i => {
      const row = sketch.pantry?.[i];
      if (!row) return;
      out.push({
        name: row.name || null,
        ingredientId: row.ingredientId || null,
      });
    });
    // Also ban any ideal-list entries the user did NOT promote to
    // shopping AND did NOT cover via pantry — those were proposed
    // by the sketch and the user implicitly skipped them. The
    // EXPLICIT remove gesture above is the strongest signal; this
    // pass catches the soft "didn't add to shopping" case where the
    // user's not-clicking is also a no.
    const lockedIds = new Set();
    const lockedNames = new Set();
    for (let i = 0; i < (sketch.pantry || []).length; i++) {
      if (pantryEdits.removes.has(i)) continue;
      const r = sketch.pantry[i];
      if (r?.ingredientId) lockedIds.add(r.ingredientId);
      if (r?.name) lockedNames.add(String(r.name).toLowerCase().trim());
    }
    pantryEdits.adds.forEach(add => {
      if (add?.ingredientId) lockedIds.add(add.ingredientId);
      if (add?.name) lockedNames.add(String(add.name).toLowerCase().trim());
    });
    pantryEdits.shopping.forEach(idx => {
      const ideal = sketch.ideal?.[idx];
      if (ideal?.ingredientId) lockedIds.add(ideal.ingredientId);
      if (ideal?.name) lockedNames.add(String(ideal.name).toLowerCase().trim());
    });
    for (const ideal of (sketch.ideal || [])) {
      if (!ideal) continue;
      const idMatch = ideal.ingredientId && lockedIds.has(ideal.ingredientId);
      const nameMatch = ideal.name && lockedNames.has(String(ideal.name).toLowerCase().trim());
      if (idMatch || nameMatch) continue;
      // Skip if already in the explicit-remove set above.
      const dupe = out.some(b =>
        (b.ingredientId && b.ingredientId === ideal.ingredientId) ||
        (b.name && ideal.name && b.name.toLowerCase() === ideal.name.toLowerCase())
      );
      if (dupe) continue;
      out.push({
        name: ideal.name || null,
        ingredientId: ideal.ingredientId || null,
      });
    }
    return out;
  };

  // Phase 4 entry — fires the FINAL full-recipe call with the
  // user's locked ingredient set + revision feedback. Lands in the
  // existing preview phase.
  const cookFinal = async () => {
    if (!sketch) return;
    setErrMsg("");
    setErrKind("other");
    setErrSource(null);
    try {
      const rawContract = await ensureContract();
      // Same OPEN-vs-pantry-only override as the sketch path — see the
      // comment in start(). Pantry-only mode wins; an OPEN contract
      // from an empty meal prompt would otherwise re-fire the DREAM
      // MODE banner and re-empty the pantry on the final call.
      const contract =
        priority === "pantry" && rawContract?.tier === "OPEN" ? null : rawContract;
      const isDreamMode = contract?.tier === "OPEN";
      const built = buildAIContext({
        pantry,
        // Allergy guard OFF for this draft → strip allergies so no exclusion
        // block is generated; otherwise the real list drives the hard block.
        profile: allergyGuardOff ? { ...profile, allergies: [] } : profile,
        ingredientInfo, cookLogs,
        mode: "rich",
        starIngredientIds,
        course:   course === "any" ? undefined : course,
        // Pass priority unconditionally — was previously nulled out
        // when course="any", which tripped the edge fn's silent
        // undefined→"category" default and flipped precedence midway
        // through a single session.
        priority,
      });
      const locked = buildLockedIngredients();
      // Union the current sketch's removes with everything banned across
      // prior regens — a removal made two rerolls ago must still bind on
      // the final pass even though its index is long gone.
      const banned = mergeBannedIdentities(buildBannedIngredients(), bannedAcrossRegens);
      // Library-avoidance + frame ride through the final pass too.
      // Without this, the final pass loses the frame anchor that
      // shaped the sketch — Claude can re-conceptualize away from
      // the frame's corner. With it, the final pass writes steps
      // for the SAME dish concept the frame produced on the sketch.
      const finalLibraryTitles = [
        ...(bundledRecipes || []).map(r => r?.title).filter(Boolean),
        ...(userRecipes || [])
          .filter(ur => ur?.userId === userId)
          .map(ur => ur?.recipe?.title)
          .filter(Boolean),
      ];
      const payload = {
        // Streaming variant always runs in final mode, no need to pass it
        pantry: isDreamMode ? [] : built.pantry,
        prefs: buildPrefs({
          recipeFeedback: recipeFeedback.trim() || undefined,
          pantryFiltered: isDreamMode ? false : built.pantryFiltered,
          // Anchor the final pass to the sketch's dish identity —
          // without this the final pass saw the locked ingredient
          // list but had no binding to what dish they belonged to,
          // and Claude would sometimes re-conceptualize the dish
          // (Bacon Egg Sandwich sketch → Quiche Lorraine final).
          sketchTitle:     sketch?.title || undefined,
          sketchSubtitle:  sketch?.subtitle || undefined,
          sketchRationale: sketch?.aiRationale || undefined,
          dishContract:    withFireTimeIntent(contract, conceptFrame || null) || undefined,
        }),
        avoidTitles: previousTitles,
        context: built.context,
        lockedIngredients: locked,
        bannedIngredients: banned,
        conceptFrame: conceptFrame || null,
        libraryTitles: finalLibraryTitles,
      };
      // userId + frame ride along for the background path's ledger
      // write (recordGeneration in backgroundRecipeGen) — both are
      // JSON-serializable so the localStorage retry stash keeps them.
      const finalizeInputs = { locked, priority, isDreamMode, userId, frame: conceptFrame || null };

      // ── Background hand-off (default) ──────────────────────────────
      // The final pass is the slow Sonnet call with no server-side
      // fallback. Pinning the user to a spinner that hangs forever on a
      // dropped connection is exactly the failure we're fixing. Mint a
      // pending Recipe-Book stub and run generation detached: App opens
      // the book on the stub, and it flips to the finished recipe (or a
      // "failed → Retry" state) when generation resolves. The same
      // finalize pipeline runs in both paths (see
      // src/lib/finalizeGeneratedRecipe.js + backgroundRecipeGen.js).
      if (typeof onBackgroundGenerate === "function") {
        await onBackgroundGenerate({
          payload,
          finalizeInputs,
          titleGuess:    sketch?.title || (mealPrompt || "").trim() || "New recipe",
          subtitleGuess: sketch?.subtitle || "",
          emojiGuess:    sketch?.emoji || "🍳",
        });
        if (userId) clearDraft(userId);
        onCancel?.();   // tear down AIRecipe; App has navigated to the book
        return;
      }

      // ── Inline fallback (no background handler wired) ──────────────
      // Stream Claude's recipe tokens as they arrive so the user sees
      // the recipe write itself instead of waiting on a 30-60s spinner.
      // NOTE: the finalize block below is mirrored by
      // finalizeGeneratedRecipe() — keep them in sync if you touch it.
      setPhase("final_loading");
      setStreamedText("");
      const { recipe: drafted } = await withRetryOnNetwork(() =>
        generateRecipeStreaming({
          ...payload,
          onChunk: (text) => {
            // Functional update so consecutive chunks accumulate even
            // if React batches multiple onChunk calls within a frame.
            setStreamedText((prev) => prev + text);
          },
        })
      );
      // Coerce canonical ids on the way in. Protects the stored
      // user_recipes row from AI drift ("fresh_tortillas" → "tortillas")
      // and guarantees the pantry-match loop downstream is operating
      // on real registry slugs.
      const normalized = coerceRecipeCanonicalIds(drafted);
      // Stamp draft-mode signals so the preview UI knows whether to
      // run pantry pairing. Pairing should ONLY happen when priority
      // is "pantry" (eat-through mode where the user explicitly
      // asked for "use what I have"). Classic mode and dream mode
      // both want the authentic recipe rendered as authored — no
      // swap suggestions, no "auto-paired from your fridge" chips,
      // no shopping-add buttons on the preview. Pairing kicks back
      // in at cook prep where it's contextually wanted.
      //
      // Boerenkool stamppot should render with potatoes + kale +
      // rookworst even if the pantry has carrots and prosciutto.
      normalized._isDreamMode  = !!isDreamMode;
      normalized._priority     = priority || "classic";
      normalized._pairingActive = priority === "pantry";
      // Re-stamp pantryItemId from the locked set. The AI response
      // only carries { item, amount, ingredientId, state? } — it
      // drops the pantryItemId that buildLockedIngredients wrote
      // for swapped/bound rows. Without this merge the cook-prep
      // pairing re-matches from scratch and lands on the wrong row
      // (in practice: swapping Mozzarella for Great Value string
      // cheese got re-paired with Powdered Sugar by the category
      // fallback). We key by ingredientId first (tightest signal)
      // then by normalized name so brand-prefixed reformulations
      // like "Great Value String Cheese" still find their locked
      // pantry binding.
      const pairedNorm = (s) => String(s || "").toLowerCase().trim();
      const byCanon = new Map();
      const byName  = new Map();
      for (const l of locked) {
        if (!l || !l.pantryItemId) continue;
        if (l.ingredientId && !byCanon.has(l.ingredientId)) byCanon.set(l.ingredientId, l.pantryItemId);
        const n = pairedNorm(l.name);
        if (n && !byName.has(n)) byName.set(n, l.pantryItemId);
      }
      const withPantry = normalized && Array.isArray(normalized.ingredients)
        ? {
            ...normalized,
            ingredients: normalized.ingredients.map(ing => {
              if (!ing || typeof ing !== "object") return ing;
              if (ing.pantryItemId) return ing;
              const canonHit = ing.ingredientId ? byCanon.get(ing.ingredientId) : null;
              const nameHit  = canonHit ? null : byName.get(pairedNorm(ing.item || ing.name));
              const pantryItemId = canonHit || nameHit || null;
              return pantryItemId ? { ...ing, pantryItemId } : ing;
            }),
          }
        : normalized;
      // Stamp dietaryClaims onto every persisted ingredient row.
      // Claims are recipe INTENT (the AI asked for "low-carb
      // tortilla"), not transient pairing — they must survive into
      // user_recipes so preview/CookMode can warn on diet-loss a
      // month from now even if the user has different pantry state.
      // Pairing itself is still re-derived live every render via
      // pairRecipeIngredients, so brands/availability drift doesn't
      // fossilize; only the claim labels on each ingredient persist.
      const claimed = withPantry && Array.isArray(withPantry.ingredients)
        ? {
            ...withPantry,
            ingredients: withPantry.ingredients.map(ing => {
              if (!ing || typeof ing !== "object") return ing;
              if (Array.isArray(ing.dietaryClaims) && ing.dietaryClaims.length > 0) {
                return ing;
              }
              const { claims } = extractDietaryClaims(ing.item || ing.name || "");
              if (claims.length === 0) return ing;
              return { ...ing, dietaryClaims: claims };
            }),
          }
        : withPantry;
      // Chained reheat upgrade. generate-recipe produces a reheat
      // block as ONE field in a ~40-field schema; Claude's attention
      // is spread thin and the steps are often flat. A second, focused
      // call through suggest-cook-instructions — same input budget,
      // purpose-built prompt — produces dramatically richer steps
      // with heat badges, doneCues, per-step timers and tips. We
      // merge the focused result onto recipe.reheat so the leftover
      // pantry row inherits the good walkthrough without a manual
      // SUGGEST tap downstream.
      //
      // Failure is non-fatal — if Claude rate-limits or the shape
      // fails, we fall back to whatever reheat generate-recipe
      // authored. The leftover then still gets a walkthrough via
      // reheatToCookInstructions' synthesis path.
      //
      // ONLY enrich a reheat block the model deliberately authored. The
      // generate-recipe prompt omits `reheat` for dishes that must be
      // eaten fresh (steak, surf & turf, fried eggs) — re-adding one
      // here told the leftover flow a fresh dish keeps, so a steak
      // dinner sprouted bogus "reheat your leftovers" instructions.
      let enriched = claimed;
      if (claimed?.title && claimed?.reheat) {
        try {
          const { cookInstructions } = await suggestCookInstructions({
            name:     claimed.title,
            category: claimed.category,
          });
          if (cookInstructions?.steps?.length > 0) {
            enriched = {
              ...claimed,
              reheat: {
                // Focused call always wins on primary since its
                // method/time/temp are derived from the same prompt
                // that wrote the walkthrough — guaranteed internally
                // consistent with the steps.
                primary: cookInstructions.reheat?.primary || claimed?.reheat?.primary || null,
                // Stash the full step array on reheat so the leftover
                // row's walkthrough reads them directly instead of
                // synthesizing from primary.
                steps: cookInstructions.steps,
                // Preserve any top-level note generate-recipe might
                // have authored ("eggs scramble if microwaved").
                ...(claimed?.reheat?.note ? { note: claimed.reheat.note } : {}),
              },
            };
          }
        } catch (_) { /* non-fatal, fall through with the first-pass reheat */ }
      }
      // Recipe-level technique checks. Orchestrator runs every
      // applicable sub-validator (baking, meat doneness, frying
      // heat, pasta water, parallel steps), applies their auto-
      // fixes silently, and returns the post-fix recipe + the
      // residual diagnostics the user actually needs to see.
      // See src/lib/recipeValidator.js for the registry.
      const { recipe: workingRecipe, diagnostics: mergedDiagnostics } =
        runRecipeTechniqueChecks(applyHonestTime(enriched), { findIngredient });
      const enrichedWithDiagnostics = mergedDiagnostics
        ? { ...workingRecipe, bakingDiagnostics: mergedDiagnostics }
        : workingRecipe;
      scanFinalForAllergens(enrichedWithDiagnostics);
      setRecipe(enrichedWithDiagnostics);
      // Generation ledger (A5) — remember what the generator drafted
      // so cross-session surprise-me runs stop re-treading the same
      // territory. Fire-and-forget; conceptFrame carries hero/technique.
      recordGeneration({
        userId,
        recipe: enrichedWithDiagnostics,
        frame: conceptFrame,
        getInfo: ingredientInfo?.getInfo,
      });
      if (enriched?.title) {
        setPreviousTitles(prev => (prev.includes(enriched.title) ? prev : [...prev, enriched.title]));
      }
      // No automatic shopping-list push. Users explicitly don't want
      // the final cook to silently dump items into their cart — that
      // was happening AFTER the recipe was drafted, surprising the
      // user. If/when they want shopping items added, the preview
      // screen's ingredient panel exposes per-row "+ SHOP" controls
      // (see IngredientsWithPairing → showShop) that hit onShoppingAdd
      // directly. Keeps consent explicit and per-item.
      setPhase("preview");
      // Preview landed, but the user hasn't saved yet — keep the
      // draft alive so "I closed the tab before hitting SAVE" still
      // recovers. The draft only clears when the user takes an
      // explicit save/schedule/cook action below.
    } catch (e) {
      console.error("AI recipe final failed:", e);
      const net = isNetworkError(e);
      setErrKind(net ? "network" : "other");
      setErrSource("final");
      setErrMsg(net
        ? "Couldn't reach the server. Check your connection and tap TRY AGAIN."
        : (e?.message || "Final cook failed"));
      setPhase("error");
    }
  };

  // Each action guards on busy so a double-tap can't fire twice.
  // On successful save/schedule/cook, the draft is cleared — that
  // commit is the point where user_recipes has a row and the local
  // sketch is no longer the source of truth. If the action throws,
  // the draft stays so the user can retry.
  const handleAction = (kind, cb) => async () => {
    if (!recipe || busy) return;
    if (kind === "save" || kind === "cook") playSfx("button-press");
    setBusy(kind);
    try {
      // Pass previewRecipe (which includes the user's servings
      // adjustment) so SAVE / SCHED / COOK IT all persist + cook
      // the scaled recipe, not Claude's original draft.
      //
      // Stamp cooking-intent tags onto the saved recipe so it lands in
      // the recipe book already filterable ("fancy", "date-night",
      // "comfort"). recipe.tags from the generator usually carries them
      // already; this unions in anything the model omitted plus the
      // vibe-bridge tags. The save path seeds user_recipes.tags from
      // recipe.tags.
      const baseRecipe = previewRecipe || recipe;
      const saveIntent = resolveCookingIntent({
        mealPrompt: (mealPrompt || "").trim(),
        derivedIntent: dishContract?.intent || null,
        conceptFrame,
      });
      const recipeToSave = saveIntent?.tags?.length
        ? { ...baseRecipe, tags: Array.from(new Set([
            ...(Array.isArray(baseRecipe.tags) ? baseRecipe.tags : []),
            ...saveIntent.tags,
          ])) }
        : baseRecipe;
      await cb?.(recipeToSave);

      // Capture training pair — generate-recipe input vs final
      // recipe the user committed to. Save / cook / schedule all
      // count as "accept" (the user kept the AI's output).
      // Diff between `recipe` (immutable model output) and
      // `previewRecipe` (post-edit / post-scale) is the training
      // signal — typically the same shape, sometimes scaled or
      // ingredient-swapped.
      if (userId) {
        const final = previewRecipe || recipe;
        const sameJson = JSON.stringify(final) === JSON.stringify(recipe);
        captureTrainingExample({
          userId,
          taskType: "recipe_generate",
          input: {
            mealPrompt,
            priority,
            course,
            cuisine,
            time,
            difficulty,
            pantryCount,
            // Flag the photo-flow vs typed-prompt origin so the
            // export script can train them as distinct flavors.
            origin: photoSeedItems ? "photo" : "manual",
            photoSketch: photoSeedSketch || null,
          },
          modelOutput: recipe,
          userFinal:   sameJson ? null : final,
          correctionKind: sameJson ? "accept" : "edit",
          modelVersion: "generate-recipe@v1",
          metadata: {
            action: kind,
            previousTitles: previousTitles.length,
            isRegen: previousTitles.length > 0,
          },
        });
      }

      if (userId) clearDraft(userId);
      setDraftSavedAt(null);
    } catch (e) {
      console.error(`[ai recipe] ${kind} failed:`, e);
    } finally {
      setBusy(null);
    }
  };
  const handleSave     = handleAction("save",     onSave);
  const handleSchedule = handleAction("schedule", onSchedule);
  const handleCookIt   = handleAction("cook",     onSaveAndCook);

  // Preview-phase add: user spotted a missing ingredient after the
  // final recipe generated. Append to pantryEdits.adds (same shape
  // the tweak-phase addPantryRow uses) then fire cookFinal() again
  // — the lockedIngredients flow picks up the new entry and the
  // regeneration integrates it into the steps. Another Sonnet call,
  // but intentional and deterministic (sketch anchor keeps the dish
  // identity). Shape matches addPantryRow in the tweak phase.
  const addIngredientAndRegen = async (row) => {
    if (!row || !sketch || busy) return;
    const pendingAdd = {
      name: row.name,
      amount: `${row.amount ?? 1}${row.unit ? ` ${row.unit}` : ""}`,
      ingredientId: row.ingredientId || row.canonicalId || null,
      pantryItemId: row.id || null,
      emoji: row.emoji,
    };
    setPantryEdits(prev => ({ ...prev, adds: [...prev.adds, pendingAdd] }));
    // cookFinal reads state — we've set pantryEdits via the setter
    // above, but React batches; call cookFinal on the NEXT tick so
    // buildLockedIngredients sees the new adds. setTimeout(0) is the
    // simplest way to defer past the setState queue.
    setTimeout(() => { cookFinal(); }, 0);
  };

  // ── Compose-a-meal handlers ─────────────────────────────────────

  // Derive ingredient descriptors for the pairWith payload. Trims to
  // name + amount so a long recipe doesn't bloat the prompt. The full
  // recipe is already saved in user_recipes; this is just the context
  // Claude needs to build something complementary.
  const ingredientDescriptors = (r) =>
    (r?.ingredients || [])
      .filter((i) => i && typeof i.item === "string" && i.item.trim())
      .map((i) => ({ name: i.item, amount: i.amount ?? null }));

  // Open the "+ Add X" sheet for a course. The sheet offers two paths:
  // Draft new (re-enters setup with pairWith seeded) or Pick existing
  // (opens the filtered library picker).
  const openAddSheet = (courseType) => {
    if (busy) return;
    setAddSheetCourse(courseType);
  };

  // "Draft new" path — saves the current recipe as the anchor (or
  // extends the in-progress meal), seeds pairWith with the anchor's
  // identity, resets the sketch/tweak machinery, and kicks back to
  // setup with course pre-filled to the target. The pieces already
  // in mealInProgress survive the reset.
  const draftNewComponent = async (courseType) => {
    if (!recipe) return;
    setAddSheetCourse(null);
    const saver = onSilentSave || onSave;
    if (!saver) return;
    setBusy("compose");
    try {
      // Fall back to the user's setup chips when Claude forgot to
      // stamp the tags on its final output — the user's intent is
      // the ground truth. Without this a pre-Phase-1 cached response
      // could land as a "main" on a slot it wasn't.
      const resolvedCourse     = recipe.course     || (course     === "any" ? null : course);
      const resolvedMealTiming = recipe.mealTiming || (mealTiming === "any" ? null : mealTiming);
      const recipeWithTags = { ...recipe, course: resolvedCourse, mealTiming: resolvedMealTiming };
      // Persist the current recipe so it exists as a standalone row
      // in user_recipes. This piece is reusable — it lives in the
      // library regardless of whether the user ever "saves the meal".
      const saved = (await saver(recipeWithTags)) || recipeWithTags;
      // Seed or extend the in-progress meal. First call establishes
      // the anchor (main); subsequent + Add calls append pieces.
      setMealInProgress((prev) => {
        const incoming = { course: saved.course || "main", recipe: saved };
        if (!prev) {
          return { anchor: saved, pieces: [incoming] };
        }
        return { ...prev, pieces: [...prev.pieces, incoming] };
      });
      // Anchor context for the next draft. Use the current anchor if
      // one's already set (so "Dessert after side after main" still
      // complements the MAIN, not the side), else the just-saved
      // recipe.
      const anchor = mealInProgress?.anchor || saved;
      setPairWith({
        title:       anchor.title,
        course:      anchor.course || "main",
        cuisine:     anchor.cuisine || undefined,
        ingredients: ingredientDescriptors(anchor),
        // Logistics for the side being drafted next (rides prefs as
        // pairWithMeta): match the anchor's portions, respect its
        // clock, and dodge the oven when the main owns it. Recipe
        // duration lives on time:{prep,cook} — there is no top-level
        // timeMin on recipe objects.
        serves: Number.isFinite(Number(anchor.serves)) ? Number(anchor.serves) : null,
        totalTimeMinutes: (() => {
          const t = anchor.time || {};
          const total = (Number(t.prep) || 0) + (Number(t.cook) || 0);
          return total > 0 ? total : null;
        })(),
        oven: (anchor.steps || []).some((s) =>
          /\b(oven|bake|roast|broil|preheat)\b/i.test(s?.instruction || s?.body || "")),
      });
      // Reset the sketch + tweak machinery AND star-ingredient picks.
      // Star ingredients are a "build around THIS protein" signal for
      // the piece the user is currently drafting; they should never
      // carry from main → side. The eggs that were the main's hero
      // shouldn't reappear in the side's star slot — the anchor's
      // proteins already ride along via pairWith, so Claude has the
      // context without the side being forced to include them.
      // mealPrompt / cuisine / time / difficulty stay — those read as
      // overall session mood, not piece-specific direction.
      setRecipe(null);
      setSketch(null);
      setPantryEdits({ swaps: {}, removes: new Set(), adds: [], shopping: new Set() });
      // Bans are piece-specific — dropping ricotta from the main shouldn't
      // ban it from the side. Fresh piece, fresh ban list.
      setBannedAcrossRegens([]);
      setRecipeFeedback("");
      setSwapOpenIdx(null);
      setSwapSearch("");
      setAddOpen(false);
      setStarIngredientIds([]);
      setCourse(courseType);
      setErrMsg("");
      setPhase("setup");
    } catch (e) {
      console.error("[ai recipe] compose draft-new failed:", e);
    } finally {
      setBusy(null);
    }
  };

  // "Pick existing" path — attach a library recipe to the meal
  // without re-drafting. Recipe is already in user_recipes (or is a
  // bundled one); we just pin it into mealInProgress.pieces. The
  // anchor stays on the current preview so the user can keep adding.
  const attachExistingComponent = async (courseType, chosenRecipe) => {
    if (!recipe || !chosenRecipe) return;
    setPickExistingFor(null);
    setAddSheetCourse(null);
    const saver = onSilentSave || onSave;
    // Make sure the current preview's anchor is in user_recipes too.
    // First call of the compose flow needs to lock the anchor in so
    // pieces are siblings, not just floating refs to an unsaved draft.
    let anchorRecipe = mealInProgress?.anchor;
    if (!mealInProgress) {
      if (!saver) return;
      setBusy("compose");
      try {
        anchorRecipe = (await saver(recipe)) || recipe;
      } catch (e) {
        console.error("[ai recipe] compose anchor-save failed:", e);
        setBusy(null);
        return;
      }
    }
    setMealInProgress((prev) => {
      if (!prev) {
        return {
          anchor: anchorRecipe,
          pieces: [
            { course: anchorRecipe.course || "main", recipe: anchorRecipe },
            { course: courseType, recipe: chosenRecipe },
          ],
        };
      }
      return { ...prev, pieces: [...prev.pieces, { course: courseType, recipe: chosenRecipe }] };
    });
    setBusy(null);
  };

  // Remove a piece from the in-progress meal. Does NOT delete the
  // recipe from user_recipes — it just unpins it from this meal.
  const removePiece = (idx) => {
    setMealInProgress((prev) => {
      if (!prev) return prev;
      const nextPieces = prev.pieces.filter((_, i) => i !== idx);
      if (nextPieces.length === 0) return null;
      return { ...prev, pieces: nextPieces };
    });
  };

  // "SAVE MEAL" CTA from the sticky header. Two jobs:
  //   1. If the user is sitting on a preview (e.g. the dessert they
  //      just drafted) that hasn't been pinned yet, silently persist
  //      it and append so the meal captures everything on screen.
  //   2. Persist the meal itself — a `meals` row plus one
  //      `meal_recipes` row per piece — via onMealSave. The pieces
  //      are already in user_recipes individually (every + Add
  //      triggered a silent save), so the meal is a pure pointer-set.
  const finishMeal = async () => {
    const saver = onSilentSave || onSave;
    setBusy("compose");
    try {
      // 1. Pin the current preview's recipe if it's not yet in pieces.
      //    Build the committed piece list in a local so we use the
      //    post-pin snapshot without waiting for setState to flush.
      let committed = mealInProgress;
      const pinnedSlugs = new Set((committed?.pieces || []).map(p => p.recipe?.slug).filter(Boolean));
      const shouldPinCurrent = phase === "preview" &&
        recipe &&
        recipe.course !== "bake" && recipe.course !== "prep" &&
        !pinnedSlugs.has(recipe.slug);
      if (shouldPinCurrent && saver) {
        try {
          const resolvedCourse     = recipe.course     || (course     === "any" ? null : course);
          const resolvedMealTiming = recipe.mealTiming || (mealTiming === "any" ? null : mealTiming);
          const recipeWithTags = { ...recipe, course: resolvedCourse, mealTiming: resolvedMealTiming };
          const saved = (await saver(recipeWithTags)) || recipeWithTags;
          const incoming = { course: saved.course || "side", recipe: saved };
          committed = committed
            ? { ...committed, pieces: [...committed.pieces, incoming] }
            : { anchor: saved, pieces: [{ course: saved.course || "main", recipe: saved }] };
          setMealInProgress(committed);
        } catch (e) {
          console.error("[ai recipe] finishMeal pin-current failed:", e);
        }
      }

      // 2. Persist the meal row. Need a parent handler + at least one
      //    piece; otherwise the CTA is really just "close this flow".
      const piecesForRow = (committed?.pieces || [])
        .filter(p => p.recipe?.slug)
        .map((p, i) => ({
          recipeSlug: p.recipe.slug,
          course:     p.course || "main",
          sortOrder:  i,
        }));
      if (onMealSave && piecesForRow.length > 0) {
        const anchor = committed?.anchor || piecesForRow[0];
        const mealTimingForRow = anchor?.mealTiming
          || (mealTiming === "any" ? null : mealTiming)
          || null;
        const name = buildMealName(committed?.anchor, committed?.pieces);
        try {
          await onMealSave({
            name,
            emoji:       committed?.anchor?.emoji   || null,
            cuisine:     committed?.anchor?.cuisine || null,
            mealTiming:  mealTimingForRow,
            anchorSlug:  committed?.anchor?.slug    || null,
            pieces:      piecesForRow,
          });
        } catch (e) {
          console.error("[ai recipe] finishMeal onMealSave failed:", e);
        }
      }
    } finally {
      setBusy(null);
    }
    setMealInProgress(null);
    setPairWith(null);
    onCancel?.();
  };

  // Header is shared across phases so the back button is always where
  // the user expects it.
  const header = (
    <>
      <div style={{ padding: "24px 20px 0", display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={onCancel} style={iconBtn}>←</button>
        <div style={{ flex: 1, fontFamily: FONT.mono, fontSize: 12, color: COLOR.plum, letterSpacing: "0.12em" }}>
          AI RECIPE
        </div>
      </div>
      {/* Allergy guard OFF banner — persistent across phases so the user
          always knows their allergens aren't being excluded, with a
          one-tap re-enable. Only shows when they actually have allergies. */}
      {allergyGuardOff && userAllergies.length > 0 && (
        <div style={{
          margin: "10px 20px 0", padding: "8px 12px", display: "flex", alignItems: "center", gap: 8,
          background: COLOR.roseDeep || "rgba(190,60,60,0.10)",
          border: `1px solid ${COLOR.roseDim || COLOR.rose}`, borderRadius: 10,
        }}>
          <span style={{ flex: 1, fontFamily: FONT.forma, fontSize: 12, color: COLOR.ink, lineHeight: 1.4 }}>
            Allergy guard <strong>OFF</strong> — your allergens may appear in recipes.
          </span>
          <button
            onClick={() => setAllergyGuardOff(false)}
            className="mcm-focusable"
            style={{
              background: COLOR.rose, color: "#fff", border: "none", borderRadius: 8,
              padding: "6px 11px", fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
              cursor: "pointer", touchAction: "manipulation", flexShrink: 0,
            }}
          >
            Turn on
          </button>
        </div>
      )}
    </>
  );

  // A slot counts as filled either because a piece is pinned in
  // mealInProgress.pieces OR because the user is currently previewing
  // a draft of that course (about to be pinned via "+ Add" or "SAVE
  // MEAL"). Without this, "+ SIDE" on a side-preview would double-add
  // the same slot.
  const filledSlots = new Set((mealInProgress?.pieces || []).map(p => p.course));
  if (phase === "preview" && recipe?.course && recipe.course !== "main"
      && recipe.course !== "bake" && recipe.course !== "prep") {
    filledSlots.add(recipe.course);
  }

  // Sticky MEAL header — shown across all phases whenever a meal is
  // mid-compose. Renders the pieces as pills + "+ Add X" buttons for
  // the slots the user hasn't filled yet, plus a SAVE MEAL CTA. Lives
  // outside the scrollable body so it stays visible as the user
  // drafts / tweaks / previews subsequent pieces. Hidden when
  // mealInProgress is null (first draft hasn't been anchored yet).
  const mealHeaderBlock = mealInProgress ? (
    <MealInProgressHeader
      meal={mealInProgress}
      filledSlots={filledSlots}
      onAdd={openAddSheet}
      onRemovePiece={removePiece}
      onFinish={finishMeal}
      disabled={!!busy}
    />
  ) : null;

  // Frame reveal — user tapped SURPRISE ME, we picked a frame, now
  // they get to see it before any AI call fires. Reroll is free
  // (client-side resample). DRAFT IT routes into start() with the
  // frame already in state. No emoji per the no-emoji-in-UI standing
  // direction; the editorial label + axis chips carry it.
  if (phase === "frame_reveal" && conceptFrame) {
    const axes = [
      conceptFrame.type      && { kind: "TYPE",      label: conceptFrame.type.label },
      conceptFrame.cuisine   && { kind: "CUISINE",   label: conceptFrame.cuisine.label },
      conceptFrame.technique && { kind: "TECHNIQUE", label: conceptFrame.technique.label },
      conceptFrame.hero      && { kind: "HERO",      label: conceptFrame.hero.label },
      conceptFrame.vibe      && { kind: "VIBE",      label: conceptFrame.vibe.label },
    ].filter(Boolean);
    return (
      <div>
        {header}
        {mealHeaderBlock}
        <div style={{ padding: "40px 22px 120px", maxWidth: 480, margin: "0 auto" }}>
          <div style={{
            fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold,
            letterSpacing: "0.14em", textAlign: "center", marginBottom: 14,
          }}>
            TONIGHT'S CONCEPT
          </div>
          <div style={{
            fontFamily: FONT.serif, fontSize: 30, fontStyle: "italic",
            fontWeight: 300, color: COLOR.ink, textAlign: "center",
            lineHeight: 1.2, margin: "0 0 22px",
          }}>
            {conceptFrame.label}
          </div>
          <div style={{
            display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center",
            marginBottom: 28,
          }}>
            {axes.map(a => (
              <span key={a.kind} style={{
                fontFamily: FONT.mono, fontSize: 9, fontWeight: 600,
                color: COLOR.gold, background: COLOR.goldDeep,
                border: `1px solid ${COLOR.goldDim}`,
                borderRadius: 4, padding: "3px 8px",
                letterSpacing: "0.10em",
              }}>
                {a.kind} · {a.label.toUpperCase()}
              </span>
            ))}
          </div>
          <div style={{
            fontFamily: FONT.sans, fontSize: 13, color: COLOR.dim,
            lineHeight: 1.5, textAlign: "center", marginBottom: 28,
            padding: "0 8px",
          }}>
            Reroll as many times as you want — drafting only fires once
            you commit. Or pin a recipe type and reroll the rest.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={() => runClarifyThenStart()}
              style={{
                ...primaryBtn,
                width: "100%", padding: "16px",
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                letterSpacing: "0.12em",
              }}
            >
              DRAFT IT →
            </button>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={rerollFrame}
                style={{
                  ...outlineBtn, flex: 1, padding: "14px",
                  fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
                  letterSpacing: "0.10em",
                }}
              >
                REROLL
              </button>
              <button
                onClick={() => setChangeTypeOpen(true)}
                style={{
                  ...outlineBtn, flex: 1, padding: "14px",
                  fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
                  letterSpacing: "0.10em",
                }}
              >
                CHANGE TYPE
              </button>
            </div>
            <button
              onClick={() => { setConceptFrame(null); setPhase("setup"); }}
              style={{
                marginTop: 6, padding: "12px",
                background: "transparent", border: "none",
                color: COLOR.sub, fontFamily: FONT.mono, fontSize: 10,
                letterSpacing: "0.12em", cursor: "pointer",
              }}
            >
              ← BACK TO SETUP
            </button>
          </div>
        </div>
        {changeTypeOpen && createPortal(
          <div
            onClick={() => setChangeTypeOpen(false)}
            style={{
              position: "fixed", inset: 0, background: "#000000dd",
              zIndex: 280, display: "flex", alignItems: "flex-end",
              maxWidth: 480, margin: "0 auto",
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                width: "100%", background: COLOR.ground,
                borderRadius: "20px 20px 0 0", padding: "20px 22px 36px",
                maxHeight: "75vh", overflowY: "auto",
              }}
            >
              <div style={{ width: 36, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 18px" }} />
              <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.12em", marginBottom: 8 }}>
                PIN A RECIPE TYPE
              </div>
              <div style={{ fontFamily: FONT.serif, fontSize: 20, fontStyle: "italic", color: COLOR.ink, marginBottom: 18 }}>
                What kind of thing do you want to make?
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {RECIPE_TYPES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => changeFrameType(t.id)}
                    style={{
                      display: "flex", alignItems: "center", padding: "12px 14px",
                      background: COLOR.soil, border: `1px solid ${COLOR.border}`,
                      borderRadius: 10, color: COLOR.ink,
                      fontFamily: FONT.sans, fontSize: 14, textAlign: "left",
                      cursor: "pointer",
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
      </div>
    );
  }

  // Clarify loading — Haiku is generating the planning questions.
  // Community match phase — pre-gen dedupe found one or more
  // community-published recipes that satisfy the user's prompt.
  // Offer them as no-cost alternatives before paying Haiku + Sonnet.
  // User can pick a candidate (opens it in CookMode with community
  // attribution) or override with "generate fresh anyway."
  if (phase === "community_match") {
    return (
      <div>
        {header}
        {mealHeaderBlock}
        <div style={{ padding: "32px 22px 140px", maxWidth: 520, margin: "0 auto" }}>
          <div style={{
            fontFamily: FONT.mono, fontSize: 10, fontWeight: 700,
            color: COLOR.gold, letterSpacing: "0.14em",
            textAlign: "center", marginBottom: 12, textTransform: "uppercase",
          }}>
            Already in the community
          </div>
          <div style={{
            fontFamily: FONT.serif || FONT.itemTitle, fontStyle: "italic",
            fontSize: 24, fontWeight: 400, color: COLOR.ink,
            textAlign: "center", lineHeight: 1.25, marginBottom: 6,
          }}>
            We already have great versions of this
          </div>
          <div style={{
            fontFamily: FONT.sans, fontSize: 13, color: COLOR.dim,
            textAlign: "center", marginBottom: 28, lineHeight: 1.4,
            padding: "0 12px",
          }}>
            People have cooked these and rated them. Try one, or generate
            your own twist.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
            {communityMatches.map((row) => (
              <button
                key={row.recipe_id}
                type="button"
                onClick={() => {
                  if (typeof onCookExistingRecipe === "function") {
                    onCookExistingRecipe({
                      slug:               row.slug,
                      title:              row.title,
                      emoji:              row.emoji,
                      goalImageURL:       row.goal_image_url,
                      _communityRecipeId: row.recipe_id,
                      _communityAuthorId: row.author_id,
                    });
                  }
                }}
                className="mcm-focusable"
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  width: "100%", padding: "14px 16px",
                  background: COLOR.deep,
                  border: `1px solid ${COLOR.gold}`,
                  borderRadius: 14,
                  cursor: "pointer", touchAction: "manipulation",
                  textAlign: "left",
                }}
              >
                {row.goal_image_url ? (
                  <img
                    src={row.goal_image_url}
                    alt=""
                    style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", background: COLOR.soil, flex: "0 0 auto" }}
                  />
                ) : (
                  <div style={{
                    width: 56, height: 56, borderRadius: 10,
                    background: COLOR.soil, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    fontSize: 28, flex: "0 0 auto",
                  }}>
                    {row.emoji || "🍽️"}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: FONT.itemTitle, fontStyle: "italic",
                    fontSize: 17, fontWeight: 500, color: COLOR.ink,
                    overflow: "hidden", textOverflow: "ellipsis",
                    whiteSpace: "nowrap", marginBottom: 4,
                  }}>
                    {row.title}
                  </div>
                  <div style={{
                    fontFamily: FONT.mono, fontSize: 11,
                    color: COLOR.sub, letterSpacing: "0.04em",
                  }}>
                    {row.cook_count} cook{row.cook_count === 1 ? "" : "s"}
                    {" · "}
                    avg {Number(row.avg_rating || 0).toFixed(2)} / 3
                  </div>
                </div>
                <div style={{
                  fontFamily: FONT.mono, fontSize: 11, fontWeight: 700,
                  color: COLOR.gold, letterSpacing: "0.08em",
                  textTransform: "uppercase", flex: "0 0 auto",
                }}>
                  Cook →
                </div>
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => {
              // One-shot bypass — record THIS prompt as already-deduped
              // so the next runClarifyThenStart skips the check and
              // proceeds straight to clarify.
              setCommunityDedupedFor((mealPrompt || "").trim());
              setCommunityMatches([]);
              runClarifyThenStart(conceptFrame);
            }}
            className="mcm-focusable"
            style={{
              width: "100%", padding: "14px",
              background: "transparent",
              border: `1px solid ${COLOR.edge}`,
              borderRadius: 14,
              fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
              letterSpacing: "0.12em", textTransform: "uppercase",
              color: COLOR.ink, cursor: "pointer",
              touchAction: "manipulation",
            }}
          >
            Generate my own anyway
          </button>
        </div>
      </div>
    );
  }

  // Brief (~700ms typical). Same loading splash as sketch with
  // different copy so the user knows what's coming next.
  // Allergy conflict — the user explicitly asked for one of their own
  // allergens. Safety gate: confirm "cooking for someone else" (turns the
  // guard off + generates) or back out.
  if (phase === "allergy_conflict") {
    return (
      <div>
        {header}
        {mealHeaderBlock}
        <div style={{ padding: "40px 24px 56px", maxWidth: 460, margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", color: COLOR.gold, marginBottom: 10 }}>
            ⚠ {allergyConflicts.join(" & ").toUpperCase()} ALLERGY
          </div>
          <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontSize: 24, color: COLOR.ink, lineHeight: 1.15 }}>
            “{(mealPrompt || "this dish").trim()}” usually needs {allergyConflicts.join(" & ")}
          </div>
          <div style={{ marginTop: 10, fontFamily: FONT.forma, fontSize: 13, color: COLOR.dim, lineHeight: 1.5 }}>
            I can make an allergy-safe version that swaps it out{(() => { const h = suggestSubstitutes(allergyConflicts); return h ? ` (${h})` : ""; })()} — or build the original if you're cooking for someone else.
          </div>
          <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={allergyConflictSubstitute} className="mcm-focusable"
              style={{ width: "100%", padding: "15px", background: COLOR.leaf, color: "#fff", border: "none", borderRadius: 14, fontFamily: FONT.forma, fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", cursor: "pointer", touchAction: "manipulation" }}>
              Make it allergy-safe — swap the {allergyConflicts.join(" & ").toLowerCase()}
            </button>
            <button onClick={allergyConflictGenerate} className="mcm-focusable"
              style={{ width: "100%", padding: "14px", background: "transparent", color: COLOR.rose, border: `1px solid ${COLOR.roseDim || COLOR.rose}`, borderRadius: 12, fontFamily: FONT.forma, fontSize: 13, fontWeight: 600, cursor: "pointer", touchAction: "manipulation" }}>
              Use it anyway — I'm cooking for someone else
            </button>
            <button onClick={allergyConflictCancel} className="mcm-focusable"
              style={{ width: "100%", padding: "13px", background: "transparent", color: COLOR.dim, border: "none", fontFamily: FONT.forma, fontSize: 13, fontWeight: 600, cursor: "pointer", touchAction: "manipulation" }}>
              Pick something else
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Appetite funnel — open-axis ("chicken with sauce") mood→lane drill.
  // Picking a lane (or a mood-level surprise) anchors the draft and fires
  // the sketch; BACK returns to setup.
  if (phase === "funnel" && funnel) {
    return (
      <div>
        {header}
        {mealHeaderBlock}
        <AppetiteFunnel
          funnel={funnel}
          onPick={pickFunnelLane}
          onChefPick={chefPickFunnel}
          onBasic={basicPickFunnel}
          onReroll={rerollFunnel}
          rerolling={funnelRerolling}
          onBack={() => { setFunnel(null); setPhase("setup"); }}
        />
      </div>
    );
  }

  if (phase === "clarify_loading") {
    return (
      <div>
        {header}
        {mealHeaderBlock}
        <div style={{ padding: "60px 20px", textAlign: "center" }}>
          <div style={{
            fontFamily: FONT.serif, fontSize: 22, fontStyle: "italic",
            color: COLOR.ink, lineHeight: 1.3,
          }}>
            A few quick choices…
          </div>
          <div style={{
            marginTop: 8, fontFamily: FONT.sans, fontSize: 12,
            color: COLOR.dim, lineHeight: 1.5,
          }}>
            Lining up some questions to make this draft fit you.
          </div>
        </div>
      </div>
    );
  }

  // Clarify phase — Haiku returned 1-3 questions. Render each as a
  // chip group, with "Chef's choice" always available as a skip-this-
  // question option. Bottom CTAs: SKIP ALL (uses chef-choice for any
  // unanswered) and DRAFT IT (commits whatever's currently selected
  // and fires the sketch). The clarifications + answers ride through
  // buildPrefs into the sketch payload.
  if (phase === "clarify" && clarifications.length > 0) {
    return (
      <div>
        {header}
        {mealHeaderBlock}
        <div style={{ padding: "32px 22px 140px", maxWidth: 480, margin: "0 auto" }}>
          <div style={{
            fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold,
            letterSpacing: "0.14em", textAlign: "center", marginBottom: 12,
          }}>
            A FEW QUICK CHOICES
          </div>
          <div style={{
            fontFamily: FONT.serif, fontSize: 24, fontStyle: "italic",
            fontWeight: 300, color: COLOR.ink, textAlign: "center",
            lineHeight: 1.25, margin: "0 0 8px",
          }}>
            Help me narrow it down
          </div>
          <div style={{
            fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim,
            textAlign: "center", lineHeight: 1.5, marginBottom: 28,
            padding: "0 12px",
          }}>
            Pick one, several, or type your own. Chef's choice always stands by.
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {clarifications.map((q) => {
              // Defensive read — old draft state might still hold a
              // legacy single-id string. Treat anything non-object as
              // chef-choice (empty selection).
              const aRaw = clarificationAnswers[q.id];
              const a = aRaw && typeof aRaw === "object"
                ? { ids: Array.isArray(aRaw.ids) ? aRaw.ids : [], custom: typeof aRaw.custom === "string" ? aRaw.custom : "" }
                : { ids: [], custom: "" };
              const hasSelection = a.ids.length > 0 || a.custom.trim().length > 0;

              const toggleOption = (optId) => {
                setClarificationAnswers(prev => {
                  const cur = prev[q.id];
                  const safe = cur && typeof cur === "object"
                    ? { ids: Array.isArray(cur.ids) ? cur.ids : [], custom: typeof cur.custom === "string" ? cur.custom : "" }
                    : { ids: [], custom: "" };
                  const ids = safe.ids.includes(optId)
                    ? safe.ids.filter(x => x !== optId)
                    : [...safe.ids, optId];
                  return { ...prev, [q.id]: { ids, custom: safe.custom } };
                });
              };
              const setCustom = (text) => {
                setClarificationAnswers(prev => {
                  const cur = prev[q.id];
                  const safe = cur && typeof cur === "object"
                    ? { ids: Array.isArray(cur.ids) ? cur.ids : [], custom: "" }
                    : { ids: [], custom: "" };
                  return { ...prev, [q.id]: { ids: safe.ids, custom: text } };
                });
              };
              const clearAll = () => {
                setClarificationAnswers(prev => ({ ...prev, [q.id]: { ids: [], custom: "" } }));
              };

              return (
                <div key={q.id}>
                  <div style={{
                    fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
                    color: COLOR.gold, letterSpacing: "0.12em",
                    marginBottom: 8, textTransform: "uppercase",
                  }}>
                    {q.question}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {q.options.map((opt) => {
                      const active = a.ids.includes(opt.id);
                      return (
                        <button
                          key={opt.id}
                          onClick={() => toggleOption(opt.id)}
                          style={{
                            padding: "8px 14px",
                            background: active ? COLOR.goldDeep : COLOR.soil,
                            border: `1px solid ${active ? COLOR.gold : COLOR.border}`,
                            color: active ? COLOR.gold : COLOR.ink,
                            borderRadius: 14,
                            fontFamily: FONT.sans, fontSize: 13,
                            fontWeight: active ? 600 : 400,
                            cursor: "pointer",
                          }}
                        >
                          {active ? "✓ " : ""}{opt.label}
                        </button>
                      );
                    })}
                    {/* Chef's choice is exclusive: tapping it clears
                        everything else. Visually inactive when the
                        user has selected anything, since the picks
                        override the "let chef decide" default. */}
                    <button
                      onClick={clearAll}
                      style={{
                        padding: "8px 14px",
                        background: !hasSelection ? COLOR.deep : "transparent",
                        border: `1px dashed ${!hasSelection ? COLOR.gold : COLOR.border}`,
                        color: !hasSelection ? COLOR.gold : COLOR.sub,
                        borderRadius: 14,
                        fontFamily: FONT.sans, fontSize: 13,
                        fontStyle: "italic",
                        fontWeight: !hasSelection ? 600 : 400,
                        cursor: "pointer",
                      }}
                    >
                      {q.chefChoiceLabel || "Chef's choice"}
                    </button>
                  </div>
                  {/* Custom-text input. Sits below the chip row so the
                      preset options stay the visual primary. Free-text
                      rides into the same comma-joined answer payload
                      sent to Claude — multi-select chips + this string
                      get joined when buildPrefs assembles the prompt. */}
                  <input
                    value={a.custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Or type your own…"
                    style={{
                      marginTop: 8,
                      width: "100%",
                      padding: "8px 12px",
                      background: COLOR.deep,
                      border: `1px solid ${a.custom.trim() ? COLOR.gold : COLOR.edge}`,
                      borderRadius: 10,
                      fontFamily: FONT.sans, fontSize: 13,
                      color: COLOR.ink,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 32, display: "flex", flexDirection: "column", gap: 8 }}>
            <button
              onClick={confirmClarifyAndDraft}
              style={{
                ...primaryBtn,
                width: "100%", padding: "16px",
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
                letterSpacing: "0.12em",
              }}
            >
              DRAFT IT →
            </button>
            <button
              onClick={() => {
                // SKIP ALL — set every unanswered question to chef-choice
                // (already the default seed, but be explicit) then draft.
                const allChef = {};
                clarifications.forEach(q => { allChef[q.id] = "chef-choice"; });
                setClarificationAnswers(allChef);
                // Fire on next tick so the state lands before start() reads it.
                setTimeout(() => confirmClarifyAndDraft(), 0);
              }}
              style={{
                ...outlineBtn,
                width: "100%", padding: "14px",
                fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
                letterSpacing: "0.10em",
              }}
            >
              SKIP ALL · CHEF DECIDES
            </button>
            <button
              onClick={() => {
                setClarifications([]);
                setClarificationAnswers({});
                setPhase(conceptFrame ? "frame_reveal" : "setup");
              }}
              style={{
                marginTop: 6, padding: "12px",
                background: "transparent", border: "none",
                color: COLOR.sub, fontFamily: FONT.mono, fontSize: 10,
                letterSpacing: "0.12em", cursor: "pointer",
              }}
            >
              ← BACK
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "sketch_loading" || phase === "final_loading" || phase === "loading") {
    // Sticky partial — updated only on successful parse via the
    // useEffect above. Title sticks as soon as Claude emits it; the
    // UI never reverts to "Cooking it up…" once a title shows.
    // GenerationProgress turns the streamed milestones (title →
    // ingredients → steps) into a determinate bar + step list, with a
    // time-based estimate filling the gaps so it never freezes.
    return (
      <div>
        {header}
        {mealHeaderBlock}
        <GenerationProgress
          phase={phase}
          partial={phase === "final_loading" ? streamedPartial : null}
          pantryCount={pantryCount}
        />
      </div>
    );
  }

  if (phase === "error") {
    const isNet = errKind === "network";
    // Smart retry: for network errors, re-fire the SAME call so the
    // user keeps everything they entered (prompt / pantry / framing).
    // For other errors fall back to dropping them at setup since the
    // failure is likely something they need to change about input.
    const onRetry = () => {
      if (isNet && errSource === "final") {
        // Final-cook failed; sketch is still in state, just re-run.
        setErrMsg(""); setErrKind("other"); setErrSource(null);
        cookFinal();
        return;
      }
      if (isNet && errSource === "sketch") {
        // Sketch failed; re-run start() with the same args. start
        // reads from React state (mealPrompt etc.) so a no-arg call
        // re-fires with whatever the user typed.
        setErrMsg(""); setErrKind("other"); setErrSource(null);
        start();
        return;
      }
      // Non-network or unknown source: safest fallback is setup.
      setErrMsg(""); setErrKind("other"); setErrSource(null);
      setPhase("setup");
    };
    return (
      <div>
        {header}
        {mealHeaderBlock}
        <div style={{ padding: "40px 20px", textAlign: "center" }}>
          <div style={{ fontSize: 44, opacity: 0.6 }}>{isNet ? "📡" : "🫠"}</div>
          <div style={{ marginTop: 14, fontFamily: FONT.serif, fontSize: 22, fontStyle: "italic", color: COLOR.ink }}>
            {isNet ? "Connection blip" : "Draft hiccup"}
          </div>
          <div style={{
            marginTop: 8, marginLeft: "auto", marginRight: "auto", maxWidth: 440,
            fontFamily: FONT.sans, fontSize: 13, color: COLOR.dim,
            lineHeight: 1.5, wordBreak: "break-word", textAlign: "left",
            padding: "10px 12px", background: COLOR.deep,
            border: `1px solid ${COLOR.edge}`, borderRadius: 10,
            maxHeight: 180, overflowY: "auto",
            whiteSpace: "pre-wrap",
          }}>
            {errMsg || "Something went sideways."}
          </div>
          {!isNet && (
            <div style={{ marginTop: 10, fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, letterSpacing: "0.06em" }}>
              Full detail also in devtools console.
            </div>
          )}
          <div style={{ marginTop: 20, display: "flex", gap: 10, justifyContent: "center" }}>
            <button onClick={onRetry} style={primaryBtn}>TRY AGAIN</button>
            <button onClick={onCancel} style={secondaryBtn}>CANCEL</button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "tweak" && sketch) {
    // Helpers scoped to the tweak render so they can close over
    // pantryEdits + setPantryEdits cleanly.
    const pantryById = new Map(pantry.map(p => [p.id, p]));
    const lookupPantryRow = (id) => (id ? pantryById.get(id) : null);

    // ── Accept / Decline proposed canonicals ────────────────────
    // The AI minted synthetics for ingredients it couldn't bind to
    // a roster id (rookworst, gjetost, doenjang). The edge function
    // already created the pending_ingredient_info row + stamped
    // _proposed markers on the sketch ingredient. The user decides:
    //   ACCEPT  — keep the synthetic, registry grows. Pending row
    //             stays at status='pending' for admin promotion.
    //   DECLINE — delete the pending row, swap the recipe's
    //             ingredientId to the AI-suggested fallback
    //             (smoked_sausage for rookworst, etc.). Strip the
    //             _proposed markers so the row reads as a normal
    //             umbrella-bound ingredient.
    const acceptProposed = async (slug) => {
      // Promote the pending row → live ingredient_info. Mirrors the
      // AdminPanel approve flow:
      //   1. Read the pending row's info JSONB
      //   2. Upsert into ingredient_info (the live canonical store)
      //   3. Mark pending row status='approved' so it doesn't re-surface
      // Once #2 lands, the resolver picks the new canonical up via
      // useIngredientInfo's realtime subscription on the next refresh —
      // recipes asking for "rookworst" downstream find the synthetic
      // immediately, no admin round-trip.
      if (!userId) {
        console.warn("[AIRecipe] acceptProposed: no userId, dismissing only");
        setProposedCanonicals(prev => prev.filter(p => p.slug !== slug));
        return;
      }
      try {
        // Step 1 — read the pending info we want to promote.
        const { data: pendingRow, error: readErr } = await supabase
          .from("pending_ingredient_info")
          .select("info")
          .eq("user_id", userId)
          .eq("slug", slug)
          .maybeSingle();
        if (readErr) throw new Error(`pre-read: ${readErr.message}`);
        if (!pendingRow?.info) {
          console.warn(`[AIRecipe] acceptProposed: no pending row found for slug "${slug}"`);
          setProposedCanonicals(prev => prev.filter(p => p.slug !== slug));
          return;
        }
        // Step 2 — promote to live ingredient_info.
        const { error: liveErr } = await supabase
          .from("ingredient_info")
          .upsert(
            { ingredient_id: slug, info: pendingRow.info },
            { onConflict: "ingredient_id" },
          );
        if (liveErr) throw new Error(`promote: ${liveErr.message}`);
        // Step 3 — flip pending row status so it doesn't re-surface.
        const { error: pErr } = await supabase
          .from("pending_ingredient_info")
          .update({ status: "approved", approved_canonical_id: slug })
          .eq("user_id", userId)
          .eq("slug", slug);
        if (pErr) console.warn(`[AIRecipe] acceptProposed status flip failed: ${pErr.message}`);
        console.info(`[AIRecipe] promoted "${slug}" to ingredient_info`);
      } catch (e) {
        console.error(`[AIRecipe] acceptProposed("${slug}") failed:`, e);
        // Don't block dismissal — user can retry via admin panel if needed.
      }
      setProposedCanonicals(prev => prev.filter(p => p.slug !== slug));
    };
    const declineProposed = async (slug, fallbackId) => {
      // Delete the pending row by (user_id, slug). Best-effort —
      // a network failure here doesn't block the swap below; the
      // row just lingers as orphaned pending data which admin can
      // clean up.
      try {
        if (userId) {
          await supabase
            .from("pending_ingredient_info")
            .delete()
            .eq("user_id", userId)
            .eq("slug", slug);
        }
      } catch (e) {
        console.warn(`[AIRecipe] failed to delete declined pending row "${slug}":`, e);
      }
      // Swap the sketch ingredient's identity to the fallback.
      // Walk both pantry[] and ideal[] arrays — the proposed flag
      // can appear on either side depending on whether Claude
      // tagged the row as "shopping" (in pantry[]) or "use the
      // ideal" (in ideal[]).
      const fallbackCanon = fallbackId ? findIngredient(fallbackId) : null;
      const swap = (rows) => (rows || []).map(row => {
        if (row?._proposedSlug !== slug) return row;
        const next = { ...row };
        next.ingredientId = fallbackId || null;
        if (fallbackCanon) {
          // Keep the AI's quantity but rename to the fallback canonical's
          // display name so the user sees "Smoked Sausage" instead of
          // "Rookworst" after declining.
          next.name = fallbackCanon.name || row.name;
        }
        delete next._proposed;
        delete next._proposedSlug;
        delete next._proposedFallbackId;
        return next;
      });
      setSketch(prev => prev ? {
        ...prev,
        pantry: swap(prev.pantry),
        ideal:  swap(prev.ideal),
      } : prev);
      setProposedCanonicals(prev => prev.filter(p => p.slug !== slug));
    };

    // Rank all pantry rows by closeness to a sketch pantry row.
    // Replaces the old same-category dump (which scrolled past the
    // screen the moment the user had >10 pantry items in that
    // bucket). Returns [{ row, score }] sorted highest first.
    //
    // Ranking signals (additive), highest of the two targets wins:
    //   +1000  same canonical id as the target
    //   +500   same parentId / hub as the target
    //   +100   same food category
    //   +10    per token shared with the target's normalized name
    //
    // TWO TARGETS. When the sketch row is an AI sub ("Croissant
    // Rolls as wrapper substitute for flour tortillas"), both the
    // current row AND the `subbedFrom` original count as targets.
    // Without this the ranker scored only against "croissant roll"
    // and the user's search for "torti" returned Tortilla Chips
    // ahead of Mission Tortillas because neither scored against
    // croissant. Scoring against subbedFrom ("flour tortillas")
    // pulls Mission Tortillas to +1000 (exact canonical) while
    // Tortilla Chips stays near zero — the match the user clearly
    // wants rises to the top.
    //
    // When `query` is non-empty we additionally require the row's
    // name to contain the query as a substring (user is explicitly
    // typing, so loose substring is fine — we're filtering a list
    // in front of their eyes, not auto-pairing).
    // Quality tiers derived from rankSwapCandidates scores.
    //   score >= 1000 → "exact"   (same canonical id)
    //   score >=  500 → "family"  (same hub — cuts of chicken, kinds of cheese)
    //   score >=  100 → "category"(same food category — dairy, produce, etc.)
    //   score >    0  → "weak"    (only token overlap)
    //   score ==   0  → "incompat"(different category AND no overlap)
    //
    // The swap picker won't render incompat candidates by default —
    // butter can't substitute for oranges and we shouldn't present it
    // as an option. An explicit "show anything" disclosure exists for
    // intentional overrides; picking a "weak" candidate triggers a
    // confirm so the user acknowledges the stretch.
    const classifySwapQuality = (score) => {
      if (score >= 1000) return "exact";
      if (score >= 500) return "family";
      if (score >= 100) return "category";
      if (score > 0) return "weak";
      return "incompat";
    };

    const rankSwapCandidates = (sketchPantryRow, query) => {
      const primaryCanon = sketchPantryRow.ingredientId
        ? findIngredient(sketchPantryRow.ingredientId)
        : null;
      const subFromName = sketchPantryRow.subbedFrom || null;
      const subFromSlug = subFromName ? resolveNameToCanonicalId(subFromName) : null;
      const subFromCanon = subFromSlug ? findIngredient(subFromSlug) : null;

      const targetSlugs = new Set(
        [primaryCanon?.id, subFromCanon?.id].filter(Boolean),
      );
      const targetHubs = new Set(
        [
          primaryCanon?.parentId || primaryCanon?.id,
          subFromCanon?.parentId || subFromCanon?.id,
        ].filter(Boolean),
      );
      const targetCats = new Set(
        [primaryCanon?.category, subFromCanon?.category].filter(Boolean),
      );
      const targetTokens = new Set([
        ...normalizeForMatch(
          sketchPantryRow.name || primaryCanon?.name || "",
        ).split(/\s+/).filter(Boolean),
        ...normalizeForMatch(subFromName || "").split(/\s+/).filter(Boolean),
      ]);

      const q = (query || "").trim().toLowerCase();
      const seen = new Set();
      const scored = [];
      for (const p of pantry) {
        if (!p) continue;
        if (p.id === sketchPantryRow.pantryItemId) continue;
        const canon = p.ingredientId ? findIngredient(p.ingredientId) : null;
        const key = canon?.id || (p.name || "").toLowerCase() || p.id;
        if (seen.has(key)) continue;
        seen.add(key);

        if (q && !(p.name || "").toLowerCase().includes(q)) continue;

        let score = 0;
        if (canon?.id && targetSlugs.has(canon.id)) score += 1000;
        const pHub = canon?.parentId || canon?.id;
        if (pHub && targetHubs.has(pHub)) score += 500;
        if (canon?.category && targetCats.has(canon.category)) score += 100;
        const pTokens = new Set(
          normalizeForMatch(p.name || "").split(/\s+/).filter(Boolean),
        );
        let overlap = 0;
        for (const t of targetTokens) if (pTokens.has(t)) overlap++;
        score += overlap * 10;
        scored.push({ row: p, score });
      }
      scored.sort((a, b) => b.score - a.score);
      return scored;
    };

    const toggleRemove = (i) => setPantryEdits(prev => {
      const removes = new Set(prev.removes);
      if (removes.has(i)) removes.delete(i);
      else removes.add(i);
      return { ...prev, removes };
    });
    const applySwap = (i, rowId) => setPantryEdits(prev => ({
      ...prev,
      swaps: { ...prev.swaps, [i]: rowId },
    }));
    const clearSwap = (i) => setPantryEdits(prev => {
      const swaps = { ...prev.swaps };
      delete swaps[i];
      return { ...prev, swaps };
    });
    const togglePromoteToShopping = (i) => setPantryEdits(prev => {
      const shopping = new Set(prev.shopping);
      if (shopping.has(i)) shopping.delete(i);
      else shopping.add(i);
      return { ...prev, shopping };
    });
    // Escape hatch for unwanted substitutions. When Claude subbed
    // Ritz for flour and the user doesn't have real flour in their
    // pantry, SWAP has no good targets and × just deletes the slot
    // entirely — the user was trapped making a recipe with ingredients
    // they didn't want. `rejectSubForShopping` promotes the IDEAL
    // to the shopping list (so "all-purpose flour" appears as a
    // buy-this item) AND drops the sub pantry row in one atomic
    // edit, so the user sees the real ingredient they'll shop for
    // instead of the substitute they wanted to reject.
    const rejectSubForShopping = (pantryIdx, idealIdx) => setPantryEdits(prev => {
      const removes = new Set(prev.removes);
      removes.add(pantryIdx);
      const shopping = new Set(prev.shopping);
      if (idealIdx != null) shopping.add(idealIdx);
      // Drop any swap the user had on this row — it's going away.
      const swaps = { ...prev.swaps };
      delete swaps[pantryIdx];
      return { ...prev, removes, shopping, swaps };
    });
    const dropAdd = (i) => setPantryEdits(prev => ({
      ...prev,
      adds: prev.adds.filter((_, idx) => idx !== i),
    }));
    const addPantryRow = (row) => setPantryEdits(prev => ({
      ...prev,
      adds: [...prev.adds, {
        name: row.name,
        amount: `${row.amount ?? 1}${row.unit ? ` ${row.unit}` : ""}`,
        ingredientId: row.ingredientId || row.canonicalId || null,
        pantryItemId: row.id,
        emoji: row.emoji,
      }],
    }));

    // Unified ingredient list — ordered by classical recipe
    // structure (sketch.ideal), with each slot resolved to either
    // a pantry row (direct match or sub) or a shoppable stub.
    // Replaces the prior two-section layout that confused users
    // with ingredients appearing in both "WHAT WE'RE GRABBING"
    // and "ALSO TYPICAL" when the name match missed a brand
    // prefix or plural.
    //
    // Entry kinds:
    //   matched    — ideal slot covered by a pantry row exactly
    //   subbed     — ideal slot covered by a different pantry row
    //                (subbedFrom or name mismatch)
    //   missing    — ideal slot with no pantry counterpart
    //   extra      — pantry row that doesn't map to any ideal slot
    //                (user added, or sketch put it in pantry only)
    //   userAdd    — user's + ADD FROM PANTRY row
    //   removed    — user removed this pantry slot (thin undo strip)
    const ingredientEntries = (() => {
      const entries = [];
      const matchedPantryIdx = new Set();
      // First pass: walk ideal slots in classical order.
      sketch.ideal.forEach((ideal, idealIdx) => {
        let pantryIdx = null;
        let isSub = false;
        // Resolve the ideal to a canonical slug (e.g. "Ricotta" →
        // "ricotta"). Used to pair pantry rows by canonical rather
        // than display-name — handles "GV Ricotta 32oz" vs "Ricotta"
        // without needing brand/size stripping heuristics to line
        // up exactly.
        // Resolve ideal → one primary canonical id plus any alt
        // canonicals the AI's "or/and/&/," separators implied. The
        // sketch's pantry column matches a canonical on EITHER side,
        // so splitting the ideal lets "ciabatta or crusty bread"
        // pair with a sketch.pantry row canonical'd to "ciabatta".
        const idealCanonIds = new Set();
        if (ideal.ingredientId) idealCanonIds.add(ideal.ingredientId);
        const directSlug = resolveNameToCanonicalId(ideal.name);
        if (directSlug) idealCanonIds.add(directSlug);
        for (const sub of splitIdealName(ideal.name)) {
          const subSlug = resolveNameToCanonicalId(sub);
          if (subSlug) idealCanonIds.add(subSlug);
        }
        const idealSubNames = [ideal.name, ...splitIdealName(ideal.name)];

        // (1) subbedFrom match — the sketch told us which ideal
        // slot this pantry row was subbing for. Cheapest, truest
        // signal: pair on that. Check against every sub-name so the
        // "ciabatta" the AI picked still matches a subbedFrom of
        // "ciabatta or crusty bread".
        for (let j = 0; j < sketch.pantry.length; j++) {
          if (matchedPantryIdx.has(j)) continue;
          const p = sketch.pantry[j];
          if (!p.subbedFrom) continue;
          if (idealSubNames.some(n => namesMatch(p.subbedFrom, n))) {
            pantryIdx = j;
            isSub = true;
            break;
          }
        }
        // (2) canonical-id match — same slug on EITHER side (primary
        // OR any alt from the split) means same ingredient regardless
        // of brand / size / casing in the display names. Hub-family
        // equivalence also counts here: the ideal's `chicken` hub
        // pairs with the sketch.pantry row's `chicken_breast` cut
        // because both resolve to the same chicken_hub parent.
        if (pantryIdx === null && idealCanonIds.size > 0) {
          for (let j = 0; j < sketch.pantry.length; j++) {
            if (matchedPantryIdx.has(j)) continue;
            const p = sketch.pantry[j];
            if (!p.ingredientId) continue;
            const familyHit = [...idealCanonIds].some(slug =>
              sameCanonicalFamily(p.ingredientId, slug),
            );
            if (familyHit) {
              pantryIdx = j;
              isSub = false;
              break;
            }
          }
        }
        // (3) fuzzy name match — last-resort fallback for rows
        // without canonical ids on either side. Match against any
        // sub-name so "Ciabatta Roll" can pair with the "ciabatta"
        // alt of a compound ideal.
        if (pantryIdx === null) {
          for (let j = 0; j < sketch.pantry.length; j++) {
            if (matchedPantryIdx.has(j)) continue;
            const p = sketch.pantry[j];
            if (idealSubNames.some(n => namesMatch(p.name, n))) {
              pantryIdx = j;
              isSub = false;
              break;
            }
          }
        }
        if (pantryIdx !== null) {
          matchedPantryIdx.add(pantryIdx);
          // USE ORIGINAL was clicked: user rejected this sub AND promoted
          // the classical ideal to shopping. Render as a clean
          // "idealShopping" entry (showing the ORIGINAL ingredient name
          // with a "not in pantry" note) — NOT as a strikethrough
          // "removed" row which reads as "this ingredient won't be in
          // the recipe." The sub pantry row is absorbed as "rejected
          // context" for an optional hint.
          const rejected = pantryEdits.removes.has(pantryIdx)
            && pantryEdits.shopping.has(idealIdx);
          if (rejected) {
            entries.push({ kind: "idealShopping", idealIdx, rejectedPantryIdx: pantryIdx });
          } else {
            entries.push({
              kind: pantryEdits.removes.has(pantryIdx) ? "removed"
                  : isSub ? "subbed"
                  : "matched",
              idealIdx,
              pantryIdx,
            });
          }
        } else {
          // No sketch.pantry match but user still promoted this ideal
          // to shopping via the standalone PROMOTE action — render as
          // idealShopping so it doesn't get lost.
          if (pantryEdits.shopping.has(idealIdx)) {
            entries.push({ kind: "idealShopping", idealIdx, rejectedPantryIdx: null });
          } else {
            entries.push({ kind: "missing", idealIdx });
          }
        }
      });
      // Second pass: any pantry rows the sketch included that
      // didn't map to an ideal slot (rare, but handles sketches
      // where the AI adds something extra beyond the classical).
      sketch.pantry.forEach((p, j) => {
        if (matchedPantryIdx.has(j)) return;
        entries.push({
          kind: pantryEdits.removes.has(j) ? "removed" : "extra",
          pantryIdx: j,
        });
      });
      // Third pass: user-added extras (egg whites case).
      pantryEdits.adds.forEach((_, k) => {
        entries.push({ kind: "userAdd", addIdx: k });
      });
      return entries;
    })();

    // When an ideal slot has no pantry match in the SKETCH, scan
    // the user's actual pantry for anything that could fill it.
    // The AI occasionally overlooks obvious pairings (saw this on
    // "Pasta (penne or rigatoni)" with penne sitting right there in
    // pantry). Three tiers, in order: same canonical id → same
    // food category → name substring. Dedupes by canonical so a
    // 50-can tuna stack contributes one candidate, not fifty.
    const findRawPantryCandidates = (ideal) => {
      // Build a list of resolution targets: one per sub-name the AI
      // wrote ("ciabatta or crusty bread" → two targets). The top-
      // level ingredientId, when Claude stamped one, rides along as
      // an additional exact-match target.
      const subNames = splitIdealName(ideal.name);
      const targets = subNames.map(n => {
        const slug = resolveNameToCanonicalId(n);
        const canon = slug ? findIngredient(slug) : null;
        return {
          name:     n,
          slug,
          category: canon?.category || null,
        };
      });
      if (ideal.ingredientId) {
        const canon = findIngredient(ideal.ingredientId);
        targets.unshift({
          name:     ideal.name,
          slug:     ideal.ingredientId,
          category: canon?.category || null,
        });
      }

      // Items already locked into the recipe (pantry-matched,
      // swapped-in, or user-added) shouldn't appear as candidates
      // for OTHER missing slots.
      const usedIds = new Set();
      sketch.pantry.forEach((row, i) => {
        if (pantryEdits.removes.has(i)) return;
        if (row.pantryItemId) usedIds.add(row.pantryItemId);
        const swapped = pantryEdits.swaps[i];
        if (swapped) usedIds.add(swapped);
      });
      pantryEdits.adds.forEach(a => { if (a.pantryItemId) usedIds.add(a.pantryItemId); });

      const seenCanon = new Set();
      const exact = [];
      const byCategory = [];
      const byName = [];
      for (const row of pantry) {
        if (usedIds.has(row.id)) continue;
        const canonKey = row.ingredientId || row.canonicalId || null;
        if (canonKey && seenCanon.has(canonKey)) continue;
        const rowCanon = row.ingredientId ? findIngredient(row.ingredientId) : null;
        const rowCategory = rowCanon?.category || null;

        // Tier 1 — canonical match against ANY target slug. Exact
        // match OR hub-family equivalence (a `chicken` target pairs
        // with a pantry row tagged `chicken_breast` because both
        // hang off chicken_hub). Hub-family is how the user's actual
        // cuts surface under a generic recipe call like "Chicken".
        let matched = null;
        for (const t of targets) {
          if (!t.slug) continue;
          if (row.ingredientId && sameCanonicalFamily(row.ingredientId, t.slug)) {
            matched = "exact"; break;
          }
        }

        // Tier 2 — category match, BUT only when the row's name also
        // carries a name-level signal for the same target. Without
        // this name check, a loose category like "pantry" pulled in
        // every unrelated jar/box/bag that happened to share the
        // fallback bucket (Mustard, Crackers, Oats, Sugar all landing
        // under "salt & black pepper"). Requiring both raises the bar.
        if (!matched) {
          for (const t of targets) {
            if (t.category && rowCategory === t.category && namesMatch(row.name, t.name)) {
              matched = "byCategory"; break;
            }
          }
        }

        // Tier 3 — last-resort fuzzy name match against any target.
        // Catches user-typed pantry rows (no ingredientId, no
        // category) where the name alone has to do the work.
        if (!matched) {
          for (const t of targets) {
            if (namesMatch(row.name, t.name)) { matched = "byName"; break; }
          }
        }

        if (matched === "exact") {
          if (canonKey) seenCanon.add(canonKey);
          exact.push(row);
        } else if (matched === "byCategory") {
          if (canonKey) seenCanon.add(canonKey);
          byCategory.push(row);
        } else if (matched === "byName") {
          if (canonKey) seenCanon.add(canonKey);
          byName.push(row);
        }
      }
      return [...exact, ...byCategory, ...byName].slice(0, 6);
    };

    const lockedCount = buildLockedIngredients().length;

    return (
      <div>
        {header}
        {mealHeaderBlock}
        <div style={{ padding: "12px 20px 60px" }}>
          {/* Sketch header — title + emoji + AI rationale. Reads as
              "here's what we drafted, want to tweak before cooking?" */}
          <div style={{ textAlign: "center", padding: "8px 0 16px" }}>
            <div style={{ fontSize: 48 }}>{sketch.emoji || "🍽️"}</div>
            <h1 style={{ fontFamily: FONT.serif, fontSize: 24, fontWeight: 300, fontStyle: "italic", color: COLOR.ink, margin: "8px 0 4px" }}>
              {sketch.title}
            </h1>
            {sketch.subtitle && (
              <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim, lineHeight: 1.4 }}>
                {sketch.subtitle}
              </div>
            )}
            <div style={{
              marginTop: 10, display: "inline-flex", gap: 6, alignItems: "center",
              padding: "4px 10px", background: COLOR.goldDeep,
              border: "1px solid #c7a8d455", borderRadius: 14,
              fontFamily: FONT.mono, fontSize: 9, color: COLOR.plum,
              letterSpacing: "0.08em",
            }}>
              ✨ SKETCH — TWEAK BEFORE COOK
            </div>
            {conceptFrame && (
              <div style={{ marginTop: 8 }}>
                <FrameChip frame={conceptFrame} onOpen={() => setFrameDetailOpen(true)} />
              </div>
            )}
          </div>

          {sketch.aiRationale && (
            <div style={{
              padding: "10px 12px", marginBottom: 14,
              background: COLOR.goldDeep,
              border: `1px solid ${COLOR.goldDim}`,
              borderRadius: 10,
              fontFamily: FONT.sans, fontSize: 12,
              color: COLOR.ink, lineHeight: 1.5,
            }}>
              {sketch.aiRationale}
            </div>
          )}

          {/* PANTRY column — what the recipe will use, derived from
              the sketch + the user's tweak diff. Each row shows the
              pulled pantry item inline + swap/remove affordances. */}
          <Section label="INGREDIENTS">
            {/* Proposed canonicals banner — AI minted new synthetics
                for ingredients with no roster match (rookworst,
                gjetost, etc.). User decides per entry: accept (keep
                in pending review) or decline (fall back to umbrella). */}
            {proposedCanonicals.length > 0 && (
              <div style={{
                padding: "12px 14px", marginBottom: 12,
                background: COLOR.goldDeep,
                border: `1px solid ${COLOR.goldDim}`,
                borderRadius: 12,
              }}>
                <div style={{
                  fontFamily: FONT.mono, fontSize: 10, letterSpacing: "0.1em",
                  color: COLOR.gold, marginBottom: 8,
                }}>
                  ✨ AI PROPOSED {proposedCanonicals.length} NEW CANONICAL{proposedCanonicals.length === 1 ? "" : "S"}
                </div>
                <div style={{
                  fontFamily: FONT.sans, fontSize: 12, color: COLOR.ink,
                  lineHeight: 1.5, marginBottom: 10,
                }}>
                  This recipe wants ingredients your registry doesn't have yet. Accept to grow the registry, or decline to fall back to a generic substitute.
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {proposedCanonicals.map(p => {
                    const fallbackCanon = p.fallbackId ? findIngredient(p.fallbackId) : null;
                    return (
                      <div key={p.slug} style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 10, padding: "8px 10px",
                        background: COLOR.ground,
                        border: `1px solid ${COLOR.edge}`, borderRadius: 8,
                        flexWrap: "wrap",
                      }}>
                        <div style={{ flex: 1, minWidth: 180 }}>
                          <div style={{
                            fontFamily: FONT.serif, fontSize: 14, fontStyle: "italic",
                            color: COLOR.ink,
                          }}>
                            {p.name}
                          </div>
                          <div style={{
                            marginTop: 2, fontFamily: FONT.mono, fontSize: 9,
                            letterSpacing: "0.08em", color: COLOR.dim,
                          }}>
                            SLUG: {p.slug}
                            {fallbackCanon && <> · FALLBACK: {fallbackCanon.name}</>}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button
                            type="button"
                            onClick={() => acceptProposed(p.slug)}
                            style={{
                              padding: "6px 12px",
                              background: COLOR.goldDeep,
                              border: `1px solid ${COLOR.gold}`,
                              borderRadius: 8,
                              fontFamily: FONT.mono, fontSize: 10,
                              letterSpacing: "0.08em", color: COLOR.gold,
                              cursor: "pointer",
                            }}
                          >
                            ADD
                          </button>
                          <button
                            type="button"
                            onClick={() => declineProposed(p.slug, p.fallbackId)}
                            style={{
                              padding: "6px 12px",
                              background: "transparent",
                              border: `1px solid ${COLOR.border}`,
                              borderRadius: 8,
                              fontFamily: FONT.mono, fontSize: 10,
                              letterSpacing: "0.08em", color: COLOR.dim,
                              cursor: "pointer",
                            }}
                          >
                            USE FALLBACK
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ marginBottom: 10, fontFamily: FONT.sans, fontSize: 11, color: COLOR.dim, fontStyle: "italic" }}>
              Listed in classical order. Swap or remove what's grabbed from your pantry, or add missing items to shopping — whatever fits.
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {ingredientEntries.map((entry, idx) => {
                // REMOVED — thin undo strip for both pantry & ideal.
                if (entry.kind === "removed") {
                  const row = sketch.pantry[entry.pantryIdx];
                  return (
                    <div key={`rem-${idx}`} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      padding: "8px 12px", background: COLOR.deep,
                      border: `1px dashed ${COLOR.border}`, borderRadius: 10,
                      fontFamily: FONT.sans, fontSize: 12, color: COLOR.sub,
                    }}>
                      <span><s>{row.name} · {row.amount}</s></span>
                      <button onClick={() => toggleRemove(entry.pantryIdx)} style={undoChip}>UNDO</button>
                    </div>
                  );
                }

                // IDEAL SHOPPING — user clicked USE ORIGINAL on a sub
                // row, which rejected Claude's substitute and asked to
                // use the classical ingredient instead. Renders as a
                // prominent "will shop for" row in the ORIGINAL
                // ingredient's position (not crossed out, not demoted
                // to a missing-style row) so the user reads it as
                // "this IS in the recipe, you'll pick it up at the
                // store." Shows the rejected sub as a small footnote
                // so the undo path stays discoverable.
                if (entry.kind === "idealShopping") {
                  const ideal = sketch.ideal[entry.idealIdx];
                  const rejectedRow = entry.rejectedPantryIdx != null
                    ? sketch.pantry[entry.rejectedPantryIdx]
                    : null;
                  return (
                    <div key={`ishop-${idx}`} style={{
                      padding: "10px 12px",
                      background: COLOR.ground, border: `1px solid ${COLOR.edge}`, borderRadius: 12,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: FONT.serif, fontStyle: "italic", fontSize: 15, color: COLOR.ink }}>
                            {ideal.name} · <span style={{ color: COLOR.dim, fontStyle: "normal", fontFamily: FONT.mono, fontSize: 12 }}>{ideal.amount}</span>
                          </div>
                          <div style={{
                            marginTop: 3, fontFamily: FONT.mono, fontSize: 9,
                            color: COLOR.amber, letterSpacing: "0.06em",
                          }}>
                            ⚠ NOT IN PANTRY — WILL ADD TO SHOPPING LIST
                          </div>
                          {rejectedRow && (
                            <div style={{
                              marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4,
                              padding: "2px 8px",
                              background: "#1a1410", border: "1px solid #3a2f1a",
                              borderRadius: 10,
                              fontFamily: FONT.mono, fontSize: 9,
                              color: "#a88868", letterSpacing: "0.04em",
                            }}>
                              rejected sub: {rejectedRow.name}
                            </div>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                          {/* UNDO restores the rejected sub: drops the
                              pantryIdx from removes AND idealIdx from
                              shopping in one click. */}
                          <button
                            onClick={() => {
                              if (entry.rejectedPantryIdx != null) toggleRemove(entry.rejectedPantryIdx);
                              togglePromoteToShopping(entry.idealIdx);
                            }}
                            style={undoChip}
                          >
                            UNDO
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }

                // MISSING — classical ingredient with no pantry match
                // IN THE SKETCH. Before offering + SHOP, scan the
                // raw user pantry for candidates the AI overlooked
                // (same canonical, same category, or name-fuzzy
                // match). If any exist, present them as tap-to-use
                // chips so the user recovers Claude's oversights
                // without bouncing through the + ADD FROM PANTRY
                // picker.
                if (entry.kind === "missing") {
                  const ideal = sketch.ideal[entry.idealIdx];
                  const promoted = pantryEdits.shopping.has(entry.idealIdx);
                  const rawCandidates = findRawPantryCandidates(ideal);
                  const missSwapKey = `miss-${entry.idealIdx}`;
                  const missSwapOpen = swapOpenIdx === missSwapKey;
                  return (
                    <div key={`miss-${idx}`} style={{
                      padding: "10px 12px",
                      background: promoted ? COLOR.leafDeep : COLOR.deep,
                      border: `1px dashed ${promoted ? COLOR.leafDim : COLOR.border}`,
                      borderRadius: 12,
                    }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontFamily: FONT.serif, fontStyle: "italic", fontSize: 15, color: promoted ? COLOR.leafMuted : COLOR.dim }}>
                            {ideal.name} · <span style={{ color: COLOR.dim, fontStyle: "normal", fontFamily: FONT.mono, fontSize: 12 }}>{ideal.amount}</span>
                          </div>
                          <div style={{ marginTop: 3, fontFamily: FONT.mono, fontSize: 9, color: promoted ? COLOR.leafMuted : (rawCandidates.length > 0 ? COLOR.sky : COLOR.amber), letterSpacing: "0.06em" }}>
                            {promoted
                              ? "✓ ON SHOPPING LIST"
                              : rawCandidates.length > 0
                                ? `⚡ YOU HAVE ${rawCandidates.length} MATCH${rawCandidates.length === 1 ? "" : "ES"} IN PANTRY`
                                : "✗ NOT IN PANTRY"}
                            {ideal.role && !promoted ? ` · ${String(ideal.role).toUpperCase()}` : ""}
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                          <button
                            onClick={() => missSwapOpen ? openSwapPicker(null) : openSwapPicker(missSwapKey)}
                            style={missSwapOpen ? swapBtnActive : swapBtn}
                          >
                            ⇌ SWAP
                          </button>
                          {onAddToPantry && !promoted && (
                            <button
                              onClick={() => onAddToPantry({
                                name: ideal.name,
                                ingredientId: ideal.ingredientId || resolveNameToCanonicalId(ideal.name) || null,
                                amount: ideal.amount,
                              })}
                              style={shopBtn}
                              title="I have this — add it to my pantry"
                            >
                              + IN PANTRY
                            </button>
                          )}
                          <button onClick={() => togglePromoteToShopping(entry.idealIdx)} style={promoted ? shopActiveBtn : shopBtn}>
                            {promoted ? "✓ ON LIST" : "+ SHOP"}
                          </button>
                        </div>
                      </div>
                      {missSwapOpen && (() => {
                        // Search picker for missing rows. The target is
                        // the ideal itself (not a sketch.pantry row) so
                        // rankSwapCandidates scores the user's pantry
                        // against what the recipe asked for. Picking
                        // something binds it via addPantryRow → userAdd
                        // path, which is the same shape + ADD FROM
                        // PANTRY uses.
                        const q = swapSearch.trim();
                        const target = {
                          name: ideal.name,
                          ingredientId: ideal.ingredientId || resolveNameToCanonicalId(ideal.name) || null,
                          pantryItemId: null,
                        };
                        const ranked = rankSwapCandidates(target, swapSearch);
                        const scored = ranked.map(r => ({
                          row: r.row,
                          score: r.score,
                          quality: classifySwapQuality(r.score),
                        }));
                        const safe = scored.filter(r => r.quality !== "incompat");
                        const incompat = scored.filter(r => r.quality === "incompat");
                        // Default-empty search-first picker. Nothing
                        // renders until the user types. Same gating as
                        // the primary swap picker — incompat items hidden
                        // unless explicitly shown.
                        // Small focused picker: show the top 5
                        // compatible candidates immediately. Widen to
                        // top 12 when the user starts typing. Incompat
                        // items hide until the user opts in via the
                        // override link.
                        const baseShown = q ? safe.slice(0, 12) : safe.slice(0, 5);
                        const shown = showIncompatSwaps
                          ? [...baseShown, ...incompat.slice(0, 20)]
                          : baseShown;
                        const pickCandidate = (c) => {
                          if (c.quality === "weak") {
                            const ok = typeof window !== "undefined" && window.confirm
                              ? window.confirm(`"${c.row.name}" isn't a close match for "${ideal.name}". Use it anyway?`)
                              : true;
                            if (!ok) return;
                          } else if (c.quality === "incompat") {
                            const ok = typeof window !== "undefined" && window.confirm
                              ? window.confirm(`"${c.row.name}" is in a different food category than "${ideal.name}" — this is an unusual substitution. Use it anyway?`)
                              : true;
                            if (!ok) return;
                          }
                          addPantryRow(c.row);
                          openSwapPicker(null);
                        };
                        return (
                          <div style={{
                            marginTop: 10, paddingTop: 10,
                            borderTop: `1px dashed ${COLOR.border}`,
                            display: "flex", flexDirection: "column", gap: 6,
                          }}>
                            <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.dim, letterSpacing: "0.08em" }}>
                              USE SOMETHING FROM PANTRY FOR {ideal.name.toUpperCase()}:
                            </div>
                            <input
                              type="text"
                              value={swapSearch}
                              onChange={e => setSwapSearch(e.target.value)}
                              placeholder="Search your pantry…"
                              style={swapSearchInput}
                              autoFocus
                            />
                            {!q && safe.length > 0 && (
                              <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, letterSpacing: "0.06em" }}>
                                COMPATIBLE SUBS · TYPE TO SEARCH
                              </div>
                            )}
                            {shown.length === 0 && (
                              <div style={{ fontFamily: FONT.sans, fontSize: 11, color: COLOR.dim, fontStyle: "italic" }}>
                                {q
                                  ? `No compatible substitutes match "${q}".`
                                  : `No compatible substitutes in your pantry for "${ideal.name}".`}
                                {!showIncompatSwaps && incompat.length > 0 && (
                                  <>
                                    {" "}
                                    <button
                                      onClick={() => setShowIncompatSwaps(true)}
                                      style={{
                                        background: "transparent", border: "none",
                                        color: COLOR.sky, cursor: "pointer",
                                        padding: 0, font: "inherit",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      Show all pantry items anyway
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                            {shown.map(c => (
                              <button
                                key={c.row.id}
                                onClick={() => pickCandidate(c)}
                                style={swapOptionBtn}
                                title={
                                  c.quality === "exact"    ? "Same ingredient"
                                : c.quality === "family"   ? "Same ingredient family"
                                : c.quality === "category" ? "Same food category"
                                : c.quality === "weak"     ? "Only loosely related"
                                : "Different food category — unusual substitution"
                                }
                              >
                                <span style={{ fontSize: 16 }}>{c.row.emoji || "🥫"}</span>
                                <span style={{ flex: 1, textAlign: "left", fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink }}>
                                  {c.row.name}
                                </span>
                                {(c.quality === "weak" || c.quality === "incompat") && (
                                  <span style={{
                                    padding: "1px 6px",
                                    background: COLOR.roseDeep, border: "1px solid #5a2a22",
                                    color: COLOR.rose, borderRadius: 6,
                                    fontFamily: FONT.mono, fontSize: 8,
                                    letterSpacing: "0.06em",
                                  }}>
                                    {c.quality === "weak" ? "LOOSE" : "DIFFERENT CATEGORY"}
                                  </span>
                                )}
                                <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim }}>
                                  {c.row.amount}{c.row.unit ? ` ${c.row.unit}` : ""}
                                </span>
                              </button>
                            ))}
                            {q && shown.length > 0 && !showIncompatSwaps && incompat.length > 0 && (
                              <button
                                onClick={() => setShowIncompatSwaps(true)}
                                style={{
                                  marginTop: 4,
                                  padding: "4px 8px",
                                  background: "transparent", border: `1px dashed ${COLOR.border}`,
                                  color: COLOR.muted, borderRadius: 6,
                                  fontFamily: FONT.mono, fontSize: 9,
                                  letterSpacing: "0.06em", cursor: "pointer",
                                }}
                              >
                                + SHOW {incompat.length} MORE (DIFFERENT CATEGORY)
                              </button>
                            )}
                          </div>
                        );
                      })()}
                      {rawCandidates.length > 0 && !promoted && (
                        <div style={{
                          marginTop: 10, paddingTop: 10,
                          borderTop: `1px dashed ${COLOR.border}`,
                          display: "flex", flexDirection: "column", gap: 6,
                        }}>
                          <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.dim, letterSpacing: "0.08em" }}>
                            THE AI MISSED THESE — TAP TO USE:
                          </div>
                          {rawCandidates.map(c => (
                            <button
                              key={c.id}
                              onClick={() => addPantryRow(c)}
                              style={swapOptionBtn}
                            >
                              <span style={{ fontSize: 16 }}>{c.emoji || "🥫"}</span>
                              <span style={{ flex: 1, textAlign: "left", fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink }}>
                                {c.name}
                              </span>
                              <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim }}>
                                {c.amount}{c.unit ? ` ${c.unit}` : ""}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                // USER-ADDED — egg-whites case. Green accent, remove only.
                if (entry.kind === "userAdd") {
                  const add = pantryEdits.adds[entry.addIdx];
                  return (
                    <div key={`add-${idx}`} style={{
                      padding: "10px 12px",
                      background: COLOR.leafDeep, border: `1px solid ${COLOR.leafDim}`, borderRadius: 12,
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: FONT.serif, fontStyle: "italic", fontSize: 15, color: COLOR.ink }}>
                          {add.emoji ? `${add.emoji} ` : ""}{add.name} · <span style={{ color: COLOR.dim, fontStyle: "normal", fontFamily: FONT.mono, fontSize: 12 }}>{add.amount}</span>
                        </div>
                        <div style={{ marginTop: 3, fontFamily: FONT.mono, fontSize: 9, color: COLOR.leafMuted, letterSpacing: "0.06em" }}>
                          + ADDED BY YOU
                        </div>
                      </div>
                      <button onClick={() => dropAdd(entry.addIdx)} style={removeBtn}>×</button>
                    </div>
                  );
                }

                // PANTRY-BACKED (matched | subbed | extra) — the
                // grabbed ingredient. Matched shows a classic tag;
                // subbed shows "classic calls for X" right under the
                // ingredient name so the user sees the swap inline
                // instead of inferring from a separate section.
                const pantryIdx = entry.pantryIdx;
                const row = sketch.pantry[pantryIdx];
                const isSub = entry.kind === "subbed";
                const isExtra = entry.kind === "extra";
                const idealRef = entry.kind === "matched" || entry.kind === "subbed"
                  ? sketch.ideal[entry.idealIdx]
                  : null;
                const swappedId = pantryEdits.swaps[pantryIdx];
                const swappedRow = lookupPantryRow(swappedId);
                const originRow  = lookupPantryRow(row.pantryItemId);
                const showRow    = swappedRow || originRow;
                const swapOpen   = swapOpenIdx === pantryIdx;
                const subHint = isSub && idealRef
                  ? `classic calls for ${idealRef.name}`
                  : row.subbedFrom && !isSub
                    ? `subbed from ${row.subbedFrom}`
                    : null;
                // Diet-loss check. When the AI asked for a claimed
                // variant ("low-carb tortilla", "vegan sausage") and
                // we paired a pantry row that doesn't carry that
                // claim, the substitution silently breaks the dietary
                // intent. Surface it inline so the user sees the
                // tradeoff before cooking. Claims are compared
                // case-insensitively; pantry claims come from scan
                // attributes (buildAttributesFromScan) or manual
                // ItemCard edits.
                const idealClaims = Array.isArray(idealRef?.dietaryClaims)
                  ? idealRef.dietaryClaims
                  : (idealRef ? extractDietaryClaims(idealRef.name).claims : []);
                const rowClaimsRaw = [
                  ...(Array.isArray(showRow?.attributes?.claims) ? showRow.attributes.claims : []),
                  ...(Array.isArray(showRow?.attributes?.certifications)
                    ? showRow.attributes.certifications.map(c => c?.label).filter(Boolean)
                    : []),
                ];
                const rowClaimSet = new Set(rowClaimsRaw.map(c => String(c).toLowerCase()));
                const lostClaims = idealClaims.filter(
                  c => !rowClaimSet.has(String(c).toLowerCase()),
                );
                return (
                  <div key={`p-${idx}`} style={{
                    padding: "10px 12px",
                    background: COLOR.ground, border: `1px solid ${COLOR.edge}`, borderRadius: 12,
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: FONT.serif, fontStyle: "italic", fontSize: 15, color: COLOR.ink, display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                          <span>{deriveRowHeader(swappedRow || row)}</span>
                          {(() => {
                            const cut = deriveRowCut(swappedRow || row);
                            if (!cut) return null;
                            return (
                              <span style={{
                                display: "inline-flex", alignItems: "center",
                                padding: "1px 7px",
                                background: COLOR.plum, border: `1px solid ${COLOR.plum}`,
                                borderRadius: 8,
                                fontFamily: FONT.mono, fontSize: 9, fontStyle: "normal",
                                color: COLOR.plum, letterSpacing: "0.08em",
                              }}>
                                {String(cut).toUpperCase()}
                              </span>
                            );
                          })()}
                          <span style={{ color: COLOR.dim, fontStyle: "normal", fontFamily: FONT.mono, fontSize: 12 }}>· {row.amount}</span>
                        </div>
                        {showRow && (
                          <div style={{ marginTop: 3, fontFamily: FONT.mono, fontSize: 9, color: COLOR.leafMuted, letterSpacing: "0.06em" }}>
                            {row.pantryItemId == null && swappedRow ? "⚡ AUTO-PAIRED FROM" : "✓ FROM"} {showRow.amount}{showRow.unit ? ` ${showRow.unit}` : ""} · {showRow.location || "pantry"}
                          </div>
                        )}
                        {!showRow && row.pantryItemId == null && (
                          <div style={{ marginTop: 3, fontFamily: FONT.mono, fontSize: 9, color: COLOR.amber, letterSpacing: "0.06em" }}>
                            ⚠ NOT IN PANTRY — SHOPPING
                          </div>
                        )}
                        {subHint && (
                          <div style={{
                            marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "2px 8px",
                            background: "#1f1410", border: "1px solid #4a2f1a",
                            borderRadius: 10,
                            fontFamily: FONT.mono, fontSize: 9,
                            color: "#d4a878", letterSpacing: "0.04em",
                          }}>
                            ⇌ {subHint}
                          </div>
                        )}
                        {lostClaims.length > 0 && (
                          <div style={{
                            marginTop: 4, marginLeft: subHint ? 6 : 0,
                            display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "2px 8px",
                            background: COLOR.roseDeep, border: "1px solid #5a2a22",
                            borderRadius: 10,
                            fontFamily: FONT.mono, fontSize: 9,
                            color: COLOR.rose, letterSpacing: "0.04em",
                          }}>
                            ⚠ NO LONGER {lostClaims.map(c => c.toUpperCase()).join(" / ")}
                          </div>
                        )}
                        {isExtra && (
                          <div style={{
                            marginTop: 4, display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "2px 8px",
                            background: COLOR.skyDeep, border: `1px solid ${COLOR.skyBorder}`,
                            borderRadius: 10,
                            fontFamily: FONT.mono, fontSize: 9,
                            color: COLOR.sky, letterSpacing: "0.04em",
                          }}>
                            + beyond classical
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        <button
                          onClick={() => swapOpen ? openSwapPicker(null) : openSwapPicker(pantryIdx)}
                          style={swapOpen ? swapBtnActive : swapBtn}
                        >
                          ⇌ SWAP
                        </button>
                        {/* USE ORIGINAL — only surfaces on substitution
                            rows (isSub). Drops the sub and carries the
                            classical ideal forward as a shopping item, so
                            users aren't trapped cooking with ingredients
                            they don't want. Example: Ritz auto-paired as
                            "flour"; user taps USE ORIGINAL → Ritz removed,
                            "all-purpose flour" lands on the shopping list. */}
                        {isSub && idealRef && (
                          <button
                            onClick={() => rejectSubForShopping(pantryIdx, entry.idealIdx)}
                            style={buyRealBtn}
                            title={`Drop this sub and use ${idealRef.name} instead`}
                          >
                            USE ORIGINAL
                          </button>
                        )}
                        <button onClick={() => toggleRemove(pantryIdx)} style={removeBtn}>×</button>
                      </div>
                    </div>
                    {swapOpen && (() => {
                      // Metadata-gated swap picker. Default shows only
                      // candidates in the same food category, hub, or
                      // sharing a token — real substitution territory.
                      // Incompatible candidates (different category, no
                      // overlap) are hidden unless the user explicitly
                      // opts to see them via the disclosure below.
                      const q = swapSearch.trim();
                      const ranked = rankSwapCandidates(row, swapSearch);
                      const scored = ranked.map(r => ({
                        row: r.row,
                        score: r.score,
                        quality: classifySwapQuality(r.score),
                      }));
                      const safe = scored.filter(r => r.quality !== "incompat");
                      const incompat = scored.filter(r => r.quality === "incompat");
                      // Swap picker on the tweak screen is small and
                      // focused — show the top compatible candidates
                      // immediately so users see "here are the 3 subs
                      // you already have for flour" without typing.
                      // With a query, widen to top 12. Incompat items
                      // stay hidden unless the user opts in.
                      const baseShown = q ? safe.slice(0, 12) : safe.slice(0, 5);
                      const shown = showIncompatSwaps
                        ? [...baseShown, ...incompat.slice(0, 20)]
                        : baseShown;
                      const pickCandidate = (c) => {
                        // Weak picks (token-only overlap — e.g. "cream
                        // cheese" searched as "cream" against "heavy
                        // cream") get a confirm so intentional stretches
                        // aren't accidental. Incompat picks (butter for
                        // oranges) get a sharper confirm before applying.
                        if (c.quality === "weak") {
                          const ok = typeof window !== "undefined" && window.confirm
                            ? window.confirm(`"${c.row.name}" isn't a close match for "${row.name}". Use it anyway?`)
                            : true;
                          if (!ok) return;
                        } else if (c.quality === "incompat") {
                          const ok = typeof window !== "undefined" && window.confirm
                            ? window.confirm(`"${c.row.name}" is in a different food category than "${row.name}" — this is an unusual substitution. Use it anyway?`)
                            : true;
                          if (!ok) return;
                        }
                        applySwap(pantryIdx, c.row.id);
                        openSwapPicker(null);
                      };
                      return (
                        <div style={{
                          marginTop: 10, paddingTop: 10,
                          borderTop: `1px dashed ${COLOR.border}`,
                          display: "flex", flexDirection: "column", gap: 6,
                        }}>
                          <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.dim, letterSpacing: "0.08em" }}>
                            SWAP {row.name.toUpperCase()} FOR:
                          </div>
                          <input
                            type="text"
                            value={swapSearch}
                            onChange={e => setSwapSearch(e.target.value)}
                            placeholder="Search your pantry…"
                            style={swapSearchInput}
                            autoFocus
                          />
                          {!q && (
                            <div style={{ fontFamily: FONT.sans, fontSize: 11, color: COLOR.muted, fontStyle: "italic" }}>
                              Type to search your pantry for a substitute.
                            </div>
                          )}
                          {q && shown.length === 0 && (
                            <div style={{ fontFamily: FONT.sans, fontSize: 11, color: COLOR.dim, fontStyle: "italic" }}>
                              No compatible substitutes match &quot;{q}&quot;.
                              {!showIncompatSwaps && incompat.length > 0 && (
                                <>
                                  {" "}
                                  <button
                                    onClick={() => setShowIncompatSwaps(true)}
                                    style={{
                                      background: "transparent", border: "none",
                                      color: COLOR.sky, cursor: "pointer",
                                      padding: 0, font: "inherit",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    Show all pantry items anyway
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                          {shown.map(c => (
                            <button
                              key={c.row.id}
                              onClick={() => pickCandidate(c)}
                              style={swapOptionBtn}
                              title={
                                c.quality === "exact"    ? "Same ingredient"
                              : c.quality === "family"   ? "Same ingredient family"
                              : c.quality === "category" ? "Same food category"
                              : c.quality === "weak"     ? "Only loosely related"
                              : "Different food category — unusual substitution"
                              }
                            >
                              <span style={{ fontSize: 16 }}>{c.row.emoji || "🥫"}</span>
                              <span style={{ flex: 1, textAlign: "left", fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink }}>
                                {c.row.name}
                              </span>
                              {/* Quality badge. exact+family fade into
                                  the chip so the common case is quiet;
                                  weak/incompat get a visible warning. */}
                              {(c.quality === "weak" || c.quality === "incompat") && (
                                <span style={{
                                  padding: "1px 6px",
                                  background: COLOR.roseDeep, border: "1px solid #5a2a22",
                                  color: COLOR.rose, borderRadius: 6,
                                  fontFamily: FONT.mono, fontSize: 8,
                                  letterSpacing: "0.06em",
                                }}>
                                  {c.quality === "weak" ? "LOOSE" : "DIFFERENT CATEGORY"}
                                </span>
                              )}
                              <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim }}>
                                {c.row.amount}{c.row.unit ? ` ${c.row.unit}` : ""}
                              </span>
                            </button>
                          ))}
                          {/* Override disclosure. Only shown when
                              compatible picks exist AND the user
                              hasn't yet opted in. Gives a way to see
                              "weird" pantry items without defaulting
                              to the kitchen-sink behavior. */}
                          {shown.length > 0 && !showIncompatSwaps && incompat.length > 0 && (
                            <button
                              onClick={() => setShowIncompatSwaps(true)}
                              style={{
                                marginTop: 4,
                                padding: "4px 8px",
                                background: "transparent", border: `1px dashed ${COLOR.border}`,
                                color: COLOR.muted, borderRadius: 6,
                                fontFamily: FONT.mono, fontSize: 9,
                                letterSpacing: "0.06em", cursor: "pointer",
                              }}
                            >
                              + SHOW {incompat.length} MORE (DIFFERENT CATEGORY)
                            </button>
                          )}
                          {swappedRow && (
                            <button onClick={() => { clearSwap(pantryIdx); openSwapPicker(null); }} style={swapClearBtn}>
                              ↺ REVERT TO ORIGINAL ({row.name})
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            {/* + ADD INGREDIENT FROM PANTRY — egg-whites case. */}
            <div style={{ marginTop: 10 }}>
              <button
                onClick={() => setAddOpen(o => !o)}
                style={addOpen ? addBtnActive : addBtn}
              >
                {addOpen ? "× CLOSE" : "+ ADD INGREDIENT FROM PANTRY"}
              </button>
              {addOpen && (() => {
                const usedIds = new Set();
                sketch.pantry.forEach((row, i) => {
                  if (pantryEdits.removes.has(i)) return;
                  if (row.pantryItemId) usedIds.add(row.pantryItemId);
                  const swapped = pantryEdits.swaps[i];
                  if (swapped) usedIds.add(swapped);
                });
                pantryEdits.adds.forEach(a => { if (a.pantryItemId) usedIds.add(a.pantryItemId); });
                const seenCanon = new Set();
                const candidates = pantry.filter(p => {
                  if (usedIds.has(p.id)) return false;
                  const canonId = p.ingredientId || p.canonicalId || p.name?.toLowerCase();
                  if (canonId && seenCanon.has(canonId)) return false;
                  if (canonId) seenCanon.add(canonId);
                  return true;
                });
                return (
                  <div style={{
                    marginTop: 8, padding: "10px 12px",
                    background: COLOR.deep, border: `1px solid ${COLOR.edge}`, borderRadius: 10,
                    maxHeight: 240, overflowY: "auto",
                    display: "flex", flexDirection: "column", gap: 6,
                  }}>
                    {candidates.length === 0 ? (
                      <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, fontStyle: "italic", padding: "12px 0", textAlign: "center" }}>
                        Every pantry item is already in the recipe.
                      </div>
                    ) : candidates.map(p => (
                      <button
                        key={p.id}
                        onClick={() => { addPantryRow(p); setAddOpen(false); }}
                        style={swapOptionBtn}
                      >
                        <span style={{ fontSize: 16 }}>{p.emoji || "🥫"}</span>
                        <span style={{ flex: 1, textAlign: "left", fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink }}>
                          {p.name}
                        </span>
                        <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim }}>
                          {p.amount}{p.unit ? ` ${p.unit}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                );
              })()}
            </div>
          </Section>

          {/* Recipe Feedback — second prompt, scoped to this sketch.
              Shapes the EXECUTION (technique, seasoning, plating) of
              the final cook. Distinct from the Meal Prompt at setup
              which shaped the CONCEPT.

              Copy is deliberately explicit: users were tapping NEW
              SKETCH to "apply" their notes, not realizing the notes
              feed directly into COOK THIS. The label + helper line
              + button-label switch ("COOK WITH NOTES" when
              recipeFeedback is non-empty) makes the relationship
              unambiguous. */}
          <Section label="LAST NOTES — APPLIED ON COOK">
            <textarea
              value={recipeFeedback}
              onChange={e => setRecipeFeedback(e.target.value)}
              placeholder='e.g. "make it spicier" · "skip the garlic in the steps" · "crispier edges" · "keep it under 30 min"'
              rows={3}
              style={{
                width: "100%", padding: "10px 12px",
                background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
                borderRadius: 10, color: COLOR.ink,
                fontFamily: FONT.sans, fontSize: 13, lineHeight: 1.5,
                outline: "none", boxSizing: "border-box", resize: "vertical",
              }}
            />
            <div style={{
              marginTop: 6,
              fontFamily: FONT.sans, fontSize: 12,
              color: COLOR.sub, lineHeight: 1.45,
            }}>
              {recipeFeedback.trim()
                ? <>Tap <strong style={{ color: COLOR.muted }}>Re-sketch</strong> to apply these notes to <em>this</em> dish, or <strong style={{ color: COLOR.muted }}>Cook this</strong> to draft the full recipe with them.</>
                : <>Tweaks here go straight into <strong style={{ color: COLOR.muted }}>Cook this</strong> below. Or type a change and tap Re-sketch to revise this dish first.</>}
            </div>
          </Section>

          {/* Action bar — RE-SKETCH (refine THIS dish with the notes, or
              a fresh reroll when notes are empty) vs COOK THIS (locks
              the ingredient set + fires final). */}
          <div style={{ marginTop: 24, display: "flex", gap: 10 }}>
            <button onClick={() => setPhase("setup")} style={iconActionBtn} title="Back to setup">
              ←
            </button>
            <button
              onClick={() => start(null, { applyNotes: !!recipeFeedback.trim() })}
              style={secondaryBtn}
              title={recipeFeedback.trim() ? "Re-sketch this dish with your notes" : "Sketch a different idea"}
            >
              ↻ {recipeFeedback.trim() ? "RE-SKETCH" : "NEW SKETCH"}
            </button>
            <button
              onClick={cookFinal}
              disabled={lockedCount === 0}
              style={{
                flex: 2, padding: "14px",
                background: lockedCount === 0 ? COLOR.border : "linear-gradient(135deg, #c7a8d4 0%, #a389b8 100%)",
                color: lockedCount === 0 ? COLOR.sub : COLOR.night,
                border: "none", borderRadius: 12,
                fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
                letterSpacing: "0.1em",
                cursor: lockedCount === 0 ? "not-allowed" : "pointer",
              }}
            >
              ✨ {recipeFeedback.trim() ? "COOK WITH NOTES" : "COOK THIS"} · {lockedCount}
            </button>
          </div>
        </div>
        <FrameDetailSheet
          frame={conceptFrame}
          open={frameDetailOpen}
          onClose={() => setFrameDetailOpen(false)}
          onReroll={rerollAndRedraft}
        />
      </div>
    );
  }

  if (phase === "preview" && recipe) {
    // Shadow `recipe` with the scaled preview inside the preview
    // branch so every render path (ingredients, nutrition, save/cook)
    // sees the user's chosen servings. Outer state `recipe` stays
    // intact so REGEN keeps Claude's original 4-person draft.
    const r = previewRecipe || recipe;
    const baseServes = Math.max(1, Number(recipe.serves) || 1);
    const currentServes = servingsOverride ?? baseServes;

    // Build the prose tokenizer's unit conversion targets from the
    // preview ingredient list, applying saved unit prefs the same way
    // IngredientsWithPairing does. Driven by the saved-pref store, so
    // when the picker writes a new pref via setPreferredUnit the
    // tokenizer re-runs and the steps below pick up the conversion
    // (½ stick of butter from 4 tbsp, etc.).
    const stepsUnitConversionTargets = buildUnitConversionTargets(
      (r.ingredients || []).map(ing => ({
        ...ing,
        amount: applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK) || ing.amount,
      })),
    );
    // Baking-science diagnostics band. Renders above the recipe
    // preview when validateBakingRecipe flagged issues or warnings on
    // this recipe. Blockers get red treatment + a REGENERATE WITH
    // FIXES CTA that pre-fills recipeFeedback with the issues block;
    // warnings get gold/amber as a non-blocking heads-up. Per beta-
    // transparency rule, EVERY diagnostic surfaces — nothing hidden.
    const bd = recipe.bakingDiagnostics;
    const hasBakingIssues = bd && bd.issues && bd.issues.length > 0;
    const hasBakingWarnings = bd && bd.warnings && bd.warnings.length > 0;
    // autoFixed corrections ride on bd.autoFixed and log to console
    // at validate-time. We deliberately don't render them in the
    // user-visible preview — if we already fixed it, there's no
    // user decision to make. An audit/admin surface could read
    // bd.autoFixed later if needed.
    const regenerateWithBakingFixes = () => {
      const lines = [];
      lines.push("Please fix these baking-science issues from the previous draft:");
      (bd?.issues || []).forEach((it, idx) => {
        lines.push(`${idx + 1}. ${it.message}${it.suggestion ? " — " + it.suggestion : ""}`);
      });
      if ((bd?.warnings || []).length > 0) {
        lines.push("Also consider:");
        (bd?.warnings || []).forEach((w, idx) => {
          lines.push(`${idx + 1}. ${w.message}${w.suggestion ? " — " + w.suggestion : ""}`);
        });
      }
      const feedbackBlock = lines.join("\n");
      const prior = recipeFeedback.trim();
      setRecipeFeedback(prior ? `${prior}\n\n${feedbackBlock}` : feedbackBlock);
      cookFinal();
    };

    return (
      <div>
        {header}
        {mealHeaderBlock}
        <div style={{ padding: "12px 20px 140px" }}>
          {/* Allergy-safe substitution notice — when this draft swapped an
              allergen the classic dish needs, tell the user plainly and give
              them the path back to the original (one tap) or to stop swapping
              (change allergies in Settings). */}
          {allergyAltInfo && (
            <div style={{ margin: "0 0 16px", padding: 14, background: COLOR.leafDeep, border: `1px solid ${COLOR.leafDim || COLOR.leaf}`, borderRadius: 12 }}>
              <div style={{ fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, letterSpacing: "0.12em", color: COLOR.leaf, marginBottom: 6 }}>
                ✓ ALLERGY-SAFE VERSION
              </div>
              <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.ink, lineHeight: 1.5 }}>
                This is an alternative that swaps out <strong>{allergyAltInfo.allergens.join(" & ")}</strong> to fit your allergies. Want the classic version instead?
              </div>
              <button onClick={generateOriginalDespiteAllergy} className="mcm-focusable"
                style={{ marginTop: 12, width: "100%", padding: "11px", background: COLOR.ink, color: "#fff", border: "none", borderRadius: 10, fontFamily: FONT.forma, fontSize: 13, fontWeight: 700, letterSpacing: "0.04em", cursor: "pointer", touchAction: "manipulation" }}>
                Generate the original instead
              </button>
              <div style={{ marginTop: 8, fontFamily: FONT.forma, fontSize: 12, color: COLOR.dim, lineHeight: 1.5, textAlign: "center" }}>
                Or remove {allergyAltInfo.allergens.join(" / ")} in Settings → Diet to stop swapping it out.
              </div>
            </div>
          )}
          {/* ALLERGEN BACKSTOP BANNER — safety-critical, renders
              FIRST. Fires only when the post-generation scan found
              the viewer's own allergens in the finished recipe
              despite the guard being on (prompt slip / dropped
              context). This is a "do not cook this" red, not a
              note. */}
          {finalAllergenConflicts.length > 0 && (
            <div style={{
              margin: "0 0 16px",
              padding: 14,
              background: "rgba(190, 60, 60, 0.14)",
              border: "1.5px solid rgba(220, 80, 80, 0.75)",
              borderRadius: 12,
            }}>
              <div style={{
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
                letterSpacing: "0.12em", color: "#e85a5a", marginBottom: 6,
              }}>
                ⚠ CONTAINS YOUR ALLERGEN — {finalAllergenConflicts.join(", ").toUpperCase()}
              </div>
              <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.ink, lineHeight: 1.5 }}>
                Our safety check found {finalAllergenConflicts.join(" and ")} in this
                draft even though your allergy guard is on. Don&apos;t cook
                this as written — regenerate, or swap the ingredient out.
              </div>
              <button
                onClick={() => runClarifyThenStart(conceptFrame, { bypassAllergyConflict: false })}
                className="mcm-focusable"
                style={{
                  marginTop: 10, padding: "10px 16px",
                  background: "#e85a5a", color: "#fff", border: "none",
                  borderRadius: 10, fontFamily: FONT.mono, fontSize: 12,
                  fontWeight: 600, letterSpacing: "0.06em", cursor: "pointer",
                }}
              >
                REGENERATE SAFELY
              </button>
            </div>
          )}
          {/* CHEF'S BALANCE NOTE (A4) — one deterministic tasting
              remark when the finished build has a clear imbalance
              (fat-heavy zero-acid, unopposed sweetness). Advisory
              gold, never blocking, suggests exactly one finishing
              move. */}
          {balanceNote && (
            <div style={{
              margin: "0 0 16px",
              padding: 14,
              background: COLOR.goldDeep,
              border: `1px solid ${COLOR.goldDim || COLOR.gold}`,
              borderRadius: 12,
            }}>
              <div style={{
                fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
                letterSpacing: "0.12em", color: COLOR.gold, marginBottom: 6,
              }}>
                CHEF&apos;S BALANCE NOTE
              </div>
              <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.ink, lineHeight: 1.5 }}>
                {balanceNote}
              </div>
            </div>
          )}
          {/* Auto-fixes are SILENT to the user — the recipe just
              shows up correct. Trevor's framing 2026-05-22: "why
              are we displaying our errors to the user." Right —
              if the system fixed it, the user has no decision to
              make. The corrections still log to console (visible
              to anyone debugging) and ride on
              recipe.bakingDiagnostics.autoFixed so an admin/audit
              surface can show them later if needed; we just don't
              advertise them in the primary preview. The red/gold
              band below STILL renders for residual issues the
              auto-fix couldn't safely repair — those ARE user
              decisions. */}
          {(hasBakingIssues || hasBakingWarnings) && (
            <div style={{
              margin: "0 0 16px",
              padding: 14,
              background: hasBakingIssues ? "rgba(190, 60, 60, 0.10)" : COLOR.goldDeep,
              border: `1px solid ${hasBakingIssues ? "rgba(220, 80, 80, 0.55)" : (COLOR.goldDim || COLOR.gold)}`,
              borderRadius: 12,
            }}>
              <div style={{
                fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
                letterSpacing: "0.12em", marginBottom: 8,
                color: hasBakingIssues ? "#e85a5a" : COLOR.gold,
              }}>
                {hasBakingIssues ? "⚠ BAKING-SCIENCE ISSUES" : "⚠ BAKING-SCIENCE NOTES"}
              </div>
              {(bd.issues || []).map((it, idx) => (
                <div key={`iss-${idx}`} style={{ marginBottom: 8, fontFamily: FONT.forma, fontSize: 13, color: COLOR.ink, lineHeight: 1.45 }}>
                  <div><span style={{ color: "#e85a5a", fontWeight: 700 }}>● </span>{it.message}</div>
                  {it.suggestion && (
                    <div style={{ marginTop: 2, marginLeft: 14, fontSize: 12, color: COLOR.dim }}>{it.suggestion}</div>
                  )}
                </div>
              ))}
              {(bd.warnings || []).map((w, idx) => (
                <div key={`warn-${idx}`} style={{ marginBottom: 8, fontFamily: FONT.forma, fontSize: 13, color: COLOR.ink, lineHeight: 1.45 }}>
                  <div><span style={{ color: COLOR.gold, fontWeight: 700 }}>● </span>{w.message}</div>
                  {w.suggestion && (
                    <div style={{ marginTop: 2, marginLeft: 14, fontSize: 12, color: COLOR.dim }}>{w.suggestion}</div>
                  )}
                </div>
              ))}
              {/* Ratios snapshot — surfaces the actual math so the user
                  can see why something was flagged. Per beta-audit
                  transparency, never hide diagnostic numbers. */}
              {bd.ratios && (
                <div style={{ marginTop: 10, fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub, lineHeight: 1.6, letterSpacing: "0.04em" }}>
                  TYPE: {(bd.type || "unknown").replace(/_/g, " ").toUpperCase()} · HYD {bd.ratios.hydration?.toFixed(0) || "—"}% · FAT {bd.ratios.fat?.toFixed(0) || "—"}% · SUG {bd.ratios.sugar?.toFixed(0) || "—"}% · LEAV {bd.ratios.leavening?.toFixed(1) || "—"}% · SALT {bd.ratios.salt?.toFixed(1) || "—"}%
                </div>
              )}
              {hasBakingIssues && (
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <button
                    onClick={regenerateWithBakingFixes}
                    style={{
                      flex: 1, padding: "10px 14px",
                      background: COLOR.leaf, color: COLOR.night,
                      border: "none", borderRadius: 10,
                      fontFamily: FONT.mono, fontSize: 12, fontWeight: 700,
                      letterSpacing: "0.1em", cursor: "pointer",
                    }}
                  >
                    ↻ REGENERATE WITH FIXES
                  </button>
                </div>
              )}
            </div>
          )}
          <div style={{ textAlign: "center", padding: "12px 0 20px" }}>
            <div style={{ fontSize: 52 }}>{recipe.emoji || "🍽️"}</div>
            <h1 style={{ fontFamily: FONT.serif, fontSize: 26, fontWeight: 300, fontStyle: "italic", color: COLOR.ink, margin: "10px 0 4px" }}>
              {recipe.title}
            </h1>
            {recipe.subtitle && (
              <div style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.dim, lineHeight: 1.4 }}>
                {recipe.subtitle}
              </div>
            )}
            <div style={{ marginTop: 6, fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, letterSpacing: "0.1em" }}>
              {(recipe.cuisine || "").toUpperCase()} · {totalTimeMin(recipe)} MIN · {difficultyLabel(recipe.difficulty).toUpperCase()}
            </div>
            {/* Technique-check receipt. Renders ONLY when the
                orchestrator actually adjusted the recipe — the badge
                names what was corrected (e.g. "added 165°F internal
                temp to chicken cook step"). Clean recipes show no
                badge. Avoids the prior trust-sticker-on-every-recipe
                noise; absence is the signal that nothing needed
                adjustment. Audit trail still lives on
                recipe.bakingDiagnostics.autoFixed for admin / debug. */}
            {!hasBakingIssues && <TechniqueCheckBadge diagnostics={bd} variant="compact" />}
            {/* Pre-cook servings stepper. The recipe drafts at
                Claude's typical default; let the user adjust before
                they hit COOK IT. ingredient amounts + nutrition
                rescale live, and SAVE / SCHED / COOK IT all persist
                the scaled recipe. */}
            <div style={{
              marginTop: 10, display: "inline-flex", alignItems: "center",
              padding: "6px 12px",
              background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
              borderRadius: 999,
            }}>
              <ServingsStepper
                value={currentServes}
                onChange={(n) => setServingsOverride(n)}
                label="SERVES"
                palette={{
                  surface: COLOR.soil, btnBg: COLOR.ground, btnBorder: COLOR.border,
                  accent: COLOR.gold, accentSoft: COLOR.goldDeep,
                  ink: COLOR.ink, muted: COLOR.muted,
                  fontLabel: FONT.forma, fontNum: FONT.forma,
                }}
              />
            </div>
            {/* Meal-composition tags — course (main/side/...) and meal
                timing (breakfast/lunch/...) render as small neutral pills
                below the cuisine line. Only surface when Claude stamped
                them; a recipe with null tags (old drafts pre-Phase-1)
                skips this row entirely. Neutral styling (no color)
                keeps clear of the reserved color axes in CLAUDE.md. */}
            {(recipe.course || recipe.mealTiming) && (
              <div style={{ marginTop: 8, display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                {recipe.course     && <MetaPill label={recipe.course} />}
                {recipe.mealTiming && <MetaPill label={recipe.mealTiming} />}
              </div>
            )}
            {conceptFrame && (
              <div style={{ marginTop: 8, display: "flex", justifyContent: "center" }}>
                <FrameChip frame={conceptFrame} onOpen={() => setFrameDetailOpen(true)} />
              </div>
            )}
            {/* Nutrition summary — per-serving macros rolled up from
                the ingredients list. `coverage` discloses gaps so we
                don't pretend a recipe is 100% known when some lines
                didn't resolve. */}
            <RecipeNutritionLine recipe={r} pantry={pantry} ingredientInfo={ingredientInfo} brandNutrition={brandNutrition} />
          </div>

          {recipe.aiRationale && (
            <AiRationaleBanner text={recipe.aiRationale} />
          )}

          <Section label={`INGREDIENTS · ${r.ingredients?.length || 0}`}>
            <IngredientsWithPairing
              ingredients={r.ingredients || []}
              // Pair against the user's actual pantry in all modes.
              // Pairing here is identity matching (recipe canonical
              // == pantry canonical → ✓ chip), not cross-canonical
              // substitution. Rookworst-not-in-pantry shows as
              // "shopping"; butter-in-pantry shows as ✓. The user
              // can manually invoke SWAP if they want to substitute,
              // but the draft never auto-swaps recipe identity.
              pantry={pantry}
              onShoppingAdd={onShoppingAdd}
              onAddToPantry={onAddToPantry}
            />
            {/* Forgot-something escape hatch — user noticed a missing
                ingredient after the draft (e.g. "oh I wanted heavy
                whipping cream"). Toggle opens a default-empty search
                over the full pantry; picking appends to
                pantryEdits.adds and re-fires the final pass so the
                new ingredient is integrated into the steps. Another
                Sonnet call, but targeted and explicit. */}
            {sketch && !busy && (
              <PreviewAddIngredient
                pantry={pantry}
                onAdd={addIngredientAndRegen}
              />
            )}
          </Section>

          <Section label={`STEPS · ${recipe.steps?.length || 0}`}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {(recipe.steps || []).map((step, i) => (
                <div key={step.id || i} style={{
                  background: COLOR.ground, border: `1px solid ${COLOR.edge}`, borderRadius: 12,
                  padding: "12px 14px",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: 11,
                      background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
                      fontFamily: FONT.mono, fontSize: 11, color: COLOR.gold,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      {i + 1}
                    </span>
                    <span style={{ fontFamily: FONT.serif, fontSize: 15, color: COLOR.ink, fontWeight: 500 }}>
                      {step.title}
                    </span>
                  </div>
                  <div style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.dim, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                    {(() => {
                      // Run the step prose through the same tokenizer
                      // CookMode uses, so partial-usage amounts in
                      // prose ("4 tbsp of butter") track the chip's
                      // display unit even when they don't appear
                      // verbatim in the ingredient list. Preview has
                      // no swaps / brand upgrades / amount byte-
                      // identical replacements, so we hand only the
                      // unit-conversion targets — the tokenizer
                      // returns plain {text} tokens.
                      const tokens = tokenizeSwappedInstruction(
                        step.instruction,
                        [],
                        [],
                        [],
                        { unitConversionTargets: stepsUnitConversionTargets },
                      );
                      if (tokens.length === 1 && tokens[0].text === step.instruction) {
                        return step.instruction;
                      }
                      return tokens.map((t, j) => {
                        if (t.text != null) return <span key={j}>{t.text}</span>;
                        if (t.brand != null) return <span key={j}>{t.brand}</span>;
                        return <span key={j}>{t.after || t.strike}</span>;
                      });
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* Bottom action area — two rows when the recipe is a plate
            piece (compose row + action bar); single row when it's a
            bake/prep component (nothing to pair with). */}
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          maxWidth: 480, margin: "0 auto",
          padding: "12px 16px calc(20px + env(safe-area-inset-bottom))",
          // MCM glass-bar treatment matching the bottom dock language —
          // frosted cream that lets the warm backdrop tint through,
          // top fade so content scrolling underneath softens up to it.
          background: "rgba(255,250,240,0.82)",
          backdropFilter: "blur(18px) saturate(160%)",
          WebkitBackdropFilter: "blur(18px) saturate(160%)",
          borderTop: `1px solid ${COLOR.edge}`,
          boxShadow: "0 -4px 22px rgba(120,90,40,0.10)",
          display: "flex", flexDirection: "column", gap: 8,
        }}>
          {recipe.course !== "bake" && recipe.course !== "prep" && (
            <ComposeRow
              filledSlots={filledSlots}
              onAdd={openAddSheet}
              disabled={!!busy}
            />
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={start}
              disabled={!!busy}
              style={{ ...iconActionBtn, opacity: busy ? 0.5 : 1 }}
              title="Draft a different recipe"
            >
              ↻
            </button>
            <button
              onClick={handleSave}
              disabled={!!busy}
              style={{ ...outlineBtn, opacity: busy ? 0.5 : 1 }}
            >
              {busy === "save" ? "…" : "SAVE"}
            </button>
            <button
              onClick={handleSchedule}
              disabled={!!busy}
              style={{ ...outlineBtn, opacity: busy ? 0.5 : 1 }}
            >
              {busy === "schedule" ? "…" : "📅 SCHED"}
            </button>
            <button
              onClick={handleCookIt}
              disabled={!!busy}
              style={{ ...primaryBtn, flex: 2, opacity: busy ? 0.6 : 1 }}
            >
              {busy === "cook" ? "SAVING…" : "COOK IT →"}
            </button>
          </div>
        </div>
        {addSheetCourse && (
          <AddComponentSheet
            courseType={addSheetCourse}
            onDraftNew={() => draftNewComponent(addSheetCourse)}
            onPickExisting={() => {
              setPickExistingFor(addSheetCourse);
              setAddSheetCourse(null);
            }}
            onClose={() => setAddSheetCourse(null)}
            busy={busy}
          />
        )}
        {pickExistingFor && (
          <PickExistingPicker
            courseType={pickExistingFor}
            anchor={mealInProgress?.anchor || recipe}
            userRecipes={userRecipes}
            bundledRecipes={bundledRecipes}
            alreadyPinnedSlugs={(mealInProgress?.pieces || []).map(p => p.recipe?.slug).filter(Boolean)}
            onPick={(picked) => attachExistingComponent(pickExistingFor, picked)}
            onClose={() => setPickExistingFor(null)}
          />
        )}
        <FrameDetailSheet
          frame={conceptFrame}
          open={frameDetailOpen}
          onClose={() => setFrameDetailOpen(false)}
          onReroll={rerollAndRedraft}
        />
      </div>
    );
  }

  // setup phase — MCM-styled. The screen lives in
  // src/experiments/mcm-cooking/AIRecipeSetup.jsx and consumes our
  // existing setup state as controlled props; the start handler
  // continues to be the same `start()` function above so the edge-fn
  // wiring + draft persistence + dish-contract logic all stay intact.
  return (
    <MCMThemeProvider>
      <AIRecipeSetup
        mealPrompt={mealPrompt}                   setMealPrompt={setMealPrompt}
        priority={priority}                       setPriority={setPriority}
        course={course}                           setCourse={setCourse}
        mealTiming={mealTiming}                   setMealTiming={setMealTiming}
        cuisine={cuisine}                         setCuisine={setCuisine}
        time={time}                               setTime={setTime}
        difficulty={difficulty}                   setDifficulty={setDifficulty}
        serves={serves}                           setServes={setServes}
        starIngredientIds={starIngredientIds}     setStarIngredientIds={setStarIngredientIds}
        starSearch={starSearch}                   setStarSearch={setStarSearch}
        starOptionGroups={starOptionGroups}
        pantryCount={pantryCount}
        photoSeedItems={photoSeedItems}
        photoSeedSketch={photoSeedSketch}
        onStart={() => runClarifyThenStart()}
        onSurpriseMe={surpriseMe}
        onRollAndDraft={rollAndDraft}
        onCancel={onCancel}
        draftSavedAt={draftSavedAt}
        draftAgeLabel={draftAgeLabel}
        hasResumableSketch={!!sketch}
        hasResumableRecipe={!!recipe}
        resumableTitle={recipe?.title || ""}
        onResume={() => setPhase(recipe ? "preview" : "tweak")}
        onAbandonDraft={abandonDraft}
        mealHeaderBlock={mealHeaderBlock}
      />
    </MCMThemeProvider>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────

// Renders the ingredients list on the preview / cook-ready screen
// with a live-derived "we'll use your X from your fridge" sub-line
// per row. The pair lookup runs every render against the CURRENT
// pantry, so the pairing is always fresh — if the user cooks this
// dish again in a month with different brands on hand, the banner
// reflects today's pantry, not whatever was stocked when the recipe
// was first drafted. Only the canonical + dietaryClaims intent
// persists on the recipe; pantry identity is re-bound live.
//
// Tone conventions match the pill palette already in use:
//   gray  (#7ec87e) — clean pair, in-kitchen
//   amber (#f59e0b) — substitute or missing, no dietary conflict
//   red   (#e8908a) — dietary conflict on the chosen pair/sub
function IngredientsWithPairing({ ingredients, pantry, onShoppingAdd, onAddToPantry }) {
  // Per-row amount overrides from the UnitPicker. Keyed by index so
  // they survive re-renders without polluting the recipe shape; the
  // shopping-cart write below reads through these overrides.
  //
  // Persistent prefs (unitPrefs.js, localStorage) apply first, so a
  // user's "I like butter in tbsp" pick carries across drafts and
  // cook sessions automatically. The in-session override wins when
  // it's set (handles the case where the user picks a one-off unit
  // for this draft without changing their saved preference).
  const [amountOverrides, setAmountOverrides] = useState({});
  const [pickerIdx, setPickerIdx] = useState(null);
  // Subscribe to preference changes so a Settings toggle re-renders
  // amounts in-place without a page reload. Value isn't used; the
  // bump triggers the component update.
  useUnitPrefsVersion();
  const effectiveIngredients = ingredients.map((ing, i) => {
    if (amountOverrides[i] != null) return { ...ing, amount: amountOverrides[i] };
    const prefApplied = applyPreferredUnit(ing.amount, ing, DISPLAY_CONTEXT.COOK);
    return prefApplied !== ing.amount ? { ...ing, amount: prefApplied } : ing;
  });
  const pairings = pairRecipeIngredients(effectiveIngredients, pantry || []);
  // Track per-row shopping adds locally so the button flips to
  // ✓ ON LIST after commit without waiting for the parent to
  // round-trip state back down.
  const [shoppedIdx, setShoppedIdx] = useState(new Set());
  const addOneToShopping = (ing, idx) => {
    if (!onShoppingAdd) return;
    if (shoppedIdx.has(idx)) return;
    const canon = ing.ingredientId ? findIngredient(ing.ingredientId) : null;
    onShoppingAdd([{
      name:         canon?.name || ing.item || "item",
      amount:       typeof ing.amount === "string"
        ? parseFloat(ing.amount) || 1
        : (Number(ing.amount) || 1),
      unit:         typeof ing.amount === "string"
        ? (String(ing.amount).replace(/[\d.\s]+/, "").trim() || "count")
        : "count",
      ingredientId: ing.ingredientId || null,
      source:       "ai-recipe",
    }]);
    setShoppedIdx(prev => {
      const next = new Set(prev);
      next.add(idx);
      return next;
    });
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {pairings.map((p, i) => {
        const { ingredient: ing, paired, closestMatch, lostClaims, status } = p;
        const describe = describePairing(p);
        const showRow = paired || closestMatch;
        const cut = showRow ? deriveRowCut(showRow) : null;
        const toneColor = describe?.tone === "gray"  ? COLOR.leafMuted
                        : describe?.tone === "sky"   ? COLOR.sky
                        : describe?.tone === "amber" ? COLOR.amber
                        : describe?.tone === "red"   ? COLOR.rose
                        : COLOR.dim;
        const borderColor = describe?.tone === "red"   ? COLOR.roseDim
                          : describe?.tone === "sky"   ? "#1a2a3a"
                          : describe?.tone === "amber" ? "#3a2a1a"
                          : COLOR.edge;
        const showShop = status === "missing" && !!onShoppingAdd;
        const shopDone = shoppedIdx.has(i);
        return (
          <div key={i} style={{
            background: COLOR.ground, border: `1px solid ${borderColor}`, borderRadius: 10,
            padding: "8px 12px", display: "flex", flexDirection: "column", gap: 4,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                type="button"
                onClick={() => setPickerIdx(i)}
                title="Change unit"
                style={{
                  // Lighter, cleaner styling — was reading as a
                  // dark "black box" on light themes. Now: subtle
                  // tan-tinted background that hints at the
                  // canonical-axis color without dominating the
                  // row. Border replaces solid fill as the
                  // primary affordance.
                  fontFamily: FONT.mono, fontSize: 11, color: CHIP_TONES.canonical.fg,
                  minWidth: 60, textAlign: "left",
                  display: "inline-flex", alignItems: "center", gap: 4,
                  background: "transparent",
                  border: `1px solid ${CHIP_TONES.canonical.fg}33`,
                  borderRadius: 6, padding: "3px 8px",
                  cursor: "pointer",
                }}
              >
                <span>{ing.amount || "—"}</span>
                <span style={{ fontSize: 8, opacity: 0.7 }}>▾</span>
              </button>
              <span style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink }}>
                {ing.item}
              </span>
              {cut && (
                <span style={{
                  padding: "1px 7px",
                  background: COLOR.plum, border: `1px solid ${COLOR.plum}`,
                  borderRadius: 8,
                  fontFamily: FONT.mono, fontSize: 9,
                  color: COLOR.plum, letterSpacing: "0.08em",
                }}>
                  {String(cut).toUpperCase()}
                </span>
              )}
              {ing.ingredientId && (
                <span style={{ marginLeft: "auto", fontFamily: FONT.mono, fontSize: 9, color: CHIP_TONES.canonical.fg }}>
                  · {ing.ingredientId}
                </span>
              )}
            </div>
            {describe && (
              <div style={{
                fontFamily: FONT.sans, fontSize: 11, fontStyle: "italic",
                color: toneColor, paddingLeft: 70, lineHeight: 1.4,
              }}>
                {describe.text}
                {lostClaims.length > 0 && (
                  <>
                    {" — "}
                    <span style={{ fontFamily: FONT.mono, fontSize: 10, fontStyle: "normal", letterSpacing: "0.04em" }}>
                      ⚠ NO LONGER {lostClaims.map(c => c.toUpperCase()).join(" / ")}
                    </span>
                  </>
                )}
              </div>
            )}
            {showShop && (
              <div style={{ paddingLeft: 70, marginTop: 4, display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button
                  onClick={() => addOneToShopping(ing, i)}
                  disabled={shopDone}
                  style={shopDone ? previewShopBtnDone : previewShopBtn}
                >
                  {shopDone ? "✓ ON LIST" : "+ SHOP"}
                </button>
                {onAddToPantry && !shopDone && (
                  <button
                    onClick={() => onAddToPantry({
                      name: ing.item,
                      ingredientId: ing.ingredientId || null,
                      amount: ing.amount,
                    })}
                    style={previewShopBtn}
                    title="I have this — add to pantry instead of shopping"
                  >
                    + ADD
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
      {pickerIdx != null && (() => {
        const row = effectiveIngredients[pickerIdx];
        if (!row) return null;
        return (
          <UnitPicker
            open={true}
            onClose={() => setPickerIdx(null)}
            amountString={String(row.amount || "")}
            ingredientId={row.ingredientId}
            itemName={row.item}
            prefKey={prefKeyForIngredient(row)}
            context={DISPLAY_CONTEXT.COOK}
            onPick={(newAmount) => {
              setAmountOverrides(prev => ({ ...prev, [pickerIdx]: newAmount }));
            }}
          />
        );
      })()}
    </div>
  );
}

const previewShopBtn = {
  padding: "5px 10px",
  background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
  color: COLOR.gold, borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
  letterSpacing: "0.08em", cursor: "pointer", whiteSpace: "nowrap",
};
const previewShopBtnDone = { ...previewShopBtn, background: COLOR.leafDeep, borderColor: COLOR.leafDim, color: COLOR.leaf, cursor: "default" };

// "WHY THIS DISH" banner with collapse-expand. The rationale can run
// 150–900 chars depending on how much context Claude had to cite; a
// hard truncation mid-word in the UI was being reported as a bug.
// Collapse threshold is 240 chars — short-enough rationales render
// in full; longer ones show a snippet + "Show more" toggle that
// reveals the rest in-place. Collapsed state doesn't mutate the
// underlying text, so copy-paste and screen readers still see the
// whole thing.
const RATIONALE_COLLAPSE_THRESHOLD = 240;
function AiRationaleBanner({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const long = text.length > RATIONALE_COLLAPSE_THRESHOLD;
  const visible = !long || expanded
    ? text
    : text.slice(0, RATIONALE_COLLAPSE_THRESHOLD).replace(/\s+\S*$/, "") + "…";
  return (
    <div style={{
      marginTop: 8, padding: "12px 14px",
      background: COLOR.goldDeep,
      border: `1px solid ${COLOR.goldDim}`,
      borderRadius: 12,
      display: "flex", gap: 10, alignItems: "flex-start",
    }}>
      <div style={{ fontSize: 18, flexShrink: 0, lineHeight: 1 }}>✨</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
          color: COLOR.warmBrown || COLOR.plum, letterSpacing: "0.12em", marginBottom: 4,
        }}>
          WHY THIS DISH
        </div>
        <div style={{
          fontFamily: FONT.sans, fontSize: 13,
          color: COLOR.ink, lineHeight: 1.55,
        }}>
          {visible}
          {long && (
            <>
              {" "}
              <button
                onClick={() => setExpanded(e => !e)}
                style={{
                  background: "transparent", border: "none", padding: 0,
                  color: COLOR.plum, cursor: "pointer",
                  fontFamily: FONT.mono, fontSize: 10,
                  letterSpacing: "0.08em", textDecoration: "underline",
                }}
              >
                {expanded ? "SHOW LESS" : "SHOW MORE"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Preview-phase "forgot an ingredient" picker. Identical UI to the
// tweak-phase "+ ADD INGREDIENT FROM PANTRY" control — same toggle
// button, same scrollable chip list, same dedupe-by-canonical rule.
// Exists on preview so users who notice something missing after
// the final pass can add it without navigating back to tweak.
function PreviewAddIngredient({ pantry, onAdd }) {
  const [open, setOpen] = useState(false);
  const candidates = (() => {
    const seenCanon = new Set();
    return (pantry || []).filter(p => {
      if (!p) return false;
      if (p.kind && p.kind !== "ingredient") return false;
      const canonId = p.ingredientId || p.canonicalId || p.name?.toLowerCase();
      if (canonId && seenCanon.has(canonId)) return false;
      if (canonId) seenCanon.add(canonId);
      return true;
    });
  })();
  return (
    <div style={{ marginTop: 10 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={open ? addBtnActive : addBtn}
      >
        {open ? "× CLOSE" : "+ ADD INGREDIENT FROM PANTRY"}
      </button>
      {open && (
        <div style={{
          marginTop: 8, padding: "10px 12px",
          background: COLOR.deep, border: `1px solid ${COLOR.edge}`, borderRadius: 10,
          maxHeight: 240, overflowY: "auto",
          display: "flex", flexDirection: "column", gap: 6,
        }}>
          {candidates.length === 0 ? (
            <div style={{ fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, fontStyle: "italic", padding: "12px 0", textAlign: "center" }}>
              No pantry items available.
            </div>
          ) : candidates.map(p => (
            <button
              key={p.id}
              onClick={() => { onAdd(p); setOpen(false); }}
              style={swapOptionBtn}
            >
              <span style={{ fontSize: 16 }}>{p.emoji || "🥫"}</span>
              <span style={{ flex: 1, textAlign: "left", fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink }}>
                {p.name}
              </span>
              <span style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.dim }}>
                {p.amount}{p.unit ? ` ${p.unit}` : ""}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div style={{ marginTop: 20 }}>
      <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.12em", marginBottom: 10 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

// Small neutral pill for meal-composition tags (course, mealTiming) on
// recipe cards and previews. Neutral styling sidesteps the reserved
// color axes in CLAUDE.md (tan/orange/blue/purple/yellow). Exported
// via file-local usage; CreateMenu imports its own copy for the
// PICK A RECIPE row.
function MetaPill({ label }) {
  return (
    <span style={{
      fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
      color: COLOR.dim, background: COLOR.soil,
      border: `1px solid ${COLOR.border}`,
      padding: "2px 7px", borderRadius: 6,
      letterSpacing: "0.1em", textTransform: "uppercase",
    }}>
      {label}
    </span>
  );
}

// Concept-frame detail sheet. Bottom-drawer modal opened from the
// FRAMED BY chip. Lists every populated axis + a REROLL CTA that
// re-seeds the frame and re-fires the sketch (this REROLL costs an
// AI call — it draws a new frame and asks Claude to draft for it).
// Portaled to document.body so the App-level <motion.div> tab-
// transition transform doesn't break its `position: fixed` (same
// gotcha that scoped MealDetailDrawer earlier).
function FrameDetailSheet({ frame, open, onClose, onReroll }) {
  if (!open || !frame) return null;
  const axes = [
    frame.type      && { kind: "TYPE",      label: frame.type.label },
    frame.cuisine   && { kind: "CUISINE",   label: frame.cuisine.label },
    frame.technique && { kind: "TECHNIQUE", label: frame.technique.label },
    frame.hero      && { kind: "HERO",      label: frame.hero.label },
    frame.vibe      && { kind: "VIBE",      label: frame.vibe.label },
    frame.fixedDishName && { kind: "DISH (LOCKED)", label: frame.fixedDishName },
  ].filter(Boolean);
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "#000000dd",
        zIndex: 280, display: "flex", alignItems: "flex-end",
        maxWidth: 480, margin: "0 auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", background: COLOR.ground,
          borderRadius: "20px 20px 0 0", padding: "20px 22px 36px",
          maxHeight: "75vh", overflowY: "auto",
        }}
      >
        <div style={{ width: 36, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 18px" }} />
        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.gold, letterSpacing: "0.12em", marginBottom: 8 }}>
          TODAY'S CONCEPT
        </div>
        <div style={{ fontFamily: FONT.serif, fontSize: 22, fontStyle: "italic", color: COLOR.ink, marginBottom: 18, lineHeight: 1.25 }}>
          {frame.label}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 22 }}>
          {axes.map((a) => (
            <div key={a.kind} style={{
              display: "flex", justifyContent: "space-between",
              padding: "10px 12px",
              background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
              borderRadius: 8,
            }}>
              <span style={{ fontFamily: FONT.mono, fontSize: 9, fontWeight: 700, color: COLOR.gold, letterSpacing: "0.10em" }}>
                {a.kind}
              </span>
              <span style={{ fontFamily: FONT.sans, fontSize: 13, color: COLOR.ink, textAlign: "right" }}>
                {a.label}
              </span>
            </div>
          ))}
        </div>
        {onReroll && (
          <button
            onClick={onReroll}
            style={{
              width: "100%", padding: "14px",
              background: COLOR.gold, border: "none", color: COLOR.night,
              borderRadius: 12, fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
              letterSpacing: "0.12em", cursor: "pointer",
            }}
          >
            REROLL & REDRAFT
          </button>
        )}
        <button
          onClick={onClose}
          style={{
            marginTop: 8, width: "100%", padding: "12px",
            background: "transparent", border: "none",
            color: COLOR.sub, fontFamily: FONT.mono, fontSize: 10,
            letterSpacing: "0.12em", cursor: "pointer",
          }}
        >
          CLOSE
        </button>
      </div>
    </div>,
    document.body,
  );
}

// Concept-frame transparency chip. Renders on sketch + preview screens
// with the frame's editorial label so the user can see what corner of
// recipe space the AI was anchored to. Tap opens the detail sheet
// (axis breakdown + REROLL). Restrained tone: tan/dim, not loud,
// because the chip is informational, not a primary action.
function FrameChip({ frame, onOpen }) {
  if (!frame) return null;
  return (
    <button
      onClick={onOpen}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 10px",
        background: COLOR.deep,
        border: `1px solid ${COLOR.edge}`,
        borderRadius: 14,
        cursor: "pointer",
        maxWidth: "100%",
      }}
    >
      <span style={{
        fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
        color: COLOR.gold, letterSpacing: "0.10em",
      }}>
        FRAMED BY
      </span>
      <span style={{
        fontFamily: FONT.sans, fontSize: 11,
        color: COLOR.muted,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
        maxWidth: 240,
      }}>
        {frame.label}
      </span>
    </button>
  );
}

function ChipRow({ value, onChange, options, color = COLOR.gold }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {options.map(o => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              padding: "7px 14px",
              background: active ? COLOR.goldDeep : COLOR.soil,
              border: `1px solid ${active ? color : COLOR.border}`,
              color: active ? color : COLOR.dim,
              borderRadius: 20,
              fontFamily: FONT.sans, fontSize: 12,
              cursor: "pointer", transition: "all 0.2s",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const iconBtn = {
  background: COLOR.soil, border: `1px solid ${COLOR.border}`,
  borderRadius: 18, width: 34, height: 34,
  color: COLOR.dim, fontSize: 16, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center",
  lineHeight: 1,
};
const primaryBtn = {
  flex: 1, padding: "14px",
  // MCM CTA — burnt-orange gradient, cream text, italic editorial
  // voice. Mirrors `ctaButton` in MCM tokens.js.
  background: "linear-gradient(180deg, #C85A1F 0%, #A34711 100%)",
  color: "#FFF8EE",
  border: "1px solid rgba(255,255,255,0.35)",
  borderRadius: 999,
  fontFamily: FONT.serif, fontSize: 14, fontWeight: 500,
  fontStyle: "italic",
  letterSpacing: "0.005em", cursor: "pointer",
  boxShadow: "0 12px 26px rgba(168,73,17,0.40), 0 2px 6px rgba(168,73,17,0.28)",
};
const secondaryBtn = {
  flex: 1, padding: "14px",
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  color: COLOR.ink, borderRadius: 999,
  fontFamily: FONT.serif, fontSize: 14, fontStyle: "italic",
  letterSpacing: "0.005em", cursor: "pointer",
};
// Outline button used between REGEN and COOK IT for SAVE / SCHED.
// Cream-glass surface with warm-brown italic label so it sits as a
// quieter sibling to the burnt CTA without disappearing.
const outlineBtn = {
  flex: 1, padding: "12px 6px",
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  color: COLOR.ink,
  borderRadius: 999,
  fontFamily: FONT.serif, fontSize: 13, fontStyle: "italic",
  fontWeight: 500, letterSpacing: "0.005em",
  cursor: "pointer", whiteSpace: "nowrap",
};
// Narrow square REGEN tap target — frees horizontal space for the
// three action buttons to breathe.
const iconActionBtn = {
  width: 44, padding: "12px 0",
  background: COLOR.ground,
  border: `1px solid ${COLOR.border}`,
  color: COLOR.muted, borderRadius: 999,
  fontFamily: FONT.mono, fontSize: 16,
  cursor: "pointer", flexShrink: 0,
};

// ── Tweak phase buttons ─────────────────────────────────────────────
const swapBtn = {
  padding: "5px 10px",
  background: COLOR.skyDeep, border: `1px solid ${COLOR.skyBorder}`,
  color: COLOR.sky, borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 9,
  letterSpacing: "0.06em", cursor: "pointer", whiteSpace: "nowrap",
};
const swapBtnActive = { ...swapBtn, background: COLOR.skyDeep, color: "#9bcae0" };
// Amber-tint button for the "BUY REAL" action on substitution rows —
// reuses the rust/amber palette from the subHint chip ("⇌ classic
// calls for X") so the two visually rhyme: same dish-substitution
// story, one explains, one resolves.
const buyRealBtn = {
  padding: "5px 10px",
  background: "#1f1410", border: "1px solid #4a2f1a",
  color: "#d4a878", borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 9,
  letterSpacing: "0.06em", cursor: "pointer", whiteSpace: "nowrap",
};
const removeBtn = {
  width: 28, height: 28, padding: 0,
  background: "transparent", border: `1px solid ${COLOR.border}`,
  color: COLOR.muted, borderRadius: 14,
  fontFamily: FONT.mono, fontSize: 14,
  cursor: "pointer", flexShrink: 0,
};
const undoChip = {
  padding: "4px 8px",
  background: "transparent", border: `1px solid ${COLOR.border}`,
  color: COLOR.dim, borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 9,
  letterSpacing: "0.06em", cursor: "pointer",
};
const swapOptionBtn = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "8px 10px", width: "100%",
  background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
  borderRadius: 8, cursor: "pointer", textAlign: "left",
};
const swapClearBtn = {
  marginTop: 4, padding: "6px 10px",
  background: "transparent", border: `1px dashed ${COLOR.border}`,
  color: COLOR.dim, borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 10,
  letterSpacing: "0.06em", cursor: "pointer",
};
const swapSearchInput = {
  width: "100%", padding: "8px 10px",
  background: COLOR.deep, border: `1px solid ${COLOR.border}`,
  borderRadius: 8, color: COLOR.ink,
  fontFamily: FONT.sans, fontSize: 12,
  outline: "none", boxSizing: "border-box",
};
const addBtn = {
  width: "100%", padding: "10px",
  background: "transparent", border: `1px dashed ${COLOR.border}`,
  color: COLOR.dim, borderRadius: 10,
  fontFamily: FONT.mono, fontSize: 11,
  letterSpacing: "0.08em", cursor: "pointer",
};
const addBtnActive = { ...addBtn, color: COLOR.dim, borderStyle: "solid" };
const shopBtn = {
  padding: "6px 12px",
  background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
  color: COLOR.gold, borderRadius: 8,
  fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
  letterSpacing: "0.08em", cursor: "pointer", whiteSpace: "nowrap",
};
const shopActiveBtn = {
  ...shopBtn,
  background: COLOR.leafDeep, border: `1px solid ${COLOR.leafDim}`, color: COLOR.leafMuted,
};

// Auto-generate a display name for a composed meal. Uses the anchor's
// title directly; if we have a strong cuisine + mealTiming signal
// we append a descriptor ("Ribeye · Italian Dinner"). Keeps it short
// so it fits a calendar tile. User can rename later via a library edit.
function buildMealName(anchor, _pieces) {
  if (!anchor?.title) return "Untitled meal";
  const base = anchor.title.trim();
  const bits = [];
  if (anchor.cuisine && anchor.cuisine !== "other") {
    bits.push(anchor.cuisine.charAt(0).toUpperCase() + anchor.cuisine.slice(1));
  }
  if (anchor.mealTiming) {
    bits.push(anchor.mealTiming.charAt(0).toUpperCase() + anchor.mealTiming.slice(1));
  }
  return bits.length ? `${base} · ${bits.join(" ")}` : base;
}

// ── Compose-a-meal components ────────────────────────────────────────

// COMPOSE_SLOTS defines the non-main slots the user can add to a meal.
// "main" is established by the anchor (the recipe currently on
// preview), so the compose row only surfaces the three optional
// accompaniments. "bake" and "prep" aren't here — those are pantry-
// building components, not plate roles.
const COMPOSE_SLOTS = [
  { id: "side",      label: "+ SIDE",    emoji: "🥗" },
  { id: "dessert",   label: "+ DESSERT", emoji: "🍰" },
  { id: "appetizer", label: "+ APP",     emoji: "🥟" },
];

// Sticky MEAL header — renders at the top of AIRecipe whenever a meal
// is mid-compose. Pills show every piece already pinned (tap × to
// unpin — removes from the meal only, recipe stays in library).
// Empty slots render as "+ Add X" buttons. SAVE MEAL on the right
// commits the composition (Phase 3 will write the meals row; for now
// it just closes since pieces are already saved individually).
function MealInProgressHeader({ meal, filledSlots, onAdd, onRemovePiece, onFinish, disabled }) {
  // filledSlots already folds in the currently-previewed recipe's
  // course (computed in the parent) so clicking "+ SIDE" from a side
  // preview is prevented — the "+ Add X" button is hidden entirely.
  const filled = filledSlots || new Set((meal?.pieces || []).map(p => p.course));
  const anchorLabel = meal?.anchor?.title || "this meal";
  return (
    <div style={{
      position: "sticky", top: 0, zIndex: 20,
      padding: "10px 20px 12px",
      background: "linear-gradient(180deg, #0b0b0b 70%, rgba(11,11,11,0.92) 100%)",
      borderBottom: `1px solid ${COLOR.border}`,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8,
        marginBottom: 8,
      }}>
        <span style={{ fontSize: 14 }}>🍽️</span>
        <div style={{
          flex: 1, fontFamily: FONT.mono, fontSize: 9,
          color: COLOR.dim, letterSpacing: "0.12em", textTransform: "uppercase",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          Your meal · {anchorLabel}
        </div>
        <button
          onClick={onFinish}
          disabled={disabled}
          style={{
            padding: "5px 10px",
            background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`,
            color: COLOR.gold, borderRadius: 8,
            fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
            letterSpacing: "0.1em", cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
          }}
        >
          SAVE MEAL
        </button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {(meal?.pieces || []).map((piece, i) => (
          <MealPiecePill
            key={`${piece.recipe?.slug || piece.course}-${i}`}
            piece={piece}
            removable={piece.course !== "main"}
            onRemove={() => onRemovePiece(i)}
          />
        ))}
        {COMPOSE_SLOTS.filter(s => !filled.has(s.id)).map(slot => (
          <button
            key={slot.id}
            onClick={() => onAdd(slot.id)}
            disabled={disabled}
            style={{
              padding: "5px 10px",
              background: COLOR.ground,
              border: `1px dashed ${COLOR.border}`,
              color: COLOR.dim, borderRadius: 8,
              fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
              letterSpacing: "0.1em", cursor: disabled ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
              opacity: disabled ? 0.5 : 1,
            }}
          >
            <span>{slot.emoji}</span>
            {slot.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function MealPiecePill({ piece, removable, onRemove }) {
  const emoji = piece.recipe?.emoji || (
    piece.course === "main" ? "🍽️" :
    piece.course === "side" ? "🥗" :
    piece.course === "dessert" ? "🍰" : "🥟"
  );
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 8px",
      background: COLOR.soil, border: `1px solid ${COLOR.border}`,
      borderRadius: 8,
      fontFamily: FONT.sans, fontSize: 11, color: "#d8d2c8",
      maxWidth: 180,
    }}>
      <span style={{ fontSize: 12 }}>{emoji}</span>
      <span style={{
        fontFamily: FONT.mono, fontSize: 8, fontWeight: 700,
        color: COLOR.dim, letterSpacing: "0.1em", textTransform: "uppercase",
      }}>
        {piece.course}
      </span>
      <span style={{
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        maxWidth: 100,
      }}>
        {piece.recipe?.title || "(untitled)"}
      </span>
      {removable && (
        <button
          onClick={onRemove}
          style={{
            background: "transparent", border: "none",
            color: COLOR.muted, fontSize: 12, lineHeight: 1,
            padding: 0, marginLeft: 2, cursor: "pointer",
          }}
          title="Remove from meal (keeps recipe in library)"
        >
          ×
        </button>
      )}
    </span>
  );
}

// Row of three small outline buttons above the main preview action
// bar. Filled slots render as disabled "✓ SIDE" confirmations; unfilled
// slots are tappable "+ SIDE" adds. Hidden entirely when the current
// recipe is a bake/prep component (not a plate piece).
function ComposeRow({ filledSlots, onAdd, disabled }) {
  const filled = filledSlots || new Set();
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {COMPOSE_SLOTS.map(slot => {
        const isFilled = filled.has(slot.id);
        return (
          <button
            key={slot.id}
            onClick={() => !isFilled && onAdd(slot.id)}
            disabled={disabled || isFilled}
            style={{
              flex: 1, padding: "8px 4px",
              background: isFilled ? COLOR.leafDeep : "transparent",
              border: `1px ${isFilled ? "solid" : "dashed"} ${isFilled ? COLOR.leafDim : COLOR.border}`,
              color: isFilled ? COLOR.leafMuted : COLOR.dim,
              borderRadius: 10,
              fontFamily: FONT.mono, fontSize: 9, fontWeight: 600,
              letterSpacing: "0.08em", whiteSpace: "nowrap",
              cursor: (disabled || isFilled) ? "default" : "pointer",
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {isFilled ? `✓ ${slot.id.toUpperCase()}` : slot.label}
          </button>
        );
      })}
    </div>
  );
}

// Bottom sheet that asks "Draft new" vs "Pick existing" for a given
// course. Backdrop dismisses. Tapping "Draft new" kicks straight into
// the setup flow with pairWith seeded; "Pick existing" opens the
// filtered library picker.
function AddComponentSheet({ courseType, onDraftNew, onPickExisting, onClose, busy }) {
  const label = courseType === "appetizer" ? "APPETIZER" : courseType.toUpperCase();
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 220,
        background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480,
          background: COLOR.ground,
          borderTop: `1px solid ${COLOR.border}`,
          borderRadius: "16px 16px 0 0",
          padding: "20px 20px 28px",
          display: "flex", flexDirection: "column", gap: 10,
        }}
      >
        <div style={{
          fontFamily: FONT.mono, fontSize: 10,
          color: COLOR.dim, letterSpacing: "0.12em",
          marginBottom: 4,
        }}>
          ADD A {label}
        </div>
        <button
          onClick={onDraftNew}
          disabled={!!busy}
          style={addPathBtn}
        >
          <span style={{ fontSize: 20 }}>✨</span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <span style={addPathTitle}>Draft new</span>
            <span style={addPathBlurb}>
              Claude builds a {courseType} that complements this dish.
            </span>
          </span>
        </button>
        <button
          onClick={onPickExisting}
          disabled={!!busy}
          style={addPathBtn}
        >
          <span style={{ fontSize: 20 }}>📖</span>
          <span style={{ flex: 1, textAlign: "left" }}>
            <span style={addPathTitle}>Pick existing</span>
            <span style={addPathBlurb}>
              Choose a {courseType} from your recipes or the library.
            </span>
          </span>
        </button>
        <button
          onClick={onClose}
          style={{
            marginTop: 6, padding: "10px",
            background: "transparent", border: `1px solid ${COLOR.border}`,
            color: COLOR.dim, borderRadius: 10,
            fontFamily: FONT.mono, fontSize: 10,
            letterSpacing: "0.08em", cursor: "pointer",
          }}
        >
          CANCEL
        </button>
      </div>
    </div>
  );
}

const addPathBtn = {
  display: "flex", alignItems: "center", gap: 12,
  padding: "14px 14px",
  background: COLOR.soil, border: `1px solid ${COLOR.border}`,
  borderRadius: 12, cursor: "pointer",
  textAlign: "left",
};
const addPathTitle = {
  display: "block",
  fontFamily: FONT.serif, fontSize: 16, fontStyle: "italic",
  color: COLOR.ink, fontWeight: 500,
};
const addPathBlurb = {
  display: "block", marginTop: 2,
  fontFamily: FONT.sans, fontSize: 12, color: COLOR.dim,
  lineHeight: 1.4,
};

// Full-screen library picker for the "Pick existing" path. Filtered to
// course=target (keeps strict for sides / desserts / apps so the user
// doesn't accidentally pin a main as a side). Soft-sorted by cuisine
// match to the anchor, then mealTiming match, then recency. Recipes
// already in the meal render disabled with an "IN MEAL" badge so the
// user can't double-add.
function PickExistingPicker({
  courseType, anchor,
  userRecipes, bundledRecipes,
  alreadyPinnedSlugs,
  onPick, onClose,
}) {
  const [query, setQuery] = useState("");

  const pinned = new Set(alreadyPinnedSlugs || []);

  // Flatten candidates from both sources. User recipes come as rows
  // with { id, recipe, source }; bundled as flat recipe objects.
  const candidates = useMemo(() => {
    const rows = [];
    (userRecipes || []).forEach((ur) => {
      if (ur && ur.recipe) rows.push({ recipe: ur.recipe, source: ur.source || "user", createdAt: ur.createdAt || null });
    });
    (bundledRecipes || []).forEach((r) => {
      if (r) rows.push({ recipe: r, source: "bundled", createdAt: null });
    });
    return rows;
  }, [userRecipes, bundledRecipes]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matchesQuery = (r) => {
      if (!q) return true;
      return (r.title || "").toLowerCase().includes(q) ||
             (r.cuisine || "").toLowerCase().includes(q) ||
             (r.category || "").toLowerCase().includes(q);
    };
    const anchorCuisine    = anchor?.cuisine || null;
    const anchorMealTiming = anchor?.mealTiming || null;
    const scored = [];
    candidates.forEach((row) => {
      const r = row.recipe;
      if (!r || !matchesQuery(r)) return;
      // Strict course filter. A recipe with no `course` tag (pre-Phase-1
      // draft) is eligible only if its `category` hints strongly at
      // the target (e.g. category="dessert" for a dessert slot). Keeps
      // the picker useful before every recipe has been re-tagged.
      const recipeCourse = r.course || null;
      if (recipeCourse && recipeCourse !== courseType) return;
      if (!recipeCourse) {
        if (courseType === "dessert" && r.category !== "dessert") return;
        if (courseType !== "dessert" && r.category === "dessert")  return;
      }
      let score = 0;
      if (anchorCuisine    && r.cuisine    === anchorCuisine)    score += 3;
      if (anchorMealTiming && r.mealTiming === anchorMealTiming) score += 2;
      if (recipeCourse === courseType)                           score += 1;
      scored.push({ row, score });
    });
    scored.sort((a, b) => b.score - a.score);
    return scored.map((s) => s.row);
  }, [candidates, query, courseType, anchor]);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 230,
      background: COLOR.deep,
      maxWidth: 480, margin: "0 auto",
      overflowY: "auto",
    }}>
      <div style={{
        padding: "24px 20px 12px",
        display: "flex", alignItems: "center", gap: 10,
        borderBottom: `1px solid ${COLOR.edge}`,
      }}>
        <button onClick={onClose} style={iconBtn}>←</button>
        <div style={{
          flex: 1, fontFamily: FONT.mono, fontSize: 10,
          color: COLOR.plum, letterSpacing: "0.12em",
        }}>
          PICK A {courseType === "appetizer" ? "APPETIZER" : courseType.toUpperCase()}
        </div>
        <button onClick={onClose} style={iconBtn}>✕</button>
      </div>
      <div style={{ padding: "14px 20px 0" }}>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${courseType}s…`}
          style={{
            width: "100%", padding: "12px 14px",
            background: COLOR.soil, border: `1px solid ${COLOR.border}`,
            borderRadius: 10, color: COLOR.ink, boxSizing: "border-box",
            fontFamily: FONT.sans, fontSize: 14, outline: "none",
          }}
        />
        {anchor?.cuisine && (
          <div style={{
            marginTop: 8,
            fontFamily: FONT.sans, fontSize: 11, color: COLOR.muted,
            fontStyle: "italic",
          }}>
            Sorted by fit with your {anchor.cuisine} main.
          </div>
        )}
      </div>
      <div style={{ padding: "14px 20px 60px", display: "flex", flexDirection: "column", gap: 8 }}>
        {filtered.length === 0 && (
          <div style={{
            padding: 40, textAlign: "center",
            color: COLOR.sub, fontFamily: FONT.sans, fontSize: 13,
          }}>
            No {courseType} recipes yet. Try "Draft new" instead.
          </div>
        )}
        {filtered.map((row, i) => {
          const r = row.recipe;
          const isPinned = pinned.has(r.slug);
          return (
            <button
              key={`${r.slug || "recipe"}-${i}`}
              onClick={() => !isPinned && onPick(r)}
              disabled={isPinned}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "12px 14px",
                background: isPinned ? COLOR.leafDeep : COLOR.soil,
                border: `1px solid ${isPinned ? COLOR.leafDim : COLOR.border}`,
                borderRadius: 12,
                cursor: isPinned ? "default" : "pointer",
                textAlign: "left",
                opacity: isPinned ? 0.7 : 1,
              }}
            >
              <div style={{ fontSize: 26, flexShrink: 0 }}>{r.emoji || "🍽️"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: FONT.serif, fontSize: 15, color: COLOR.ink,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {r.title}
                </div>
                <div style={{
                  fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub,
                  letterSpacing: "0.05em", marginTop: 2,
                }}>
                  {(r.cuisine || "").toUpperCase()}
                  {r.mealTiming ? ` · ${r.mealTiming.toUpperCase()}` : ""}
                  {row.source === "bundled" ? " · BUNDLED" : row.source === "ai" ? " · AI" : " · CUSTOM"}
                </div>
              </div>
              {isPinned ? (
                <span style={{
                  fontFamily: FONT.mono, fontSize: 9, fontWeight: 700,
                  color: COLOR.leafMuted, background: COLOR.leafDeep,
                  border: `1px solid ${COLOR.leafDim}`,
                  padding: "3px 7px", borderRadius: 6,
                  letterSpacing: "0.1em",
                }}>
                  IN MEAL
                </span>
              ) : (
                <span style={{ color: COLOR.plum, fontFamily: FONT.mono, fontSize: 14 }}>→</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// RecipeNutritionLine extracted to its own file
// (src/components/RecipeNutritionLine.jsx) so the same per-serving +
// total calorie tile renders on RecipeBook + MealDetailDrawer + here
// without code duplication.
